<template>
  <div class="jobFormMain">
    <Loader :isVisible="loading" :loadingText="loadText" />
    <div>
      <div v-show="!file && !showPageSelector">
        <p class="noteToUser"
          >Upload your document to extract shipment details, review the information, and proceed with job creation.</p
        >
        <div class="pdf-guidance">
          <p
            ><b>Tip:</b><br />
            &bull; Avoid including blank or unrelated pages to improve AI processing accuracy.<br />
            &bull; AI detection works best with clear, high-resolution documents. Blurry or distorted images may result
            in missing or incorrect data.<br />
            &bull; AI can make mistakes. Check and review all important information.
          </p>
          <p>
            <b>Preferred Documents:</b><br />

            <span v-if="pageType === 'Import'">
              &bull;<b> Import Job:</b> Arrival Notice, Bill of Lading, Sea Waybill
            </span>

            <span v-if="pageType === 'Export'">
              &bull;<b> Export Job:</b> Booking Confirmation, Booking Acknowledgment
            </span>
          </p>
        </div>
        <br />
        <DocAIFileUploadField ref="docAIUpload" @fileSelected="docAIFileSelected" @input="onInputChild">
          <p
            >Drop your PDF here, or <span style="color: darkblue">click to upload</span> <br />
            <span style="color: grey; font-size: 13px">Supports PDF only • Max 20MB / 15 pages</span></p
          >
        </DocAIFileUploadField>
      </div>
      <div v-if="!file && showPageSelector" class="page-review-layout">
        <div>
          <div class="pdf-guidance">
            Select the page range that contains the
            <strong>relevant document information</strong> then click <strong>Process PDF</strong>.
          </div>
          <div class="page-selector">
            <span>Select Pages:</span>

            <Dropdown
              placeholder="Start"
              :items="pageOptions"
              itemKey="Page"
              :selectedObject.sync="pageStartObj"
              style="width: 80px"
            />

            <span>to</span>

            <Dropdown
              placeholder="End"
              :items="pageOptions"
              itemKey="Page"
              :selectedObject.sync="pageEndObj"
              style="width: 80px"
            />

            <button @click="processSelectedPages">Process PDF</button>

            <span class="page-count"> ( {{ selectedPageCount }} selected / {{ totalPages }} pages ) </span>
          </div>
        </div>
        <div class="page-review-pdf">
          <iframe
            v-if="pendingDocAIPreviewSrc"
            class="pdf-display"
            type="application/pdf"
            :src="pendingDocAIPreviewSrc"
          ></iframe>
        </div>
      </div>
    </div>
    <div class="formDisplay" v-if="file">
      <StepProgressBar :steps="steps" :currentStep="currentStep" :stepErrors="stepErrors" @stepClick="goToStep" />
      <div class="row">
        <div class="pdfDisplayColumn">
          <div class="pdf-display-condition">
            <iframe class="pdf-display" v-if="embedSrc" type="video/webm" :src="embedSrc" />
          </div>
        </div>
        <div class="formDisplayColumn">
          <div style="margin-right: 1rem; margin-bottom: 0.1rem; text-align: right">
            <table
              style="
                margin-left: auto;
                text-align: left;
                background-color: #f0f0f0;
                border-collapse: collapse;
                width: auto;
                border: 1px solid #ccc;
              "
            >
              <tr>
                <td style="padding: 0.1rem 0.5rem; border: 1px solid #ccc; border-right: none"
                  ><strong> Detected Document:</strong></td
                >
                <td style="padding: 0.1rem 0.5rem; border: 1px solid #ccc; border-left: none">{{
                  selectedHeaderInfoData['detectedDocument'] ? selectedHeaderInfoData['detectedDocument'] : 'Unknown'
                }}</td>
                <td style="padding: 0.1rem 0.5rem; border: 1px solid #ccc; border-right: none"
                  ><strong> Document Name:</strong></td
                >
                <td style="padding: 0.1rem 0.5rem; border: 1px solid #ccc; border-left: none">{{
                  fileName ? fileName : 'Unknown'
                }}</td>
              </tr>
            </table>
          </div>
          <transition name="step-fade" mode="out-in">
            <div class="stepDisplay" :key="currentStep">
              <template v-if="currentStep === 0">
                <!--Header Info-->
                <div class="table-wrapper">
                  <div class="form-step-0-group-1">
                    <p class="divisionLabel">Header Info</p>
                    <div class="form-content-row">
                      <div class="form-content-key-columns"><span class="compulsory">**</span>Transport Mode</div>
                      <div class="form-content-columns">
                        <Dropdown
                          placeholder=""
                          :items="headerInfoData['transMode']"
                          itemKey="Description"
                          :selectedObject.sync="selectedHeaderInfoData['selectedTransMode']"
                          :errorMessage.sync="step1ErrMessage['selectedTransMode']"
                          validator="text,50"
                        />
                      </div>
                    </div>
                    <div class="form-content-row">
                      <div class="form-content-key-columns"><span class="compulsory">**</span>Cargo Type</div>
                      <div class="form-content-columns">
                        <InputQuery
                          placeholder=""
                          :items="headerInfoData['cargoType']"
                          itemKey="Cargo Type"
                          descKey="Description"
                          :selectedObject.sync="selectedHeaderInfoData['selectedCargoType']"
                          :errorMessage.sync="step1ErrMessage['selectedCargoType']"
                          validator="text,50"
                        />
                      </div>
                    </div>
                    <!-- <div class="form-content-row">
                  <div class="form-content-key-columns">Project Number</div>
                  <div class="form-content-columns">
                    <Input placeholder="" :inputValue.sync="selectedHeaderInfoData['projectNo']" />
                  </div>
                </div> -->
                  </div>
                  <div class="form-step-0-group-2">
                    <div class="form-content-row custom-background">
                      <div class="form-content-key-columns"></div>

                      <div class="form-masterfile-detected-columns comparison-header-cell">
                        <span>{{ comparisonHeader.detectedLabel }}</span>
                        <Tooltip position="right" :minWidth="260" :tooltipText="comparisonHeader.detectedTooltip" />
                      </div>

                      <div class="form-masterfile-submitted-columns comparison-header-cell">
                        <span>{{ comparisonHeader.finalLabel }}</span>
                        <Tooltip position="left" :minWidth="280" :tooltipText="comparisonHeader.finalTooltip" />
                      </div>

                      <div class="form-collision-number-columns comparison-header-cell">
                        <span>{{ comparisonHeader.matchesLabel }}</span>
                        <Tooltip position="left" :minWidth="280" :tooltipText="comparisonHeader.matchesTooltip" />
                      </div>
                    </div>
                    <div class="form-content-row">
                      <div class="form-content-key-columns"
                        ><span v-if="pageType != 'Import'" class="compulsory">**</span>Shipper
                      </div>
                      <div class="form-masterfile-detected-columns">
                        <div
                          ><p :class="{ 'no-ai-value': !docAIData.selectedShipperDisplay }">
                            {{ docAIData.selectedShipperDisplay || 'No value detected' }}</p
                          ></div
                        >
                      </div>
                      <div class="form-masterfile-submitted-columns">
                        <InputQuery
                          tabindex="0"
                          placeholder=""
                          :items="headerInfoData['shipper']"
                          itemKey="Code - Name"
                          descKey="Shipper Name"
                          :selectedObject.sync="selectedHeaderInfoData['selectedShipper']"
                          :errorMessage.sync="step1ErrMessage['selectedShipper']"
                          @selected="shipperSelected"
                          validator="text,115"
                        />
                      </div>
                      <div class="form-collision-number-columns">
                        <div class="center-container">
                          <button
                            :class="[
                              collisionNumber['selectedShipper'] ? 'matchFound' : 'matchNotFound',
                              'button-size',
                            ]"
                            @click="openTableBox($event, matchesList, 'selectedShipper')"
                          >
                            <div
                              >{{ collisionNumber.selectedShipper }} Match(es)
                              <img
                                class="img-size"
                                src="https://dsmlib.logistics.systems/Icons/icons8-expand-arrow-24.png"
                                :style="{ transform: `scaleY(${matchesList['selectedShipper'] ? '-1' : '1'})` }"
                              />
                            </div>
                          </button>
                        </div>
                        <div
                          v-if="matchesList['selectedShipper']"
                          class="collision-table"
                          :style="matchesList['tableStyling']"
                        >
                          <TableBox
                            title=""
                            :tableData="filteredData['shipper']"
                            :additionalButtons="[{ text: 'Select', index: 0, width: 75 }]"
                            :stickColumn="[0]"
                            @buttonClicked="shipperClicked"
                          />
                        </div>
                        <div
                          v-if="matchesList['selectedShipper']"
                          @click="matchesList['selectedShipper'] = false"
                          class="collision-table-background"
                        ></div>
                      </div>
                    </div>
                    <div class="form-content-row">
                      <div class="form-content-key-columns">
                        <button
                          class="btn-fill"
                          v-if="docAIData.shipperAddress || selectedHeaderInfoData['selectedShipper']"
                          @click="applyDocAIAddress(docAIData.shipperAddress, shipperAddress, 'showShipperAddress')"
                        >
                          Use Extracted Address
                        </button>
                      </div>
                      <div class="form-masterfile-detected-columns">
                        <div
                          ><p :class="{ 'no-ai-value': !docAIData.shipperAddress }">
                            {{ docAIData.shipperAddress || 'No value detected' }}</p
                          ></div
                        >
                      </div>
                      <div class="form-masterfile-long-submitted-columns">
                        <div class="address" :class="{ 'address-collapse': !showShipperAddress }">
                          <div class="edit-area">
                            <button
                              v-if="selectedHeaderInfoData['selectedShipper'] !== null"
                              @click="editDetailsPopup('shipper')"
                              >Edit</button
                            >
                            <img
                              src="https://dsmlib.logistics.systems/Icons/icons8-expand-arrow-24.png"
                              alt=""
                              height="20"
                              @click="showHideAddress('shipper')"
                              :class="[showShipperAddress ? 'img-show' : 'img-hide']"
                            />
                          </div>

                          <div v-if="showShipperAddress" class="address-block">
                            <div v-if="shipperAddress">
                              <template v-if="shipperAddress['name']">{{ shipperAddress['name'] }}<br /></template>
                              <template v-if="shipperAddress['add1']">{{ shipperAddress['add1'] }}<br /></template>
                              <template v-if="shipperAddress['add2']">{{ shipperAddress['add2'] }}<br /></template>
                              <template v-if="shipperAddress['add3']">{{ shipperAddress['add3'] }}<br /></template>
                              <template v-if="shipperAddress['add4']">{{ shipperAddress['add4'] }}<br /></template>
                              <template v-if="shipperAddress['postcode'] || shipperAddress['city']">
                                {{ shipperAddress['postcode'] }} {{ shipperAddress['city'] }}<br />
                              </template>
                              <template v-if="shipperAddress['state'] || shipperAddress['country']">
                                {{ shipperAddress['state'] }} {{ shipperAddress['country'] }}<br />
                              </template>
                              <br
                                v-if="
                                  shipperAddress['attn'] ||
                                  shipperAddress['tel'] ||
                                  shipperAddress['fax'] ||
                                  shipperAddress['gst']
                                "
                              />
                              <template v-if="shipperAddress['attn']"
                                >Attn: {{ shipperAddress['attn'] }}<br
                              /></template>
                              <template v-if="shipperAddress['tel']">Tel: {{ shipperAddress['tel'] }}<br /></template>
                              <template v-if="shipperAddress['fax']">Fax: {{ shipperAddress['fax'] }}<br /></template>
                              <template v-if="shipperAddress['gst']"
                                >GST No: {{ shipperAddress['gst'] }}<br
                              /></template>
                            </div>
                            <div v-else> No Shipper Selected </div>
                          </div>
                          <div v-else> Shipper Details </div>
                        </div>
                      </div>
                    </div>
                    <div class="form-content-row">
                      <div class="form-content-key-columns"
                        ><span v-if="pageType == 'Import'" class="compulsory">**</span>Consignee
                      </div>
                      <div class="form-masterfile-detected-columns">
                        <div
                          ><p :class="{ 'no-ai-value': !docAIData.selectedConsigneeDisplay }">
                            {{ docAIData.selectedConsigneeDisplay || 'No value detected' }}</p
                          ></div
                        >
                      </div>
                      <div class="form-masterfile-submitted-columns">
                        <InputQuery
                          tabindex="0"
                          placeholder=""
                          :items="headerInfoData['consignee']"
                          itemKey="Code - Name"
                          descKey="Consignee Name"
                          :selectedObject.sync="selectedHeaderInfoData['selectedConsignee']"
                          :errorMessage.sync="step1ErrMessage['selectedConsignee']"
                          @selected="consigneeSelected"
                          validator="text,115"
                        />
                      </div>
                      <div class="form-collision-number-columns">
                        <div class="center-container">
                          <button
                            :class="[
                              collisionNumber['selectedConsignee'] ? 'matchFound' : 'matchNotFound',
                              'button-size',
                            ]"
                            @click="openTableBox($event, matchesList, 'selectedConsignee')"
                          >
                            <div
                              >{{ collisionNumber.selectedConsignee }} Match(es)
                              <img
                                class="img-size"
                                src="https://dsmlib.logistics.systems/Icons/icons8-expand-arrow-24.png"
                                :style="{ transform: `scaleY(${matchesList['selectedConsignee'] ? '-1' : '1'})` }"
                              />
                            </div>
                          </button>
                        </div>
                        <div
                          v-if="matchesList['selectedConsignee']"
                          class="collision-table"
                          :style="matchesList['tableStyling']"
                        >
                          <TableBox
                            title=""
                            :tableData="filteredData['consignee']"
                            :additionalButtons="[{ text: 'Select', index: 0, width: 75 }]"
                            :stickColumn="[0]"
                            @buttonClicked="consigneeClicked"
                          />
                        </div>
                        <div
                          v-if="matchesList['selectedConsignee']"
                          @click="matchesList['selectedConsignee'] = false"
                          class="collision-table-background"
                        ></div>
                      </div>
                    </div>
                    <div class="form-content-row">
                      <div class="form-content-key-columns">
                        <button
                          v-if="docAIData.consigneeAddress || selectedHeaderInfoData['selectedConsignee']"
                          @click="
                            applyDocAIAddress(docAIData.consigneeAddress, consigneeAddress, 'showConsigneeAddress')
                          "
                        >
                          Use Extracted Address
                        </button>
                      </div>
                      <div class="form-masterfile-detected-columns">
                        <div
                          ><p :class="{ 'no-ai-value': !docAIData.consigneeAddress }">
                            {{ docAIData.consigneeAddress || 'No value detected' }}</p
                          ></div
                        >
                      </div>
                      <div class="form-masterfile-long-submitted-columns">
                        <div class="address" :class="{ 'address-collapse': !showConsigneeAddress }">
                          <div class="edit-area">
                            <button
                              v-if="selectedHeaderInfoData['selectedConsignee'] !== null"
                              @click="editDetailsPopup('consignee')"
                              >Edit</button
                            >
                            <img
                              src="https://dsmlib.logistics.systems/Icons/icons8-expand-arrow-24.png"
                              alt=""
                              height="20"
                              @click="showHideAddress('consignee')"
                              :class="[showConsigneeAddress ? 'img-show' : 'img-hide']"
                            />
                          </div>

                          <div v-if="showConsigneeAddress" class="address-block">
                            <div v-if="consigneeAddress">
                              <template v-if="consigneeAddress['name']">{{ consigneeAddress['name'] }}<br /></template>
                              <template v-if="consigneeAddress['add1']">{{ consigneeAddress['add1'] }}<br /></template>
                              <template v-if="consigneeAddress['add2']">{{ consigneeAddress['add2'] }}<br /></template>
                              <template v-if="consigneeAddress['add3']">{{ consigneeAddress['add3'] }}<br /></template>
                              <template v-if="consigneeAddress['add4']">{{ consigneeAddress['add4'] }}<br /></template>

                              <template v-if="consigneeAddress['postcode'] || consigneeAddress['city']">
                                {{ consigneeAddress['postcode'] }} {{ consigneeAddress['city'] }}<br />
                              </template>

                              <template v-if="consigneeAddress['state'] || consigneeAddress['country']">
                                {{ consigneeAddress['state'] }} {{ consigneeAddress['country'] }}<br />
                              </template>

                              <br
                                v-if="
                                  consigneeAddress['attn'] ||
                                  consigneeAddress['tel'] ||
                                  consigneeAddress['fax'] ||
                                  consigneeAddress['gst']
                                "
                              />

                              <template v-if="consigneeAddress['attn']"
                                >Attn: {{ consigneeAddress['attn'] }}<br
                              /></template>
                              <template v-if="consigneeAddress['tel']"
                                >Tel: {{ consigneeAddress['tel'] }}<br
                              /></template>
                              <template v-if="consigneeAddress['fax']"
                                >Fax: {{ consigneeAddress['fax'] }}<br
                              /></template>
                              <template v-if="consigneeAddress['gst']"
                                >GST No: {{ consigneeAddress['gst'] }}<br
                              /></template>
                            </div>
                            <div v-else> No Consignee Selected </div>
                          </div>
                          <div v-else> Consignee Details </div>
                        </div>
                      </div>
                    </div>
                    <div class="form-content-row">
                      <div class="form-content-key-columns">Notify Party</div>
                      <div class="form-masterfile-detected-columns">
                        <div
                          ><p :class="{ 'no-ai-value': !docAIData.selectedNotifyPartyDisplay }">
                            {{ docAIData.selectedNotifyPartyDisplay || 'No value detected' }}</p
                          ></div
                        >
                      </div>
                      <div class="form-masterfile-submitted-columns">
                        <!-- <InputDiv
                      :info="selectedHeaderInfoData['selectedNotifyParty']"
                    >  
                      <template v-slot="{ trigger }"> -->
                        <InputQuery
                          tabindex="0"
                          placeholder=""
                          :items="headerInfoData['notifyParty']"
                          itemKey="Code - Name"
                          descKey="Notify Party Name"
                          :selectedObject.sync="selectedHeaderInfoData['selectedNotifyParty']"
                          validator="text,115"
                          @selected="npartySelected"
                        />
                        <!-- </template>
                    </InputDiv> -->
                      </div>
                      <div class="form-collision-number-columns">
                        <div class="center-container">
                          <button
                            :class="[
                              collisionNumber['selectedNotifyParty'] ? 'matchFound' : 'matchNotFound',
                              'button-size',
                            ]"
                            @click="openTableBox($event, matchesList, 'selectedNotifyParty')"
                          >
                            <div
                              >{{ collisionNumber.selectedNotifyParty }} Match(es)
                              <img
                                class="img-size"
                                src="https://dsmlib.logistics.systems/Icons/icons8-expand-arrow-24.png"
                                :style="{ transform: `scaleY(${matchesList['selectedNotifyParty'] ? '-1' : '1'})` }"
                              />
                            </div>
                          </button>
                        </div>
                        <div
                          v-if="matchesList['selectedNotifyParty']"
                          class="collision-table"
                          :style="matchesList['tableStyling']"
                        >
                          <TableBox
                            title=""
                            :tableData="filteredData['notifyParty']"
                            :additionalButtons="[{ text: 'Select', index: 0, width: 75 }]"
                            :stickColumn="[0]"
                            @buttonClicked="notifyPartyClicked"
                          />
                        </div>
                        <div
                          v-if="matchesList['selectedNotifyParty']"
                          @click="matchesList['selectedNotifyParty'] = false"
                          class="collision-table-background"
                        ></div>
                      </div>
                    </div>
                    <div class="form-content-row">
                      <div class="form-content-key-columns">
                        <button
                          v-if="docAIData.nPartyAddress"
                          @click="applyDocAIAddress(docAIData.nPartyAddress, notifyPartyAddress, 'showNPartyAddress')"
                        >
                          Use Extracted Address
                        </button>
                      </div>
                      <div class="form-masterfile-detected-columns">
                        <div
                          ><p :class="{ 'no-ai-value': !docAIData.nPartyAddress }">
                            {{ docAIData.nPartyAddress || 'No value detected' }}</p
                          ></div
                        >
                      </div>
                      <div class="form-masterfile-long-submitted-columns">
                        <div class="address" :class="{ 'address-collapse': !showNPartyAddress }">
                          <div class="edit-area">
                            <button
                              v-if="selectedHeaderInfoData['selectedNotifyParty'] !== null"
                              @click="editDetailsPopup('nparty')"
                              >Edit</button
                            >
                            <img
                              src="https://dsmlib.logistics.systems/Icons/icons8-expand-arrow-24.png"
                              alt=""
                              height="20"
                              @click="showHideAddress('nparty')"
                              :class="[showNPartyAddress ? 'img-show' : 'img-hide']"
                            />
                          </div>

                          <div v-if="showNPartyAddress" class="address-block">
                            <div v-if="notifyPartyAddress">
                              <template v-if="notifyPartyAddress['name']"
                                >{{ notifyPartyAddress['name'] }}<br
                              /></template>
                              <template v-if="notifyPartyAddress['add1']"
                                >{{ notifyPartyAddress['add1'] }}<br
                              /></template>
                              <template v-if="notifyPartyAddress['add2']"
                                >{{ notifyPartyAddress['add2'] }}<br
                              /></template>
                              <template v-if="notifyPartyAddress['add3']"
                                >{{ notifyPartyAddress['add3'] }}<br
                              /></template>
                              <template v-if="notifyPartyAddress['add4']"
                                >{{ notifyPartyAddress['add4'] }}<br
                              /></template>

                              <template v-if="notifyPartyAddress['postcode'] || notifyPartyAddress['city']">
                                {{ notifyPartyAddress['postcode'] }} {{ notifyPartyAddress['city'] }}<br />
                              </template>

                              <template v-if="notifyPartyAddress['state'] || notifyPartyAddress['country']">
                                {{ notifyPartyAddress['state'] }} {{ notifyPartyAddress['country'] }}<br />
                              </template>

                              <br
                                v-if="
                                  notifyPartyAddress['attn'] ||
                                  notifyPartyAddress['tel'] ||
                                  notifyPartyAddress['fax'] ||
                                  notifyPartyAddress['gst']
                                "
                              />

                              <template v-if="notifyPartyAddress['attn']"
                                >Attn: {{ notifyPartyAddress['attn'] }}<br
                              /></template>
                              <template v-if="notifyPartyAddress['tel']"
                                >Tel: {{ notifyPartyAddress['tel'] }}<br
                              /></template>
                              <template v-if="notifyPartyAddress['fax']"
                                >Fax: {{ notifyPartyAddress['fax'] }}<br
                              /></template>
                              <template v-if="notifyPartyAddress['gst']"
                                >GST No: {{ notifyPartyAddress['gst'] }}<br
                              /></template>
                            </div>
                            <div v-else> No Notify Party Selected </div>
                          </div>
                          <div v-else> Notify Party Address </div>
                        </div>
                      </div>
                    </div>
                    <div class="form-content-row">
                      <div class="form-content-key-columns"><span class="compulsory">**</span>Bill To</div>
                      <div class="form-masterfile-detected-columns">
                        <div
                          ><p :class="{ 'no-ai-value': !docAIData.selectedBillToDisplay }">
                            {{ docAIData.selectedBillToDisplay || 'No value detected' }}</p
                          ></div
                        >
                      </div>
                      <div class="form-masterfile-submitted-columns">
                        <!-- <InputDiv
                      :info="selectedHeaderInfoData['selectedBillTo']"
                    >
                      <template v-slot="{ trigger }"> -->
                        <InputQuery
                          tabindex="0"
                          placeholder=""
                          :items="headerInfoData['billTo']"
                          itemKey="Code - Name"
                          descKey="Customer Name"
                          :selectedObject.sync="selectedHeaderInfoData['selectedBillTo']"
                          :errorMessage.sync="step1ErrMessage['selectedBillTo']"
                          validator="text,115"
                        />
                        <!-- </template>  
                    </InputDiv> -->
                      </div>
                      <div class="form-collision-number-columns">
                        <div class="center-container">
                          <button
                            :class="[collisionNumber['selectedBillTo'] ? 'matchFound' : 'matchNotFound', 'button-size']"
                            @click="openTableBox($event, matchesList, 'selectedBillTo')"
                          >
                            <div
                              >{{ collisionNumber.selectedBillTo }} Match(es)
                              <img
                                class="img-size"
                                src="https://dsmlib.logistics.systems/Icons/icons8-expand-arrow-24.png"
                                :style="{ transform: `scaleY(${matchesList['selectedBillTo'] ? '-1' : '1'})` }"
                              />
                            </div>
                          </button>
                        </div>
                        <div
                          v-if="matchesList['selectedBillTo']"
                          class="collision-table"
                          :style="matchesList['tableStyling']"
                        >
                          <TableBox
                            title=""
                            :tableData="filteredData['billTo']"
                            :additionalButtons="[{ text: 'Select', index: 0, width: 75 }]"
                            :stickColumn="[0]"
                            @buttonClicked="billToClicked"
                          />
                        </div>
                        <div
                          v-if="matchesList['selectedBillTo']"
                          @click="matchesList['selectedBillTo'] = false"
                          class="collision-table-background"
                        ></div>
                      </div>
                    </div>
                    <div class="form-content-row">
                      <div class="form-content-key-columns">Customer Ref #</div>
                      <div class="form-masterfile-detected-columns">
                        <div
                          ><p :class="{ 'no-ai-value': !docAIData.custRefNo }">
                            {{ docAIData.custRefNo || 'No value detected' }}</p
                          ></div
                        >
                      </div>
                      <div class="form-masterfile-submitted-columns">
                        <Input
                          placeholder=""
                          :inputValue.sync="selectedHeaderInfoData['custRefNo']"
                          validator="text,40"
                        />
                      </div>
                      <div class="form-collision-number-columns"> </div>
                    </div>
                    <div class="form-content-row">
                      <div v-if="pageType != 'Import'" class="form-content-key-columns">Booking Ref #</div>
                      <div v-if="pageType == 'Import'" class="form-content-key-columns">B/L #</div>
                      <div class="form-masterfile-detected-columns">
                        <div
                          ><p :class="{ 'no-ai-value': !docAIData.vbkgRefNo }">
                            {{ docAIData.vbkgRefNo || 'No value detected' }}</p
                          ></div
                        >
                      </div>
                      <div class="form-masterfile-submitted-columns">
                        <Input
                          placeholder=""
                          :inputValue.sync="selectedHeaderInfoData['vbkgRefNo']"
                          validator="text,25"
                        />
                      </div>
                      <div class="form-collision-number-columns"> </div>
                    </div>
                    <br /><br /><br /><br />
                  </div>
                </div>
                <div class="button-group">
                  <button class="jfCompNextButton" type="button" @click="nextStep()">Next</button>
                </div>
              </template>
              <template v-if="currentStep === 1">
                <!--Shipping Info 1-->
                <div class="table-wrapper">
                  <div class="form-step-1-group-1">
                    <p class="divisionLabel">Shipping Info (1/3)</p>
                    <div class="form-content-row custom-background">
                      <div class="form-content-key-columns"></div>
                      <div class="form-masterfile-detected-columns comparison-header-cell">
                        <span>{{ comparisonHeader.detectedLabel }}</span>
                        <Tooltip position="right" :minWidth="260" :tooltipText="comparisonHeader.detectedTooltip" />
                      </div>

                      <div class="form-masterfile-submitted-columns comparison-header-cell">
                        <span>{{ comparisonHeader.finalLabel }}</span>
                        <Tooltip position="left" :minWidth="280" :tooltipText="comparisonHeader.finalTooltip" />
                      </div>

                      <div class="form-collision-number-columns comparison-header-cell">
                        <span>{{ comparisonHeader.matchesLabel }}</span>
                        <Tooltip position="left" :minWidth="280" :tooltipText="comparisonHeader.matchesTooltip" />
                      </div>
                    </div>
                    <div class="form-content-row">
                      <div class="form-content-key-columns"><span class="compulsory">**</span>Ship Call No</div>
                      <div class="form-masterfile-detected-columns">
                        <div
                          ><p :class="{ 'no-ai-value': !docAIData.selectedShipCall }">
                            {{ docAIData.selectedShipCall || 'No value detected' }}</p
                          ></div
                        >
                      </div>
                      <div class="form-masterfile-submitted-columns">
                        <InputQuery
                          tabindex="0"
                          placeholder=""
                          :items="shippingInfoData['shipCallNoList']"
                          itemKey="SCN - Terminal"
                          descKey="Vessel Name"
                          :selectedObject.sync="selectedShippingInfoData['selectedShipCall']"
                          ref="shipcall_no"
                          :errorMessage.sync="step2ErrMessage['selectedShipCall']"
                          validator="text,10"
                          :allowFreeText="true"
                          @selected="shipCallNoSelected"
                        />
                        <!-- </template>
                     </InputDiv> -->
                      </div>
                      <div class="form-collision-number-columns">
                        <div class="center-container">
                          <button
                            :class="[
                              collisionNumber['selectedShipCall'] ? 'matchFound' : 'matchNotFound',
                              'button-size',
                            ]"
                            @click="openTableBox($event, matchesList, 'selectedShipCall')"
                          >
                            <div
                              >{{ collisionNumber.selectedShipCall }} Match(es)
                              <img
                                class="img-size"
                                src="https://dsmlib.logistics.systems/Icons/icons8-expand-arrow-24.png"
                                :style="{ transform: `scaleY(${matchesList['selectedShipCall'] ? '-1' : '1'})` }"
                              />
                            </div>
                          </button>
                        </div>
                        <div
                          v-if="matchesList['selectedShipCall']"
                          class="collision-table"
                          :style="matchesList['tableStyling']"
                        >
                          <TableBox
                            title=""
                            :tableData="filteredData['shipCallNo']"
                            :additionalButtons="[{ text: 'Select', index: 0, width: 75 }]"
                            :stickColumn="[0]"
                            @buttonClicked="shipCallClicked"
                          />
                        </div>
                        <div
                          v-if="matchesList['selectedShipCall']"
                          @click="matchesList['selectedShipCall'] = false"
                          class="collision-table-background"
                        ></div>
                      </div>
                    </div>
                    <div class="form-content-row">
                      <div class="form-content-key-columns"><span class="compulsory">**</span>Voyage #</div>
                      <div class="form-masterfile-detected-columns">
                        <div
                          ><p :class="{ 'no-ai-value': !docAIData.voyNo }">
                            {{ docAIData.voyNo || 'No value detected' }}</p
                          ></div
                        >
                      </div>
                      <div class="form-masterfile-submitted-columns">
                        <span
                          v-if="shipCallChangedFields['voyNo']"
                          class="ship-call-changed-indicator"
                          title="Value updated from selected Ship Call No"
                          >SCN</span
                        >
                        <Input
                          placeholder=""
                          :inputValue.sync="selectedShippingInfoData['voyNo']"
                          validator="text,10"
                          :errorMessage.sync="step2ErrMessage['voyNo']"
                        />
                      </div>
                      <div class="form-collision-number-columns"> </div>
                    </div>
                    <div class="form-content-row">
                      <div class="form-content-key-columns"
                        ><span class="compulsory">**</span>Vessel ID <br /><br />Vessel Name</div
                      >
                      <div class="form-masterfile-detected-columns">
                        <!-- <p :class="{ 'no-ai-value': !docAIData.selectedVesselID && !docAIData.selectedVesselName }">
                      {{ docAIData.selectedVesselID || "No value detected" }}<br>
                      Vessel Name: {{ docAIData.selectedVesselName || "No value detected" }}</p> -->
                        <p :class="{ 'no-ai-value': !docAIData.selectedVesselID }">
                          {{ docAIData.selectedVesselID || 'No value detected' }}</p
                        >
                        <p :class="{ 'no-ai-value': !docAIData.selectedVesselName }">
                          {{ docAIData.selectedVesselName || 'No value detected' }}</p
                        >
                      </div>
                      <div class="form-masterfile-submitted-columns">
                        <!-- <InputDiv
                      :info="selectedShippingInfoData['selectedVesselID']"
                    >
                      <template v-slot="{ trigger }"> -->
                        <span
                          v-if="shipCallChangedFields['selectedVesselID']"
                          class="ship-call-changed-indicator"
                          title="Value updated from selected Ship Call No"
                          >SCN</span
                        >
                        <InputQuery
                          tabindex="0"
                          placeholder=""
                          :items="shippingInfoData['vesselIDList']"
                          itemKey="ID - Name"
                          descKey="Vessel Name"
                          :selectedObject.sync="selectedShippingInfoData['selectedVesselID']"
                          :errorMessage.sync="step2ErrMessage['selectedVesselID']"
                          validator="text,45"
                        />
                        <!-- </template>
                    </InputDiv> -->
                      </div>
                      <div class="form-collision-number-columns">
                        <div class="center-container">
                          <button
                            :class="[
                              collisionNumber['selectedVesselID'] ? 'matchFound' : 'matchNotFound',
                              'button-size',
                            ]"
                            @click="openTableBox($event, matchesList, 'selectedVesselID')"
                          >
                            <div
                              >{{ collisionNumber.selectedVesselID }} Match(es)
                              <img
                                class="img-size"
                                src="https://dsmlib.logistics.systems/Icons/icons8-expand-arrow-24.png"
                                :style="{ transform: `scaleY(${matchesList['selectedVesselID'] ? '-1' : '1'})` }"
                              />
                            </div>
                          </button>
                        </div>
                        <div
                          v-if="matchesList['selectedVesselID']"
                          class="collision-table"
                          :style="matchesList['tableStyling']"
                        >
                          <TableBox
                            title=""
                            :tableData="filteredData['vesselID']"
                            :additionalButtons="[{ text: 'Select', index: 0, width: 75 }]"
                            :stickColumn="[0]"
                            @buttonClicked="vesselIDClicked"
                          />
                        </div>
                        <div
                          v-if="matchesList['selectedVesselID']"
                          @click="matchesList['selectedVesselID'] = false"
                          class="collision-table-background"
                        ></div>
                      </div>
                    </div>
                    <div class="form-content-row">
                      <div class="form-content-key-columns"><span class="compulsory">**</span>Port of Loading</div>
                      <div class="form-masterfile-detected-columns">
                        <div
                          ><p :class="{ 'no-ai-value': !docAIData.selectedPortLoading }">
                            {{ docAIData.selectedPortLoading || 'No value detected' }}</p
                          ></div
                        >
                      </div>
                      <div class="form-masterfile-submitted-columns">
                        <!-- <InputDiv
                      :info="selectedShippingInfoData['selectedPortLoading']"
                     >  
                      <template v-slot="{ trigger }"> -->
                        <InputQuery
                          tabindex="0"
                          placeholder=""
                          :items="shippingInfoData['portOfLoading']"
                          itemKey="Code - Desc"
                          descKey="Port Location"
                          :selectedObject.sync="selectedShippingInfoData['selectedPortLoading']"
                          ref="imp_port"
                          :errorMessage.sync="step2ErrMessage['selectedPortLoading']"
                          validator="text,10"
                        />
                        <!-- </template>
                     </InputDiv> -->
                      </div>
                      <div class="form-collision-number-columns">
                        <div class="center-container">
                          <button
                            :class="[
                              collisionNumber['selectedPortLoading'] ? 'matchFound' : 'matchNotFound',
                              'button-size',
                            ]"
                            @click="openTableBox($event, matchesList, 'selectedPortLoading')"
                          >
                            <div
                              >{{ collisionNumber.selectedPortLoading }} Match(es)
                              <img
                                class="img-size"
                                src="https://dsmlib.logistics.systems/Icons/icons8-expand-arrow-24.png"
                                :style="{ transform: `scaleY(${matchesList['selectedPortLoading'] ? '-1' : '1'})` }"
                              />
                            </div>
                          </button>
                        </div>
                        <div
                          v-if="matchesList['selectedPortLoading']"
                          class="collision-table"
                          :style="matchesList['tableStyling']"
                        >
                          <TableBox
                            title=""
                            :tableData="filteredData['portOfLoading']"
                            :additionalButtons="[{ text: 'Select', index: 0, width: 75 }]"
                            :stickColumn="[0]"
                            @buttonClicked="portOfLoadingClicked"
                          />
                        </div>
                        <div
                          v-if="matchesList['selectedPortLoading']"
                          @click="matchesList['selectedPortLoading'] = false"
                          class="collision-table-background"
                        ></div>
                      </div>
                    </div>
                    <div class="form-content-row">
                      <div class="form-content-key-columns"><span class="compulsory">**</span>Port of Discharge</div>
                      <div class="form-masterfile-detected-columns">
                        <div
                          ><p :class="{ 'no-ai-value': !docAIData.selectedPortDischarge }">
                            {{ docAIData.selectedPortDischarge || 'No value detected' }}</p
                          ></div
                        >
                      </div>
                      <div class="form-masterfile-submitted-columns">
                        <!-- <InputDiv
                      :info="selectedShippingInfoData['selectedPortDischarge']"
                    >  
                      <template v-slot="{ trigger }"> -->
                        <InputQuery
                          tabindex="0"
                          placeholder=""
                          :items="shippingInfoData['portOfDischarge']"
                          itemKey="Code - Desc"
                          descKey="Port Location"
                          :selectedObject.sync="selectedShippingInfoData['selectedPortDischarge']"
                          ref="load_port"
                          :errorMessage.sync="step2ErrMessage['selectedPortDischarge']"
                          validator="text,10"
                        />
                        <!-- </template>
                    </InputDiv> -->
                      </div>
                      <div class="form-collision-number-columns">
                        <div class="center-container">
                          <button
                            :class="[
                              collisionNumber['selectedPortDischarge'] ? 'matchFound' : 'matchNotFound',
                              'button-size',
                            ]"
                            @click="openTableBox($event, matchesList, 'selectedPortDischarge')"
                          >
                            <div
                              >{{ collisionNumber.selectedPortDischarge }} Match(es)
                              <img
                                class="img-size"
                                src="https://dsmlib.logistics.systems/Icons/icons8-expand-arrow-24.png"
                                :style="{ transform: `scaleY(${matchesList['selectedPortDischarge'] ? '-1' : '1'})` }"
                              />
                            </div>
                          </button>
                        </div>
                        <div
                          v-if="matchesList['selectedPortDischarge']"
                          class="collision-table"
                          :style="matchesList['tableStyling']"
                        >
                          <TableBox
                            title=""
                            :tableData="filteredData['portOfDischarge']"
                            :additionalButtons="[{ text: 'Select', index: 0, width: 75 }]"
                            :stickColumn="[0]"
                            @buttonClicked="portDischargeClicked"
                          />
                        </div>
                        <div
                          v-if="matchesList['selectedPortDischarge']"
                          @click="matchesList['selectedPortDischarge'] = false"
                          class="collision-table-background"
                        ></div>
                      </div>
                    </div>
                    <div class="form-content-row">
                      <div class="form-content-key-columns"><span class="compulsory">**</span>Shipping Agent</div>
                      <div class="form-masterfile-detected-columns">
                        <div
                          ><p :class="{ 'no-ai-value': !docAIData.selectedAgentList }">
                            {{ docAIData.selectedAgentList || 'No value detected' }}</p
                          ></div
                        >
                      </div>
                      <div class="form-masterfile-submitted-columns">
                        <!-- <InputDiv
                      :info="selectedShippingInfoData['selectedAgentList']"
                    >  
                      <template v-slot="{ trigger }"> -->
                        <InputQuery
                          tabindex="0"
                          placeholder=""
                          :items="shippingInfoData['shippingAgentList']"
                          itemKey="Code - Name"
                          descKey="Agent Name"
                          :selectedObject.sync="selectedShippingInfoData['selectedAgentList']"
                          :errorMessage.sync="step2ErrMessage['selectedAgentList']"
                          validator="text,115"
                        />
                        <!-- </template>
                    </InputDiv> -->
                      </div>
                      <div class="form-collision-number-columns">
                        <div class="center-container">
                          <button
                            :class="[
                              collisionNumber['selectedAgentList'] ? 'matchFound' : 'matchNotFound',
                              'button-size',
                            ]"
                            @click="openTableBox($event, matchesList, 'selectedAgentList')"
                          >
                            <div
                              >{{ collisionNumber.selectedAgentList }} Match(es)
                              <img
                                class="img-size"
                                src="https://dsmlib.logistics.systems/Icons/icons8-expand-arrow-24.png"
                                :style="{ transform: `scaleY(${matchesList['selectedAgentList'] ? '-1' : '1'})` }"
                              />
                            </div>
                          </button>
                        </div>
                        <div
                          v-if="matchesList['selectedAgentList']"
                          class="collision-table"
                          :style="matchesList['tableStyling']"
                        >
                          <TableBox
                            title=""
                            :tableData="filteredData['agentList']"
                            :additionalButtons="[{ text: 'Select', index: 0, width: 75 }]"
                            :stickColumn="[0]"
                            @buttonClicked="agentListClicked"
                          />
                        </div>
                        <div
                          v-if="matchesList['selectedAgentList']"
                          @click="matchesList['selectedAgentList'] = false"
                          class="collision-table-background"
                        ></div>
                      </div>
                    </div>
                    <div class="form-content-row">
                      <div class="form-content-key-columns">Shipping Line</div>
                      <div class="form-masterfile-detected-columns">
                        <div
                          ><p :class="{ 'no-ai-value': !docAIData.selectedShippingLine }">
                            {{ docAIData.selectedShippingLine || 'No value detected' }}</p
                          ></div
                        >
                      </div>
                      <div class="form-masterfile-submitted-columns">
                        <!-- <InputDiv
                      :info="selectedShippingInfoData['selectedShippingLine']"
                    >  
                      <template v-slot="{ trigger }"> -->
                        <InputQuery
                          tabindex="0"
                          placeholder=""
                          :items="shippingInfoData['shippingLineList']"
                          itemKey="Code - Name"
                          descKey="Agent Name"
                          :selectedObject.sync="selectedShippingInfoData['selectedShippingLine']"
                          validator="text,115"
                        />
                        <!-- </template>
                    </InputDiv> -->
                      </div>
                      <div class="form-collision-number-columns">
                        <div class="center-container">
                          <button
                            :class="[
                              collisionNumber['selectedShippingLine'] ? 'matchFound' : 'matchNotFound',
                              'button-size',
                            ]"
                            @click="openTableBox($event, matchesList, 'selectedShippingLine')"
                          >
                            <div
                              >{{ collisionNumber.selectedShippingLine }} Match(es)
                              <img
                                class="img-size"
                                src="https://dsmlib.logistics.systems/Icons/icons8-expand-arrow-24.png"
                                :style="{ transform: `scaleY(${matchesList['selectedShippingLine'] ? '-1' : '1'})` }"
                              />
                            </div>
                          </button>
                        </div>
                        <div
                          v-if="matchesList['selectedShippingLine']"
                          class="collision-table"
                          :style="matchesList['tableStyling']"
                        >
                          <TableBox
                            title=""
                            :tableData="filteredData['shippingLine']"
                            :additionalButtons="[{ text: 'Select', index: 0, width: 75 }]"
                            :stickColumn="[0]"
                            @buttonClicked="shippingLineClicked"
                          />
                        </div>
                        <div
                          v-if="matchesList['selectedShippingLine']"
                          @click="matchesList['selectedShippingLine'] = false"
                          class="collision-table-background"
                        ></div>
                      </div>
                    </div>
                    <div class="form-content-row">
                      <div class="form-content-key-columns">Frt Forwarder</div>
                      <div class="form-masterfile-detected-columns">
                        <div
                          ><p :class="{ 'no-ai-value': !docAIData.selectedFrtForwarder }">
                            {{ docAIData.selectedFrtForwarder || 'No value detected' }}</p
                          ></div
                        >
                      </div>
                      <div class="form-masterfile-submitted-columns">
                        <InputQuery
                          tabindex="0"
                          placeholder=""
                          :items="shippingInfoData['frtForwarderList']"
                          itemKey="Code - Name"
                          descKey="Agent Name"
                          :selectedObject.sync="selectedShippingInfoData['selectedFrtForwarder']"
                          validator="text,115"
                        />
                      </div>
                      <div class="form-collision-number-columns">
                        <div class="center-container">
                          <button
                            :class="[
                              collisionNumber['selectedFrtForwarder'] ? 'matchFound' : 'matchNotFound',
                              'button-size',
                            ]"
                            @click="openTableBox($event, matchesList, 'selectedFrtForwarder')"
                          >
                            <div
                              >{{ collisionNumber.selectedFrtForwarder }} Match(es)
                              <img
                                class="img-size"
                                src="https://dsmlib.logistics.systems/Icons/icons8-expand-arrow-24.png"
                                :style="{ transform: `scaleY(${matchesList['selectedFrtForwarder'] ? '-1' : '1'})` }"
                              />
                            </div>
                          </button>
                        </div>
                        <div
                          v-if="matchesList['selectedFrtForwarder']"
                          class="collision-table"
                          :style="matchesList['tableStyling']"
                        >
                          <TableBox
                            title=""
                            :tableData="filteredData['frtForwarder']"
                            :additionalButtons="[{ text: 'Select', index: 0, width: 75 }]"
                            :stickColumn="[0]"
                            @buttonClicked="frtForwarderClicked"
                          />
                        </div>
                        <div
                          v-if="matchesList['selectedFrtForwarder']"
                          @click="matchesList['selectedFrtForwarder'] = false"
                          class="collision-table-background"
                        ></div>
                        <!-- <Card :isTableProp="true">
                      <template v-slot:title>
                        <span :class="collisionNumber['selectedFrtForwarder'] ? 'matchFound' : 'matchNotFound'">{{ collisionNumber.selectedFrtForwarder }} match(es)</span>
                      </template>
                      <template v-slot:default>
                        <TableBox title="" tableID="sltFrtForwarder" :tableData="filteredData['frtForwarder']" :selectedRow.sync="filteredData['selectedFrtForwarder']" />
                      </template>
                    </Card> -->
                      </div>
                    </div>
                    <br /><br /><br /><br />
                  </div>
                </div>
                <div class="button-group">
                  <button class="prevButton" type="button" @click="prevStep()">Prev</button>
                  <button class="jfCompNextButton" type="button" @click="nextStep()">Next</button>
                </div>
              </template>
              <template v-if="currentStep === 2">
                <!--Shipping Info 2-->
                <div class="table-wrapper">
                  <div class="form-step-5-group-1">
                    <p class="divisionLabel">Shipping Info (2/3)</p>
                    <div class="form-content-row custom-background">
                      <div class="form-content-key-columns"></div>

                      <div class="form-masterfile-detected-columns comparison-header-cell">
                        <span>{{ comparisonHeader.detectedLabel }}</span>
                        <Tooltip position="right" :minWidth="260" :tooltipText="comparisonHeader.detectedTooltip" />
                      </div>

                      <div class="form-masterfile-submitted-columns comparison-header-cell">
                        <span>{{ comparisonHeader.finalLabel }}</span>
                        <Tooltip position="left" :minWidth="280" :tooltipText="comparisonHeader.finalTooltip" />
                      </div>
                    </div>
                    <p class="divisionLabelSmall port-label-cell">
                      <span>Port Of Loading</span>
                      <span
                        v-if="
                          selectedShippingInfoData['selectedPortLoading'] &&
                          selectedShippingInfoData['selectedPortLoading']['Code - Desc']
                        "
                        class="port-code-badge"
                      >
                        {{ selectedShippingInfoData['selectedPortLoading']['Code - Desc'] }}
                      </span>
                    </p>

                    <!-- <div class="form-content-row">
                      <div class="form-content-key-columns">Code</div>
                      <div class="form-masterfile-detected-columns">
                        <div
                          ><p :class="{ 'no-ai-value': !docAIData.selectedPortLoading }">
                            {{ docAIData.selectedPortLoading || 'No value detected' }}</p
                          ></div
                        >
                      </div>
                      <div class="form-masterfile-only-submitted-columns">
                        <InputQuery
                          placeholder=""
                          :items="shippingInfoData['portOfLoading']"
                          itemKey="Code - Desc"
                          descKey="Port Location"
                          :isDisabled="true"
                          :selectedObject.sync="selectedShippingInfoData['selectedPortLoading']"
                          ref="imp_port"
                          validator="text,10"
                        />
                      </div>
                      <div class="form-collision-number-columns"> </div>
                    </div> -->
                    <div v-if="pageType != 'Import'" class="form-content-row">
                      <div class="form-content-key-columns"><span class="compulsory">**</span>POL ETA</div>
                      <div class="form-masterfile-detected-columns">
                        <div
                          ><p :class="{ 'no-ai-value': !docAIData.polETA }">
                            {{ docAIData.polETA || 'No value detected' }}</p
                          ></div
                        >
                      </div>
                      <div class="form-masterfile-only-submitted-columns">
                        <span
                          v-if="shipCallChangedFields['portOfLoadingETA']"
                          class="ship-call-changed-indicator"
                          title="Value updated from selected Ship Call No"
                          >SCN</span
                        >
                        <DateTimePicker
                          :maxDate="maxDate"
                          :minDate="minDate"
                          :value.sync="selectedShippingInfoData['portOfLoadingETA']"
                          ref="POLETAPicker"
                          :errorMessage.sync="step3ErrMessage['portOfLoadingETA']"
                          @dateChanged="dateChanged('portOfLoadingETA')"
                          @click.native="adjustCalendarPosition('POLETAPicker')"
                        />
                      </div>
                      <div class="form-collision-number-columns"> </div>
                    </div>
                    <div v-if="pageType == 'Import'" class="form-content-row">
                      <div class="form-content-key-columns">POL ETD</div>
                      <div class="form-masterfile-detected-columns">
                        <div
                          ><p :class="{ 'no-ai-value': !docAIData.polETD }">
                            {{ docAIData.polETD || 'No value detected' }}</p
                          ></div
                        >
                      </div>
                      <div class="form-masterfile-only-submitted-columns">
                        <DateTimePicker
                          :minDate="minDate"
                          :maxDate="maxDate"
                          :value.sync="selectedShippingInfoData['portOfLoadingETD']"
                          ref="POLETDPicker"
                          :errorMessage.sync="step3ErrMessage['portOfLoadingETD']"
                          @click.native="adjustCalendarPosition('POLETDPicker')"
                        />
                      </div>
                      <div class="form-collision-number-columns"> </div>
                    </div>
                    <div v-if="pageType != 'Import'" class="form-content-row">
                      <div class="form-content-key-columns"><span class="compulsory">**</span>Closing</div>
                      <div class="form-masterfile-detected-columns">
                        <div
                          ><p :class="{ 'no-ai-value': !docAIData.closingTime }">
                            {{ docAIData.closingTime || 'No value detected' }}</p
                          ></div
                        >
                      </div>
                      <div class="form-masterfile-only-submitted-columns">
                        <span
                          v-if="shipCallChangedFields['closingTime']"
                          class="ship-call-changed-indicator"
                          title="Value updated from selected Ship Call No"
                          >SCN</span
                        >
                        <DateTimePicker
                          :minDate="minDate"
                          :maxDate="maxDate"
                          :value.sync="selectedShippingInfoData['closingTime']"
                          ref="closingTime"
                          :errorMessage.sync="step3ErrMessage['closingTime']"
                          @dateChanged="dateChanged('closingTime')"
                          @click.native="adjustCalendarPosition('closingTime')"
                        />
                      </div>
                      <div class="form-collision-number-columns"> </div>
                    </div>
                  </div>
                  <div class="form-step-5-group-2">
                    <p class="divisionLabelSmall port-label-cell">
                      <span>Port Of Discharge</span>
                      <span
                        v-if="
                          selectedShippingInfoData['selectedPortDischarge'] &&
                          selectedShippingInfoData['selectedPortDischarge']['Code - Desc']
                        "
                        class="port-code-badge"
                      >
                        {{ selectedShippingInfoData['selectedPortDischarge']['Code - Desc'] }}
                      </span>
                    </p>
                    <!-- <div class="form-content-row">
                      <div class="form-content-key-columns">Code</div>
                      <div class="form-masterfile-detected-columns">
                        <div
                          ><p :class="{ 'no-ai-value': !docAIData.selectedPortDischarge }">
                            {{ docAIData.selectedPortDischarge || 'No value detected' }}</p
                          ></div
                        >
                      </div>
                      <div class="form-masterfile-only-submitted-columns">
                        <InputQuery
                          placeholder=""
                          :items="shippingInfoData['portOfDischarge']"
                          itemKey="Code - Desc"
                          descKey="Port Location"
                          :isDisabled="true"
                          :selectedObject.sync="selectedShippingInfoData['selectedPortDischarge']"
                          ref="load_port"
                          validator="text,10"
                        />
                      </div>
                      <div class="form-collision-number-columns"> </div>
                    </div> -->
                    <div class="form-content-row">
                      <div class="form-content-key-columns"><span class="compulsory">**</span>POD ETA</div>
                      <div class="form-masterfile-detected-columns">
                        <div
                          ><p :class="{ 'no-ai-value': !docAIData.podETA }">
                            {{ docAIData.podETA || 'No value detected' }}</p
                          ></div
                        >
                      </div>
                      <div class="form-masterfile-only-submitted-columns">
                        <span
                          v-if="shipCallChangedFields['portOfDischargeETA']"
                          class="ship-call-changed-indicator"
                          title="Value updated from selected Ship Call No"
                          >SCN</span
                        >
                        <DateTimePicker
                          :minDate="minDate"
                          :maxDate="maxDate"
                          title=""
                          :value.sync="selectedShippingInfoData['portOfDischargeETA']"
                          ref="PODETAPicker"
                          :errorMessage.sync="step3ErrMessage['portOfDischargeETA']"
                          @dateChanged="dateChanged('portOfDischargeETA')"
                          @click.native="adjustCalendarPosition('PODETAPicker')"
                        />
                      </div>
                      <div class="form-collision-number-columns"> </div>
                    </div>
                  </div>
                  <div class="form-step-5-group-3">
                    <p class="divisionLabelSmall">Final Destination</p>
                    <div class="form-content-row">
                      <div class="form-content-key-columns">Code</div>
                      <div class="form-masterfile-detected-columns">
                        <div
                          ><p :class="{ 'no-ai-value': !docAIData.selectedFinalDestination }">
                            {{ docAIData.selectedFinalDestination || 'No value detected' }}</p
                          ></div
                        >
                      </div>
                      <div class="form-masterfile-only-submitted-columns">
                        <InputQuery
                          placeholder=""
                          :items="shippingInfoData['finalDestination']"
                          itemKey="Code - Desc"
                          descKey="Port Location"
                          :selectedObject.sync="selectedShippingInfoData['selectedFinalDestination']"
                          validator="text,10"
                        />
                      </div>
                      <div class="form-collision-number-columns"> </div>
                    </div>
                    <div class="form-content-row">
                      <div class="form-content-key-columns">Final Dest. ETA</div>
                      <div class="form-masterfile-detected-columns">
                        <div
                          ><p :class="{ 'no-ai-value': !docAIData.fDestETA }">
                            {{ docAIData.fDestETA || 'No value detected' }}</p
                          ></div
                        >
                      </div>
                      <div class="form-masterfile-only-submitted-columns">
                        <DateTimePicker
                          :minDate="minDate"
                          :maxDate="maxDate"
                          title=""
                          :value.sync="selectedShippingInfoData['fDestETA']"
                          ref="FinalDestPicker"
                          :errorMessage.sync="step3ErrMessage['fDestETA']"
                          @click.native="adjustCalendarPosition('FinalDestPicker')"
                        />
                      </div>
                      <div class="form-collision-number-columns"> </div>
                    </div>
                  </div>
                  <div class="form-step-5-group-3">
                    <p class="divisionLabelSmall">Transhipment</p>
                    <div class="form-content-row">
                      <div class="form-content-key-columns">M. Vessel ID</div>
                      <div class="form-masterfile-detected-columns">
                        <div
                          ><p :class="{ 'no-ai-value': !docAIData.selectedMVesselID }">
                            {{ docAIData.selectedMVesselID || 'No value detected' }}</p
                          ></div
                        >
                      </div>
                      <div class="form-masterfile-only-submitted-columns">
                        <InputQuery
                          placeholder=""
                          :items="shippingInfoData['vesselIDList']"
                          itemKey="ID - Name"
                          descKey="Vessel Name"
                          :selectedObject.sync="selectedShippingInfoData['selectedMVesselID']"
                          validator="text,45"
                        />
                      </div>
                      <div class="form-collision-number-columns"> </div>
                    </div>
                    <div class="form-content-row">
                      <div class="form-content-key-columns">M. Voyage #</div>
                      <div class="form-masterfile-detected-columns">
                        <div
                          ><p :class="{ 'no-ai-value': !docAIData.mVoyNo }">
                            {{ docAIData.mVoyNo || 'No value detected' }}</p
                          ></div
                        >
                      </div>
                      <div class="form-masterfile-submitted-columns">
                        <Input
                          placeholder=""
                          :inputValue.sync="selectedShippingInfoData['mVoyNo']"
                          validator="text,10"
                        />
                      </div>
                      <div class="form-collision-number-columns"> </div>
                    </div>
                    <div class="form-content-row">
                      <div class="form-content-key-columns">Transhipment Port of Discharge</div>
                      <div class="form-masterfile-detected-columns">
                        <div
                          ><p :class="{ 'no-ai-value': !docAIData.selectedPortDischarge2 }">
                            {{ docAIData.selectedPortDischarge2 || 'No value detected' }}</p
                          ></div
                        >
                      </div>
                      <div class="form-masterfile-only-submitted-columns">
                        <InputQuery
                          placeholder=""
                          :items="shippingInfoData['portOfDischarge']"
                          itemKey="Code - Desc"
                          descKey="Port Location"
                          :selectedObject.sync="selectedShippingInfoData['selectedPortDischarge2']"
                          validator="text,10"
                        />
                      </div>
                      <div class="form-collision-number-columns"> </div>
                    </div>
                    <div class="form-content-row">
                      <div class="form-content-key-columns">ETA</div>
                      <div class="form-masterfile-detected-columns">
                        <div
                          ><p :class="{ 'no-ai-value': !docAIData.ETATime }">
                            {{ docAIData.ETATime || 'No value detected' }}</p
                          ></div
                        >
                      </div>
                      <div class="form-masterfile-only-submitted-columns">
                        <DateTimePicker
                          :openOnTop="true"
                          :minDate="minDate"
                          :maxDate="maxDate"
                          title=""
                          :value.sync="selectedShippingInfoData['ETATime']"
                          ref="ETATimePicker"
                          :errorMessage.sync="step3ErrMessage['ETATime']"
                          @click.native="adjustCalendarPosition('ETATimePicker')"
                        />
                      </div>
                      <div class="form-collision-number-columns"> </div>
                    </div>
                  </div>
                  <div v-if="pageType == 'Import'" class="form-step-5-group-3">
                    <p class="divisionLabelSmall">Free Storage / Demurrage / Detention</p>
                    <div class="form-content-row">
                      <div class="form-content-key-columns">Free Storage</div>
                      <div class="form-masterfile-detected-columns">
                        <div
                          ><p :class="{ 'no-ai-value': !docAIData.freeStorage }">
                            {{ docAIData.freeStorage || 'No value detected' }}</p
                          ></div
                        >
                      </div>
                      <div class="form-masterfile-submitted-columns">
                        <div class="free-input-group">
                          <Input
                            placeholder=""
                            :inputValue.sync="selectedShippingInfoData['freeStorage']"
                            validator="integer,3"
                          />
                          <div class="unit-toggle">
                            <button
                              :class="['toggle-btn', { active: selectedShippingInfoData['storageUnit'] === 'D' }]"
                              @click="setUnit('storageUnit', 'D')"
                              >Days</button
                            >
                            <button
                              :class="['toggle-btn', { active: selectedShippingInfoData['storageUnit'] === 'H' }]"
                              @click="setUnit('storageUnit', 'H')"
                              >Hours</button
                            >
                          </div>
                        </div>
                      </div>
                      <div class="form-collision-number-columns"> </div>
                    </div>
                    <div class="form-content-row">
                      <div class="form-content-key-columns">Free Demurrage</div>
                      <div class="form-masterfile-detected-columns">
                        <div
                          ><p :class="{ 'no-ai-value': !docAIData.freeDemurrage }">
                            {{ docAIData.freeDemurrage || 'No value detected' }}</p
                          ></div
                        >
                      </div>
                      <div class="form-masterfile-submitted-columns">
                        <div class="free-input-group">
                          <Input
                            placeholder=""
                            :inputValue.sync="selectedShippingInfoData['freeDemurrage']"
                            validator="integer,3"
                          />
                          <div class="unit-toggle">
                            <button
                              :class="['toggle-btn', { active: selectedShippingInfoData['demurrageUnit'] === 'D' }]"
                              @click="setUnit('demurrageUnit', 'D')"
                              >Days</button
                            >
                            <button
                              :class="['toggle-btn', { active: selectedShippingInfoData['demurrageUnit'] === 'H' }]"
                              @click="setUnit('demurrageUnit', 'H')"
                              >Hours</button
                            >
                          </div>
                        </div>
                      </div>
                      <div class="form-collision-number-columns"> </div>
                    </div>
                    <div class="form-content-row">
                      <div class="form-content-key-columns">Free Detention</div>
                      <div class="form-masterfile-detected-columns">
                        <div
                          ><p :class="{ 'no-ai-value': !docAIData.freeDetention }">
                            {{ docAIData.freeDetention || 'No value detected' }}</p
                          ></div
                        >
                      </div>
                      <div class="form-masterfile-submitted-columns">
                        <div class="free-input-group">
                          <Input
                            placeholder=""
                            :inputValue.sync="selectedShippingInfoData['freeDetention']"
                            validator="integer,3"
                          />
                          <div class="unit-toggle">
                            <button
                              :class="['toggle-btn', { active: selectedShippingInfoData['detentionUnit'] === 'D' }]"
                              @click="setUnit('detentionUnit', 'D')"
                              >Days</button
                            >
                            <button
                              :class="['toggle-btn', { active: selectedShippingInfoData['detentionUnit'] === 'H' }]"
                              @click="setUnit('detentionUnit', 'H')"
                              >Hours</button
                            >
                          </div>
                        </div>
                      </div>
                      <div class="form-collision-number-columns"> </div>
                    </div>
                  </div>
                  <br /><br /><br /><br />
                </div>
                <div class="button-group">
                  <button class="prevButton" type="button" @click="prevStep()">Prev</button>
                  <button class="jfCompNextButton" type="button" @click="nextStep()">Next</button>
                </div>
              </template>
              <template v-if="currentStep === 3">
                <!--Shipping Info 3-->
                <div class="table-wrapper">
                  <div class="form-step-4-group-1">
                    <p class="divisionLabel">Shipping Info (3/3)</p>
                    <div class="form-content-row custom-background">
                      <div class="form-content-key-columns"></div>

                      <div class="form-masterfile-detected-columns comparison-header-cell">
                        <span>{{ comparisonHeader.detectedLabel }}</span>
                        <Tooltip position="right" :minWidth="260" :tooltipText="comparisonHeader.detectedTooltip" />
                      </div>

                      <div class="form-masterfile-submitted-columns comparison-header-cell">
                        <span>{{ comparisonHeader.finalLabel }}</span>
                        <Tooltip position="left" :minWidth="280" :tooltipText="comparisonHeader.finalTooltip" />
                      </div>
                    </div>
                    <div class="form-content-row">
                      <div class="form-content-key-columns">Special Treatment</div>
                      <div class="form-masterfile-detected-columns">
                        <div
                          ><p :class="{ 'no-ai-value': !docAIData.selectedSpecialTreatment }">
                            {{ docAIData.selectedSpecialTreatment || 'No value detected' }}</p
                          ></div
                        >
                      </div>
                      <div class="form-masterfile-only-submitted-columns">
                        <InputQuery
                          placeholder=""
                          :items="shippingInfoData['specialTreatment']"
                          itemKey="Code - Desc"
                          descKey="ST Desc"
                          :selectedObject.sync="selectedShippingInfoData['selectedSpecialTreatment']"
                          validator="text,65"
                        />
                      </div>
                      <div class="form-collision-number-columns"> </div>
                    </div>
                    <div class="form-content-row">
                      <div class="form-content-key-columns">Station</div>
                      <div class="form-masterfile-detected-columns">
                        <div
                          ><p :class="{ 'no-ai-value': !docAIData.selectedStation }">
                            {{ docAIData.selectedStation || 'No value detected' }}</p
                          ></div
                        >
                      </div>
                      <div class="form-masterfile-only-submitted-columns">
                        <InputQuery
                          placeholder=""
                          :items="shippingInfoData['station']"
                          itemKey="Code - Name"
                          descKey="Station Name"
                          :selectedObject.sync="selectedShippingInfoData['selectedStation']"
                          validator="text,50"
                        />
                      </div>
                      <!-- <div class="form-collision-number-columns">
                  </div> -->
                    </div>
                    <div class="form-content-row">
                      <div class="form-content-key-columns">Port Operator</div>
                      <div class="form-masterfile-detected-columns">
                        <div
                          ><p :class="{ 'no-ai-value': !docAIData.selectedPortOperator }">
                            {{ docAIData.selectedPortOperator || 'No value detected' }}</p
                          ></div
                        >
                      </div>
                      <div class="form-masterfile-only-submitted-columns">
                        <InputQuery
                          placeholder=""
                          :items="shippingInfoData['portOperator']"
                          itemKey="Code - Name"
                          descKey="Operator Name"
                          :selectedObject.sync="selectedShippingInfoData['selectedPortOperator']"
                          validator="text,115"
                        />
                      </div>
                      <div class="form-collision-number-columns"> </div>
                    </div>
                    <!-- <div class="form-content-row">
                  <div class="form-content-key-columns">Final Destination</div>
                  <div class="form-masterfile-detected-columns" >
                    <div><p :class="{ 'no-ai-value': !docAIData.selectedFinalDestination }">
                      {{ docAIData.selectedFinalDestination || "No value detected" }}</p></div>
                  </div>
                  <div class="form-masterfile-submitted-columns" >
                    <InputQuery placeholder="" :items="shippingInfoData['finalDestination']" itemKey="Port Code" descKey="Port Location"
                    :selectedObject.sync="selectedShippingInfoData['selectedFinalDestination']" />
                  </div>
                  <div class="form-collision-number-columns">
                  </div>
                </div>
                <div class="form-content-row">
                  <div class="form-content-key-columns">Final Destination ETA</div>
                  <div class="form-masterfile-detected-columns" >
                    <div><p :class="{ 'no-ai-value': !docAIData.fDestETA }">
                      {{ docAIData.fDestETA || "No value detected" }}</p></div>
                  </div>
                  <div class="form-masterfile-submitted-columns" >
                    <DateTimePicker title="" :value.sync="selectedShippingInfoData['fDestETA']" />
                  </div>
                  <div class="form-collision-number-columns">
                  </div>
                </div> -->
                    <div class="form-content-row">
                      <div class="form-content-key-columns">Goods Description</div>
                      <div class="form-content-columns">
                        <div style="display: flex; align-items: center; position: relative">
                          <RemarksBox
                            title=""
                            :text.sync="selectedShippingInfoData['goodsDescription']"
                            style="height: 100px"
                            :freeText="true"
                          />
                          <Tooltip
                            style="position: absolute; top: 0; left: 100%; margin-top: 4%"
                            position="left"
                            :minWidth="300"
                            tooltipText="The text will be reformatted upon save to accommodate printing of official forms and EDI declaration."
                          />
                        </div>
                      </div>
                    </div>
                    <div class="form-content-row">
                      <div class="form-content-key-columns">Shipping Marks</div>
                      <div class="form-content-columns">
                        <div style="display: flex; align-items: center; position: relative">
                          <RemarksBox
                            title=""
                            :text.sync="selectedShippingInfoData['shippingMarks']"
                            style="height: 100px"
                            :freeText="true"
                          />
                          <Tooltip
                            style="position: absolute; top: 0; left: 100%; margin-top: 4%"
                            position="left"
                            :minWidth="300"
                            tooltipText="The text will be reformatted upon save to accommodate printing of official forms and EDI declaration."
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="button-group">
                  <button class="prevButton" type="button" @click="prevStep()">Prev</button>
                  <button class="jfCompNextButton" type="button" @click="nextStep()">Next</button>
                </div>
              </template>
              <template v-if="currentStep === 4">
                <!--General Info-->
                <div class="table-wrapper">
                  <div class="form-step-2-group-1">
                    <p class="divisionLabel">General Info</p>
                    <div class="form-content-row custom-background">
                      <div class="form-content-key-columns"></div>

                      <div class="form-masterfile-detected-columns comparison-header-cell">
                        <span>{{ comparisonHeader.detectedLabel }}</span>
                        <Tooltip position="right" :minWidth="260" :tooltipText="comparisonHeader.detectedTooltip" />
                      </div>

                      <div class="form-masterfile-submitted-columns comparison-header-cell">
                        <span>{{ comparisonHeader.finalLabel }}</span>
                        <Tooltip position="left" :minWidth="280" :tooltipText="comparisonHeader.finalTooltip" />
                      </div>
                    </div>

                    <!-- <div class="form-content-row">
                  <div class="form-content-key-columns">Customer Reference Number</div>
                  <div class="form-masterfile-detected-columns" >
                    <div><p :class="{ 'no-ai-value': !docAIData.custRefNo }">
                      {{ docAIData.custRefNo || "No value detected" }}</p></div>
                  </div>
                  <div class="form-masterfile-submitted-columns" >
                    <Input placeholder="" :inputValue.sync="selectedHeaderInfoData['custRefNo']" validator="text,40" />
                  </div>
                  <div class="form-collision-number-columns">
                  </div>
                </div> -->
                    <!-- <div class="form-content-row">
                  <div class="form-content-key-columns">Vessel Booking Reference Number</div>
                  <div class="form-content-columns">
                    <Input placeholder="" />
                  </div>
                </div> -->
                    <!-- <div class="form-content-row">
                  <div v-if="pageType != 'Import'" class="form-content-key-columns">Booking Ref #</div>
                  <div v-if="pageType == 'Import'" class="form-content-key-columns">B/L #</div>
                  <div class="form-masterfile-detected-columns" >
                    <div><p :class="{ 'no-ai-value': !docAIData.vbkgRefNo }">
                      {{ docAIData.vbkgRefNo || "No value detected" }}</p></div>
                  </div>
                  <div class="form-masterfile-submitted-columns" >
                    <Input placeholder="" :inputValue.sync="selectedHeaderInfoData['vbkgRefNo']"  validator="text,25"/>
                  </div>
                  <div class="form-collision-number-columns">
                  </div>
                </div> -->

                    <div class="form-content-row">
                      <div class="form-content-key-columns">Freight Type</div>
                      <div class="form-masterfile-detected-columns">
                        <div
                          ><p :class="{ 'no-ai-value': !docAIData.selectedFreightType }">
                            {{ docAIData.selectedFreightType || 'No value detected' }}</p
                          ></div
                        >
                      </div>
                      <div class="form-masterfile-long-submitted-columns">
                        <Dropdown
                          placeholder=""
                          :items="generalInfoData['freightTypeList']"
                          itemKey="Freight Type Desc"
                          :selectedObject.sync="selectedGeneralInfoData['selectedFreightType']"
                          validator="text,100"
                        />
                      </div>
                      <div class="form-collision-number-columns"> </div>
                    </div>
                  </div>
                  <div class="form-step-2-group-2">
                    <div class="form-content-row">
                      <div class="form-content-key-columns">Payment Country</div>
                      <div class="form-masterfile-detected-columns">
                        <div
                          ><p :class="{ 'no-ai-value': !docAIData.selectedCountry }">
                            {{ docAIData.selectedCountry || 'No value detected' }}</p
                          ></div
                        >
                      </div>
                      <div class="form-masterfile-only-submitted-columns">
                        <InputQuery
                          placeholder=""
                          :items="generalInfoData['countryList']"
                          itemKey="Country Code"
                          descKey="Country"
                          :selectedObject.sync="selectedGeneralInfoData['selectedCountry']"
                          validator="text,2"
                        />
                      </div>
                      <div class="form-collision-number-columns"> </div>
                    </div>
                    <div class="form-content-row">
                      <div class="form-content-key-columns">Currency</div>
                      <div class="form-masterfile-detected-columns">
                        <div
                          ><p :class="{ 'no-ai-value': !docAIData.selectedCurrency }">
                            {{ docAIData.selectedCurrency || 'No value detected' }}</p
                          ></div
                        >
                      </div>
                      <div class="form-masterfile-only-submitted-columns">
                        <InputQuery
                          placeholder=""
                          :items="generalInfoData['currencyList']"
                          itemKey="Currency Code"
                          descKey="Description"
                          :selectedObject.sync="selectedGeneralInfoData['selectedCurrency']"
                          validator="text,3"
                        />
                      </div>
                      <div class="form-collision-number-columns"> </div>
                    </div>
                    <div class="form-content-row">
                      <div class="form-content-key-columns">INCO Term</div>
                      <div class="form-masterfile-detected-columns">
                        <div
                          ><p :class="{ 'no-ai-value': !docAIData.selectedIncoTerms }">
                            {{ docAIData.selectedIncoTerms || 'No value detected' }}</p
                          ></div
                        >
                      </div>
                      <div class="form-masterfile-only-submitted-columns">
                        <InputQuery
                          placeholder=""
                          :items="generalInfoData['incoTermsList']"
                          itemKey="Incoterm"
                          descKey="Incoterm Desc"
                          :selectedObject.sync="selectedGeneralInfoData['selectedIncoTerms']"
                          validator="text,10"
                        />
                      </div>
                      <div class="form-collision-number-columns"> </div>
                    </div>
                    <div class="form-content-row">
                      <div class="form-content-key-columns">Insurance</div>
                      <div class="form-masterfile-detected-columns">
                        <div
                          ><p :class="{ 'no-ai-value': !docAIData.selectedInsurance }">
                            {{ docAIData.selectedInsurance || 'No value detected' }}</p
                          ></div
                        >
                      </div>
                      <div class="form-masterfile-long-submitted-columns">
                        <Dropdown
                          placeholder=""
                          :items="generalInfoData['insuranceList']"
                          itemKey="Insurance"
                          :selectedObject.sync="selectedGeneralInfoData['selectedInsurance']"
                          validator="text,5"
                        />
                      </div>
                      <div class="form-collision-number-columns"> </div>
                    </div>
                    <div class="form-content-row">
                      <div class="form-content-key-columns">House B/L</div>
                      <div class="form-masterfile-detected-columns">
                        <div
                          ><p :class="{ 'no-ai-value': !docAIData.houseBL }">
                            {{ docAIData.houseBL || 'No value detected' }}</p
                          ></div
                        >
                      </div>
                      <div class="form-masterfile-long-submitted-columns">
                        <Input
                          placeholder=""
                          :inputValue.sync="selectedGeneralInfoData['houseBL']"
                          validator="text,35"
                        />
                      </div>
                      <div class="form-collision-number-columns"> </div>
                    </div>
                    <div class="form-content-row">
                      <div class="form-content-key-columns">Remarks</div>
                      <div class="form-content-columns">
                        <RemarksBox
                          title=""
                          :text.sync="selectedGeneralInfoData['remarks']"
                          :errorMessage.sync="remarksError"
                          style="height: 100px"
                          :maxLength="200"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <br /><br /><br /><br />
                <div class="button-group">
                  <button class="prevButton" type="button" @click="prevStep()">Prev</button>
                  <button class="jfCompNextButton" type="button" @click="nextStep()">Next</button>
                </div>
              </template>
              <template v-if="currentStep === 5">
                <!--Cargo Info-->
                <div class="table-wrapper">
                  <div class="form-step-6-group-1">
                    <p class="divisionLabel">Cargo Info</p>
                    <div class="form-content-row custom-background">
                      <div class="form-content-key-columns"></div>

                      <div class="form-masterfile-detected-columns comparison-header-cell">
                        <span>{{ comparisonHeader.detectedLabel }}</span>
                        <Tooltip position="right" :minWidth="260" :tooltipText="comparisonHeader.detectedTooltip" />
                      </div>

                      <div class="form-masterfile-submitted-columns comparison-header-cell">
                        <span>{{ comparisonHeader.finalLabel }}</span>
                        <Tooltip position="left" :minWidth="280" :tooltipText="comparisonHeader.finalTooltip" />
                      </div>
                    </div>
                    <p class="divisionLabelSmall">Goods Measurement</p>
                    <div class="form-content-row">
                      <div class="form-content-key-columns">Gross Weight</div>
                      <div class="form-masterfile-detected-columns">
                        <div
                          ><p :class="{ 'no-ai-value': !docAIData.gWeight }">
                            {{ docAIData.gWeight || 'No value detected' }}</p
                          ></div
                        >
                      </div>
                      <div class="form-masterfile-submitted-columns">
                        <div style="display: flex; align-items: center">
                          <Input
                            placeholder=""
                            validator="positiveDecimal,5,4"
                            :inputValue.sync="selectedCargoInfoData['gWeight']"
                          />
                          KG
                        </div>
                      </div>
                      <div class="form-collision-number-columns"> </div>
                    </div>
                    <div class="form-content-row">
                      <div class="form-content-key-columns">Volume</div>
                      <div class="form-masterfile-detected-columns">
                        <div
                          ><p :class="{ 'no-ai-value': !docAIData.volume }">
                            {{ docAIData.volume || 'No value detected' }}</p
                          ></div
                        >
                      </div>
                      <div class="form-masterfile-submitted-columns">
                        <div style="display: flex; align-items: center">
                          <Input
                            placeholder=""
                            validator="positiveDecimal,5,4"
                            :inputValue.sync="selectedCargoInfoData['volume']"
                          />
                          M<sup>3</sup>
                        </div>
                      </div>
                      <div class="form-collision-number-columns"> </div>
                    </div>
                    <div class="form-content-row">
                      <div class="form-content-key-columns">Chargeable</div>
                      <div class="form-masterfile-detected-columns">
                        <div
                          ><p :class="{ 'no-ai-value': !docAIData.chargeable }">
                            {{ docAIData.chargeable || 'No value detected' }}</p
                          ></div
                        >
                      </div>
                      <div class="form-masterfile-submitted-columns">
                        <div style="display: flex; align-items: center">
                          <Input
                            placeholder=""
                            :inputValue.sync="selectedCargoInfoData['chargeable']"
                            validator="positiveDecimal,5,4"
                          />
                          KG
                        </div>
                      </div>
                      <div class="form-collision-number-columns"> </div>
                    </div>
                  </div>
                  <p class="divisionLabelSmall">Package Details</p>
                  <div class="form-step-6-group-2">
                    <div class="form-content-row">
                      <div class="form-content-key-columns"><span class="compulsory">**</span>No. of Pkg</div>
                      <div class="form-masterfile-detected-columns">
                        <div
                          ><p :class="{ 'no-ai-value': !docAIData.packageCount }">
                            {{ docAIData.packageCount || 'No value detected' }}</p
                          ></div
                        >
                      </div>
                      <div class="form-masterfile-submitted-columns">
                        <Input
                          placeholder=""
                          :inputValue.sync="selectedCargoInfoData['packageCount']"
                          validator="positiveDecimal,8,2"
                          :errorMessage.sync="step6ErrMessage['packageCount']"
                          ref="packageCount"
                        />
                      </div>
                      <div class="form-collision-number-columns"> </div>
                    </div>
                    <div class="form-content-row">
                      <div class="form-content-key-columns"><span class="compulsory">**</span>Pkg Type</div>
                      <div class="form-masterfile-detected-columns">
                        <div
                          ><p :class="{ 'no-ai-value': !docAIData.selectedPackageType }">
                            {{ docAIData.selectedPackageType || 'No value detected' }}</p
                          ></div
                        >
                      </div>
                      <div class="form-masterfile-only-submitted-columns">
                        <InputQuery
                          placeholder=""
                          :items="cargoInfoData['pkgTypeList']"
                          itemKey="Code - Desc"
                          descKey="Description"
                          :selectedObject.sync="selectedCargoInfoData['selectedPackageType']"
                          :errorMessage.sync="step6ErrMessage['selectedPackageType']"
                          ref="selectedPackageType"
                          validator="text,65"
                        />
                      </div>
                      <div class="form-collision-number-columns"> </div>
                    </div>
                    <div class="form-content-row">
                      <div class="form-content-key-columns"><span class="compulsory">**</span>Packing Material</div>
                      <div class="form-masterfile-detected-columns">
                        <div
                          ><p :class="{ 'no-ai-value': !docAIData.selectedPMaterial }">
                            {{ docAIData.selectedPMaterial || 'No value detected' }}</p
                          ></div
                        >
                      </div>
                      <div class="form-masterfile-only-submitted-columns">
                        <InputQuery
                          placeholder=""
                          :items="cargoInfoData['pMaterialList']"
                          itemKey="Code - Desc"
                          descKey="Material Description"
                          :selectedObject.sync="selectedCargoInfoData['selectedPMaterial']"
                          :errorMessage.sync="step6ErrMessage['selectedPMaterial']"
                          ref="selectedPMaterial"
                          validator="text,58"
                        />
                      </div>
                      <div class="form-collision-number-columns"> </div>
                    </div>
                    <div class="form-content-row">
                      <div class="form-content-key-columns">Commodity</div>
                      <div class="form-masterfile-detected-columns">
                        <div
                          ><p :class="{ 'no-ai-value': !docAIData.selectedCommodity }">
                            {{ docAIData.selectedCommodity || 'No value detected' }}</p
                          ></div
                        >
                      </div>
                      <div class="form-masterfile-only-submitted-columns">
                        <InputQuery
                          placeholder=""
                          :items="cargoInfoData['commodityList']"
                          itemKey="Commodity Code"
                          descKey="Description"
                          :selectedObject.sync="selectedCargoInfoData['selectedCommodity']"
                          validator="text,10"
                        />
                      </div>
                    </div>
                  </div>
                  <div class="form-step-6-group-3">
                    <Card :isDivisionProp="true" :defaultOpen="true">
                      <template #title>
                        <span>Sub Package Details</span>
                      </template>
                      <template #default>
                        <div class="checkoutTable">
                          <div class="tableBody" v-for="(subPackage, index) in subPackageList" :key="subPackage.id">
                            <div class="tableHeaderColumn">
                              <label>Package {{ index + 1 }}</label>
                              <div class="delete-button-container">
                                <button @click="deleteSubPackage(index)" class="delete-package-btn">Delete</button>
                              </div>
                            </div>
                            <div>
                              <div
                                class="tableItems"
                                style="display: flex; align-items: center; width: 100%; margin-top: 5px"
                              >
                                <Input
                                  placeholder="Pkg Qty"
                                  validator="integer,8"
                                  style="margin-left: 5px"
                                  :inputValue.sync="subPackageList[index]['pkgQty']"
                                  :isRequired="true"
                                  @focusChanged="calculateAllInput(index)"
                                  :ref="`pkgQtyRef${index}`"
                                  :errorMessage.sync="errMessage[`pkgQtyRef${index}`]"
                                />

                                <InputQuery
                                  placeholder="Pkg Type"
                                  style="margin-left: 5px"
                                  :items="cargoInfoData['pkgTypeList']"
                                  itemKey="Code - Desc"
                                  descKey="Description"
                                  :selectedObject="
                                    cargoInfoData['pkgTypeList'].find(
                                      (x) => x['Package Code'].trim() === subPackageList[index]['pkgType'],
                                    )
                                  "
                                  @selected="
                                    onTableDivInputReceived($event, 'subPackageList', index, 'pkgType', 'Package Code')
                                  "
                                  :isRequired="true"
                                  :ref="`pkgTypeRef${index}`"
                                  :errorMessage.sync="errMessage[`pkgTypeRef${index}`]"
                                  validator="text,65"
                                />
                              </div>

                              <div class="tableItems">
                                <strong>Dimensions: </strong>
                                <div class="content-details">
                                  <div class="dimension-input">
                                    <Input
                                      placeholder="Length"
                                      :inputValue.sync="subPackageList[index]['dimensions'].pkgLength"
                                      validator="positiveDecimal,5,4"
                                      @focusChanged="calculateAllInput(index)"
                                    />
                                    <div>X</div>
                                    <Input
                                      placeholder="Width"
                                      :inputValue.sync="subPackageList[index]['dimensions'].pkgWidth"
                                      validator="positiveDecimal,5,4"
                                      @focusChanged="calculateAllInput(index)"
                                    />
                                    <div>X</div>
                                    <Input
                                      placeholder="Height"
                                      :inputValue.sync="subPackageList[index]['dimensions'].pkgHeight"
                                      validator="positiveDecimal,5,4"
                                      @focusChanged="calculateAllInput(index)"
                                    />
                                  </div>
                                </div>
                              </div>
                              <!-- <div class="tableItems" style="display: flex; align-items: center; width: 100%">
                              <Input placeholder="" style="margin-left: 5px" :inputValue.sync="subPackageList[index]['dimensionsFull']" :isDisabled="true"/>
                            
                              <Input placeholder="Measurement (M3)"  style="margin-left: 5px"  :inputValue.sync="subPackageList[index]['measurement']" :isDisabled="true"/>
                            </div> -->
                              <div class="tableItems" style="display: flex; align-items: center; width: 100%">
                                <Input
                                  placeholder="Total Gross (KG)"
                                  style="margin-left: 5px"
                                  :inputValue.sync="subPackageList[index]['grossWeight']"
                                  @focusChanged="calculateAllInput(index)"
                                  :isRequired="true"
                                  validator="positiveDecimal,5,4"
                                  :ref="`grossWeightRef${index}`"
                                  :errorMessage.sync="errMessage[`grossWeightRef${index}`]"
                                />

                                <Input
                                  placeholder="Per Pkg (KG)"
                                  style="margin-left: 5px"
                                  :isDisabled="true"
                                  :inputValue.sync="subPackageList[index]['grossPerPkg']"
                                />
                              </div>
                              <div class="tableItems" style="display: flex; align-items: center; width: 100%">
                                <Input
                                  placeholder="Total Net (KG)"
                                  style="margin-left: 5px"
                                  :inputValue.sync="subPackageList[index]['netWeight']"
                                  @focusChanged="calculateAllInput(index)"
                                  validator="positiveDecimal,5,4"
                                  :ref="`netWeightRef${index}`"
                                  :errorMessage.sync="errMessage[`netWeightRef${index}`]"
                                />

                                <Input
                                  placeholder="Per Pkg (KG)"
                                  style="margin-left: 5px"
                                  :isDisabled="true"
                                  :inputValue.sync="subPackageList[index]['netPerPkg']"
                                />
                              </div>
                              <div class="tableItems" style="display: flex; align-items: center; width: 100%">
                                <Input
                                  placeholder="Total Vol (M³)"
                                  style="margin-left: 5px"
                                  validator="positiveDecimal,5,4"
                                  :inputValue.sync="subPackageList[index]['totalVol']"
                                  :isDisabled="true"
                                />
                                <Input
                                  placeholder="Per Pkg (M³)"
                                  style="margin-left: 5px"
                                  :isDisabled="true"
                                  :inputValue.sync="subPackageList[index]['volPerPkg']"
                                />
                              </div>
                              <div class="tableItems" style="display: flex; align-items: center; width: 100%">
                                <!-- <Input placeholder="Item Code" style="margin-left: 5px" :inputValue.sync="subPackageList[index]['itemCode']"  validator="text,25"/> -->
                                <InputQuery
                                  placeholder="Item Code"
                                  style="margin-left: 5px"
                                  :items="cargoInfoData['itemCode']"
                                  itemKey="Product Code"
                                  descKey="Description"
                                  :selectedObject="
                                    cargoInfoData['itemCode'].find(
                                      (x) => x['Product Code'].trim() === subPackageList[index]['itemCode'],
                                    )
                                  "
                                  @selected="
                                    onTableDivInputReceived($event, 'subPackageList', index, 'itemCode', 'Product Code')
                                  "
                                  validator="text,50"
                                />

                                <Input
                                  placeholder="Packing List #"
                                  style="margin-left: 5px"
                                  :inputValue.sync="subPackageList[index]['packingListNumber']"
                                  validator="text,25"
                                />
                              </div>
                              <div class="tableItems" style="display: flex; align-items: center; width: 100%">
                                <Input
                                  placeholder="Invoice #"
                                  style="margin-left: 5px"
                                  :inputValue.sync="subPackageList[index]['invoiceNumber']"
                                  validator="text,25"
                                />

                                <Input
                                  placeholder="Container #"
                                  style="margin-left: 5px"
                                  :inputValue.sync="subPackageList[index]['containerNumber']"
                                  validator="text,25"
                                />
                              </div>

                              <div class="tableItems" style="display: flex; align-items: center; width: 100%">
                                <RemarksBox
                                  title="Remarks"
                                  style="height: 100px"
                                  :maxLength="200"
                                  :text.sync="subPackageList[index]['pkgRemarks']"
                                  validator="text,200"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                        <div class="add-button-container">
                          <button @click="addSubPackage" class="add-package-btn">Add Package</button>
                          <button
                            @click="deleteSubPackageAll"
                            :disabled="subPackageList.length === 0"
                            class="deleteall-package-btn"
                            >Delete All</button
                          >
                        </div>
                      </template>
                    </Card>
                  </div>
                  <div class="form-step-6-group-4">
                    <Card :isDivisionProp="true" :defaultOpen="true">
                      <template #title>
                        <span>Container Details</span>
                      </template>
                      <template #default>
                        <div class="container-batch-panel" v-if="containerList.length > 0">
                          <label class="container-select-all">
                            <input
                              type="checkbox"
                              class="big-checkbox"
                              v-model="containerSelectAll"
                              @change="toggleContainerSelectAll"
                            />
                            Select All |
                          </label>

                          <span>{{ selectedContainerCount }} selected</span>

                          <Dropdown
                            placeholder="Select field"
                            :items="containerBatchFieldOptions"
                            itemKey="label"
                            :selectedObject.sync="selectedContainerBatchFieldObj"
                            style="width: 180px"
                          />

                          <Input
                            v-if="containerBatchField && containerBatchFieldConfig[containerBatchField].type === 'text'"
                            :inputValue.sync="containerBatchValue"
                            :placeholder="'Enter ' + containerBatchFieldConfig[containerBatchField].label"
                            validator="text,50"
                            style="width: 180px"
                          />

                          <Input
                            v-if="
                              containerBatchField && containerBatchFieldConfig[containerBatchField].type === 'number'
                            "
                            :inputValue.sync="containerBatchValue"
                            :placeholder="'Enter ' + containerBatchFieldConfig[containerBatchField].label"
                            validator="positiveDecimal,5,4"
                            style="width: 180px"
                          />

                          <Dropdown
                            v-if="
                              containerBatchField && containerBatchFieldConfig[containerBatchField].type === 'dropdown'
                            "
                            :items="cargoInfoData[containerBatchFieldConfig[containerBatchField].source]"
                            :itemKey="containerBatchFieldConfig[containerBatchField].itemKey"
                            :placeholder="'Select ' + containerBatchFieldConfig[containerBatchField].label"
                            :selectedObject="getContainerBatchSelectedObject()"
                            @selected="containerBatchValue = $event"
                            style="width: 180px"
                          />

                          <InputQuery
                            v-if="
                              containerBatchField &&
                              containerBatchFieldConfig[containerBatchField].type === 'inputQuery'
                            "
                            :items="cargoInfoData[containerBatchFieldConfig[containerBatchField].source]"
                            :itemKey="containerBatchFieldConfig[containerBatchField].itemKey"
                            :descKey="containerBatchFieldConfig[containerBatchField].descKey"
                            :selectedObject.sync="containerBatchValue"
                            :placeholder="'Select ' + containerBatchFieldConfig[containerBatchField].label"
                            style="width: 180px"
                          />

                          <button
                            @click="applyContainerBatchEdit"
                            :disabled="selectedContainerCount === 0 || !containerBatchField"
                          >
                            Apply
                          </button>
                          <button @click="deleteSelectedContainers" :disabled="selectedContainerCount === 0"
                            >Delete Selected</button
                          >
                          <button @click="clearSelectedContainers" :disabled="selectedContainerCount === 0"
                            >Clear</button
                          >
                        </div>
                        <div class="checkoutTable">
                          <div class="tableBody2" v-for="(container, index) in containerList" :key="container.id">
                            <div class="tableHeaderColumn">
                              <label>
                                <input
                                  type="checkbox"
                                  class="container-row-checkbox big-checkbox"
                                  v-model="containerList[index].selected"
                                  @change="syncContainerSelectAll"
                                />
                                Container {{ index + 1 }}
                              </label>
                              <div class="delete-button-container">
                                <button @click="deleteContainer(index)" class="delete-package-btn">Delete</button>
                              </div>
                            </div>
                            <div>
                              <div
                                class="tableItems"
                                style="display: flex; align-items: center; width: 100%; margin-top: 5px"
                              >
                                <Input
                                  placeholder="Container #"
                                  style="margin-left: 5px"
                                  :inputValue.sync="containerList[index]['contNo']"
                                  :isRequired="pageType === 'Import'"
                                  validator="text,20"
                                  :ref="`contNoRef${index}`"
                                  :errorMessage.sync="errMessage[`contNoRef${index}`]"
                                />
                                <Input
                                  placeholder="Seal #"
                                  style="margin-left: 5px"
                                  :inputValue.sync="containerList[index]['sealNo']"
                                  validator="text,20"
                                />
                              </div>
                              <div class="tableItems" style="display: flex; align-items: center; width: 100%">
                                <Dropdown
                                  placeholder="Size"
                                  style="margin-left: 5px"
                                  :items="cargoInfoData['contSizeList']"
                                  itemKey="Size"
                                  :selectedObject="
                                    cargoInfoData['contSizeList'].find(
                                      (x) => x['Size'] === containerList[index]['contSize'],
                                    )
                                  "
                                  @selected="
                                    onTableDivInputReceived($event, 'containerList', index, 'contSize', 'Size')
                                  "
                                  :isRequired="true"
                                  :ref="`contSizeRef${index}`"
                                  :errorMessage.sync="errMessage[`contSizeRef${index}`]"
                                  validator="text,2"
                                />

                                <Dropdown
                                  placeholder="Type"
                                  style="margin-left: 5px"
                                  :items="cargoInfoData['contTypeList']"
                                  itemKey="Code - Desc"
                                  :selectedObject="
                                    cargoInfoData['contTypeList'].find(
                                      (x) => x['Cont Type'].trim() === containerList[index]['contType'],
                                    )
                                  "
                                  @selected="
                                    onTableDivInputReceived($event, 'containerList', index, 'contType', 'Cont Type')
                                  "
                                  :isRequired="true"
                                  :ref="`contTypeRef${index}`"
                                  :errorMessage.sync="errMessage[`contTypeRef${index}`]"
                                  validator="text,60"
                                />
                              </div>
                              <div class="tableItems" style="display: flex; align-items: center; width: 100%">
                                <Dropdown
                                  placeholder="Status"
                                  style="margin-left: 5px"
                                  :items="cargoInfoData['contStatusList']"
                                  itemKey="Cont Status"
                                  :selectedObject="
                                    cargoInfoData['contStatusList'].find(
                                      (x) => x['Cont Status'].trim() === containerList[index]['status'],
                                    )
                                  "
                                  @selected="
                                    onTableDivInputReceived($event, 'containerList', index, 'status', 'Cont Status')
                                  "
                                  validator="text,10"
                                />

                                <Input
                                  placeholder="Weight (KG)"
                                  style="margin-left: 5px"
                                  :inputValue.sync="containerList[index]['contWeight']"
                                  validator="positiveDecimal,5,4"
                                />
                              </div>
                              <div class="tableItems" style="display: flex; align-items: center; width: 100%">
                                <InputQuery
                                  placeholder="Handling Type"
                                  style="margin-left: 5px"
                                  :items="cargoInfoData['contHandlingTypeList']"
                                  itemKey="Handling Type"
                                  descKey="Description"
                                  :selectedObject="
                                    cargoInfoData['contHandlingTypeList'].find(
                                      (x) => x['Handling Type'].trim() === containerList[index]['handlingType'],
                                    )
                                  "
                                  @selected="
                                    onTableDivInputReceived(
                                      $event,
                                      'containerList',
                                      index,
                                      'handlingType',
                                      'Handling Type',
                                    )
                                  "
                                  :isRequired="true"
                                  :ref="`handlingTypeRef${index}`"
                                  :errorMessage.sync="errMessage[`handlingTypeRef${index}`]"
                                  validator="text,5"
                                />

                                <InputQuery
                                  placeholder="Delivery Type"
                                  style="margin-left: 5px"
                                  :items="cargoInfoData['contDeliveryTypeList']"
                                  itemKey="Delivery Type"
                                  descKey="Description"
                                  :selectedObject="
                                    cargoInfoData['contDeliveryTypeList'].find(
                                      (x) => x['Delivery Type'].trim() === containerList[index]['deliveryType'],
                                    )
                                  "
                                  @selected="
                                    onTableDivInputReceived(
                                      $event,
                                      'containerList',
                                      index,
                                      'deliveryType',
                                      'Delivery Type',
                                    )
                                  "
                                  :isRequired="true"
                                  :ref="`deliveryTypeRef${index}`"
                                  :errorMessage.sync="errMessage[`deliveryTypeRef${index}`]"
                                  validator="text,5"
                                />
                              </div>

                              <div class="tableItems" style="display: flex; align-items: center; width: 100%">
                                <InputQuery
                                  placeholder="Send To"
                                  :items="cargoInfoData['contSendToList']"
                                  itemKey="Destination Code"
                                  descKey="Company"
                                  @selected="
                                    onTableDivInputReceived(
                                      $event,
                                      'containerList',
                                      index,
                                      'sendTo',
                                      'Destination Code',
                                    )
                                  "
                                  :selectedObject="
                                    cargoInfoData['contSendToList'].find(
                                      (x) => x['Destination Code'].trim() === containerList[index]['sendTo'],
                                    )
                                  "
                                  validator="text,10"
                                />

                                <Dropdown
                                  :items="cargoInfoData['soccocTypeList']"
                                  itemKey="SOCCOC Type"
                                  placeholder="SOC/COC"
                                  :isRequired="true"
                                  :selectedObject="
                                    cargoInfoData['soccocTypeList'].find(
                                      (x) => x['SOCCOC Type'] === containerList[index]['contSocCoc'],
                                    )
                                  "
                                  @selected="
                                    onTableDivInputReceived($event, 'containerList', index, 'contSocCoc', 'SOCCOC Type')
                                  "
                                  :ref="`contSocCocRef${index}`"
                                  :errorMessage.sync="errMessage[`contSocCocRef${index}`]"
                                  validator="text,30"
                                />
                              </div>

                              <div class="tableItems" style="display: flex; align-items: center; width: 100%">
                                <RemarksBox
                                  title="Remarks"
                                  style="height: 100px"
                                  key=""
                                  :maxLength="200"
                                  :text.sync="containerList[index]['remarks']"
                                  validator="text,200"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                        <div class="add-button-container">
                          <button @click="addContainer" class="add-package-btn">Add Container</button>
                          <button
                            @click="deleteContainerAll"
                            :disabled="containerList.length === 0"
                            class="deleteall-package-btn"
                            >Delete All</button
                          >
                        </div>
                      </template>
                    </Card>
                    <br /><br /><br />
                  </div>
                </div>
                <div class="button-group">
                  <button class="prevButton" type="button" @click="prevStep()">Prev</button>
                  <button class="submitButton" type="button" @click="submitCheck()">Submit</button>
                </div>
              </template>
            </div>
          </transition>
        </div>
      </div>
    </div>
    <div class="edit-address-popup" v-if="edittingDetails">
      <div class="edit-content">
        <div class="title">Edit {{ detailsEditTitle }} Details</div>
        <Input placeholder="Name" :inputValue.sync="detailsEditObject['name']" validator="text,100" />
        <AddressFormV2
          :address.sync="detailsEditObject"
          :postcodes="addressDetails.postcodeList"
          :states="addressDetails.stateList"
          :countries="addressDetails.countryList"
        />
        <Input placeholder="Attn To" :inputValue.sync="detailsEditObject['attn']" validator="text,100" />
        <div style="display: flex">
          <Input
            placeholder="Tel #"
            style="padding-right: 5px"
            :inputValue.sync="detailsEditObject['tel']"
            validator="text,20"
          />
          <Input
            placeholder="Fax #"
            style="padding-left: 5px"
            :inputValue.sync="detailsEditObject['fax']"
            validator="text,20"
          />
        </div>
        <Input placeholder="GST License #" :inputValue.sync="detailsEditObject['gst']" validator="text,50" />
        <div class="button-section">
          <button @click="detailsEditSave('save')" style="background-color: green; color: white">Update</button>
          <button @click="detailsEditSave('cancel')" style="background-color: red; color: white">Cancel</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */

import StepProgressBar from '@/components/formVerification/StepProgressBar.vue';
import InputDiv from '@/components/formVerification/InputDiv.vue';
import Card from '@/components/Card.vue';
//import DateTimePicker from '@/components/DateTimePicker.vue';
import RemarksBox from '@/components/RemarksBox.vue';
import DocAIFileUploadField from '@/components/formVerification/DocAIFileUploadField.vue';
import { doubleMetaphone } from 'double-metaphone';
import Fuse from 'fuse.js';
import { PDFDocument } from 'pdf-lib';

export default {
  components: {
    StepProgressBar,
    InputDiv,
    Card,
    //DateTimePicker,
    RemarksBox,
    DocAIFileUploadField,
  },
  props: {
    headerInfoData: {
      transMode: {
        type: Array,
        default: function () {
          return [];
        },
      },
      cargoType: {
        type: Array,
        default: function () {
          return [];
        },
      },
      smkFormType: {
        type: Array,
        default: function () {
          return [];
        },
      },
      shipper: {
        type: Array,
        default: function () {
          return [];
        },
      },
      consignee: {
        type: Array,
        default: function () {
          return [];
        },
      },
      notifyParty: {
        type: Array,
        default: function () {
          return [];
        },
      },

      billTo: {
        type: Array,
        default: function () {
          return [];
        },
      },
      services: {
        type: Array,
        default: function () {
          return [];
        },
      },
    },
    generalInfoData: {
      freightTypeList: {
        type: Array,
        default: function () {
          return [];
        },
      },
      countryList: {
        type: Array,
        default: function () {
          return [];
        },
      },
      currencyList: {
        type: Array,
        default: function () {
          return [];
        },
      },
      incoTermsList: {
        type: Array,
        default: function () {
          return [];
        },
      },
      insuranceList: {
        type: Array,
        default: function () {
          return [];
        },
      },
      branchList: {
        type: Array,
        default: function () {
          return [];
        },
      },
      servicePICList: {
        type: Array,
        default: function () {
          return [];
        },
      },
      serviceTeamList: {
        type: Array,
        default: function () {
          return [];
        },
      },
      salesPICList: {
        type: Array,
        default: function () {
          return [];
        },
      },
      salesTeamList: {
        type: Array,
        default: function () {
          return [];
        },
      },
    },
    shippingInfoData: {
      shipCallNoList: {
        type: Array,
        default: function () {
          return [];
        },
      },
      vesselIDList: {
        type: Array,
        default: function () {
          return [];
        },
      },
      shippingAgentList: {
        type: Array,
        default: function () {
          return [];
        },
      },
      shippingLineList: {
        type: Array,
        default: function () {
          return [];
        },
      },
      frtForwarderList: {
        type: Array,
        default: function () {
          return [];
        },
      },
      specialTreatment: {
        type: Array,
        default: function () {
          return [];
        },
      },
      station: {
        type: Array,
        default: function () {
          return [];
        },
      },
      portOperator: {
        type: Array,
        default: function () {
          return [];
        },
      },

      portOfLoading: {
        type: Array,
        default: function () {
          return [];
        },
      },
      portOfDischarge: {
        type: Array,
        default: function () {
          return [];
        },
      },
      finalDestination: {
        type: Array,
        default: function () {
          return [];
        },
      },

      fumigatorList: {
        type: Array,
        default: function () {
          return [];
        },
      },
      fumigatorLocationList: {
        type: Array,
        default: function () {
          return [];
        },
      },
    },
    cargoInfoData: {
      commodityList: {
        type: Array,
        default: function () {
          return [];
        },
      },
      pMaterialList: {
        type: Array,
        default: function () {
          return [];
        },
      },
      pkgTypeList: {
        type: Array,
        default: function () {
          return [];
        },
      },
      itemCode: {
        type: Array,
        default: function () {
          return [];
        },
      },
    },
    documentInfoData: {
      updatePopup: { type: Boolean, default: false },
      statusList: {
        type: Array,
        default: function () {
          return [];
        },
      },
      docTypeList: {
        type: Array,
        default: function () {
          return [];
        },
      },
      docStatusList: {
        type: Array,
        default: function () {
          return [];
        },
      },
    },
  },
  data() {
    return {
      shipperAddress: {
        add1: '',
        add2: '',
        add3: '',
        add4: '',
      },
      consigneeAddress: null,
      notifyPartyAddress: null,
      detailsEditTitle: '',
      detailsEditObject: null,
      edittingDetails: false,

      addressDetails: {
        countryList: [],
        postcodeList: [],
        stateList: [],
      },

      showConsigneeAddress: true,
      showShipperAddress: true,
      showNPartyAddress: true,
      defaults: null,
      pageType: '',
      fileName: '',
      minDate: new Date('1990-01-01'),
      maxDate: new Date('2079-01-01'),
      matchesList: {
        tableStyling: {},
        selectedShipper: false,
        selectedConsignee: false,
        selectedNotifyParty: false,
        selectedBillTo: false,
        selectedVesselID: false,
        selectedShipCall: false,
        selectedPortLoading: false,
        selectedPortDischarge: false,
        selectedShippingLine: false,
        selectedAgentList: false,
        selectedFrtForwarder: false,
      },
      loading: false,
      loadText: '',
      pageType: '',
      file: '',
      embedSrc: '',
      CoRefname: '',
      docAIData: {
        selectedShipper: null,
        selectedConsignee: null,
        selectedNotifyParty: null,
        selectedBillTo: null,
        selectedVesselID: null,
        selectedVesselName: null,
        selectedShipCall: null,
        selectedPortLoading: null,
        selectedPortDischarge: null,
        selectedShippingLine: null,
        selectedAgentList: null,
        custRefNo: null,
        gWeight: null,
        volume: null,
        oriBL: null,
        selectedFrtForwarder: null,
        selectedCargoType: null,
        selectedFreightType: null,
        selectedCountry: null,
        selectedCurrency: null,
        selectedFinalDestination: null,
        selectedSpecialTreatment: null,
        selectedStation: null,
        selectedPortOperator: null,
        selectedMVesselID: null,
        selectedPortDischarge2: null,
        selectedPackageType: null,
        selectedPMaterial: null,
        selectedCommodity: null,
        selectedIncoTerms: null,
        selectedInsurance: null,
        voyNo: null,
        custRefNo: null,
        vbkgRefNo: null,
        houseBL: null,
        fDestETA: null,
        polETA: null,
        polETD: null,
        mVoyNo: null,
        ETATime: null,
        gWeight: null,
        volume: null,
        chargeable: null,
        packageCount: null,
        freeStorage: null,
        freeDemurrage: null,
        freeDetention: null,
      },
      soundexEncoding: {
        selectedShipper: null,
        selectedConsignee: null,
        selectedNotifyParty: null,
        selectedBillTo: null,
        selectedShipCall: null,
        selectedPortLoading: null,
        selectedPortDischarge: null,
      },
      dMetaphoneEncoding: {
        selectedShipper: null,
        selectedConsignee: null,
        selectedNotifyParty: null,
        selectedBillTo: null,
        selectedShipCall: null,
        selectedPortLoading: null,
        selectedPortDischarge: null,
      },
      collisionNumber: {
        selectedShipper: 0,
        selectedConsignee: 0,
        selectedNotifyParty: 0,
        selectedBillTo: 0,
        selectedShipCall: 0,
        selectedVesselID: 0,
        selectedPortLoading: 0,
        selectedPortDischarge: 0,
        selectedShippingLine: 0,
        selectedAgentList: 0,
        selectedFrtForwarder: 0,
      },
      filteredData: {
        shipper: [],
        selectedShipper: [{}],
        consignee: [],
        selectedConsignee: [{}],
        notifyParty: [],
        selectedNotifyParty: [{}],
        billTo: [],
        selectedBillTo: [{}],
        vesselID: [],
        selectedVesselID: [{}],
        portOfLoading: [],
        selectedPortLoading: [{}],
        portOfDischarge: [],
        selectedPortDischarge: [{}],
        shipCallNo: [],
        selectedShipCall: [{}],
        shippingLine: [],
        selectedShippingLine: [{}],
        agentList: [],
        selectedAgentList: [{}],
        frtForwarder: [],
      },
      filteredShippingInfoData: {
        selectedFinalDestination: [{}],
        selectedSpecialTreatment: [{}],
        selectedStation: [{}],
        selectedPortOperator: [{}],
        selectedMVesselID: [{}],
        selectedPortDischarge2: [{}],
      },
      filteredHeaderInfoData: {
        selectedCargoType: [{}],
      },
      filteredGeneralInfoData: {
        selectedFreightType: [{}],
        selectedCountry: [{}],
        selectedCurrency: [{}],
      },
      filteredCargoInfoData: {
        selectedPackageType: [{}],
        selectedPMaterial: [{}],
        selectedCommodity: [{}],
      },
      selectedHeaderInfoData: {
        detectedDocument: null,
        selectedTransMode: null,
        selectedCargoType: null,
        selectedSmkFormType: null,
        selectedShipper: null,
        selectedConsignee: null,
        selectedNotifyParty: null,

        projectNo: null,
        custRefNo: null,
        vbkgRefNo: null,
        selectedBillTo: null,
        billToDetails: null,

        jobNo: null,
        jobTime: null,
        jobStatus: null,
      },
      step1ErrMessage: {
        selectedTransMode: null,
        selectedCargoType: null,
        selectedShipper: null,
        selectedConsignee: null,
        selectedBillTo: null,
      },
      step6ErrMessage: {
        selectedPMaterial: null,
        selectedPackageType: null,
        packageCount: null,
      },
      step2ErrMessage: {
        selectedVesselID: null,
        selectedAgentList: null,
        selectedShipCall: null,
        selectedPortLoading: null,
        selectedPortDischarge: null,
      },
      step3ErrMessage: {
        portOfDischargeETA: null,
        portOfLoadingETA: null,
        closingTime: null,
        ETATime: null,
        fDestETA: null,
        portOfLoadingETD: null,
      },
      selectedGeneralInfoData: {
        vendor: null,
        vesselBkgBy: null,
        oriBL: null,
        nonNegoBL: null,
        selectedFreightType: null,
        selectedCountry: null,
        selectedCurrency: null,
        selectedIncoTerms: null,
        selectedInsurance: null,
        houseBL: null,
        selectedBranch: null,

        selectedServicePIC: null,
        selectedServiceTeam: null,
        selectedSalesPIC: null,
        selectedSalesTeam: null,
        remarks: '',
      },
      remarksError: '',
      selectedShippingInfoData: {
        selectedShipCall: null,
        voyNo: null,
        selectedVesselID: null,
        selectedAgentList: null,
        selectedShippingLine: null,
        selectedFrtForwarder: null,
        selectedSpecialTreatment: null,
        selectedStation: null,
        selectedPortOperator: null,
        warehouse: null,
        selectedShipCall: null,
        selectedPortLoading: null,
        selectedPortDischarge: null,
        selectedFinalDestination: null,
        demurageTime: null,
        detention: null,
        isTranshipment: null,
        selectedMVesselID: null,
        mVoyNo: null,
        selectedPortDischarge2: null,
        ETATime: null,

        closingTime: null,
        portOfLoadingETD: null,
        portOfLoadingETA: null,
        portOfDischargeETA: null,
        fDestETA: null,
        isFumigation: null,
        selectedFumigator: null,
        selectedLocation: null,
        fumigateTime: null,
        ventilationTime: null,
        phytoMarking: null,

        goodsDescription: '',
        shippingMarks: '',

        freeStorage: '',
        storageUnit: 'D',
        freeDemurrage: '',
        demurrageUnit: 'D',
        freeDetention: '',
        detentionUnit: 'D',
      },
      selectedCargoInfoData: {
        gWeight: '',
        volume: '',
        chargeable: '',

        selectedCommodity: null,
        selectedPMaterial: null,
        packageCount: null,
        selectedPackageType: null,

        subPkgList: [],
        selectedSubPkgs: null,
        contDetailsList: [],
        selectedContDetails: null,
      },
      cargoPopupDetails: {
        cargoAddType: '',
      },
      selectedDocumentInfoData: {
        docData: [],
        selectedDoc: null,
      },
      shipperSoundexEncoding: [],
      consigneeSoundexEncoding: [],
      notifyPartySoundexEncoding: [],
      billToSoundexEncoding: [],
      polPortCodeSoundexEncoding: [],
      podPortCodeSoundexEncoding: [],
      shipperDMetaphoneEncoding: [],
      consigneeDMetaphoneEncoding: [],
      notifyPartyDMetaphoneEncoding: [],
      billToDMetaphoneEncoding: [],
      polPortCodeDMetaphoneEncoding: [],
      podPortCodeDMetaphoneEncoding: [],
      shipCallNoSoundexEncoding: [],
      shipCallNoDMetaphoneEncoding: [],
      steps: [
        'Header Info',
        'Shipping Info (1/3)',
        'Shipping Info (2/3)',
        'Shipping Info (3/3)',
        'General Info',
        'Cargo Info',
      ],
      currentStep: 0,
      subPackageList: [],
      containerList: [],
      errMessage: {
        // pkgQtyRefError: null,
        // pkgTypeRef: null,
        // grossWeightRef: null,
        // netWeightRef: null,
      },
      stepErrors: {},
      finalMissingFields: [],
      comparisonHeader: {
        detectedLabel: 'AI Extracted',
        finalLabel: 'Final Value',
        matchesLabel: 'Matches',
        detectedTooltip: 'Value extracted by Document AI from the uploaded document.',
        finalTooltip:
          'The final value that will be used and saved into DSM. You may change this value before proceeding.',
        matchesTooltip: 'Possible matching records found in DSM based on the value extracted by Document AI.',
      },
      shipCallChangedFields: {
        selectedVesselID: false,
        voyNo: false,
        portOfLoadingETA: false,
        portOfDischargeETA: false,
        closingTime: false,
      },

      containerSelectAll: false,
      containerBatchField: '',
      containerBatchValue: '',
      containerBatchFieldConfig: {
        contNo: { type: 'text', label: 'Container #' },
        sealNo: { type: 'text', label: 'Seal #' },
        contSize: { type: 'dropdown', label: 'Size', source: 'contSizeList', itemKey: 'Size', valueKey: 'Size' },
        contType: {
          type: 'dropdown',
          label: 'Type',
          source: 'contTypeList',
          itemKey: 'Code - Desc',
          valueKey: 'Cont Type',
        },
        status: {
          type: 'dropdown',
          label: 'Status',
          source: 'contStatusList',
          itemKey: 'Cont Status',
          valueKey: 'Cont Status',
        },
        contWeight: { type: 'number', label: 'Weight (KG)' },
        handlingType: {
          type: 'inputQuery',
          label: 'Handling Type',
          source: 'contHandlingTypeList',
          itemKey: 'Handling Type',
          descKey: 'Description',
          valueKey: 'Handling Type',
        },
        deliveryType: {
          type: 'inputQuery',
          label: 'Delivery Type',
          source: 'contDeliveryTypeList',
          itemKey: 'Delivery Type',
          descKey: 'Description',
          valueKey: 'Delivery Type',
        },
        sendTo: {
          type: 'inputQuery',
          label: 'Send To',
          source: 'contSendToList',
          itemKey: 'Destination Code',
          descKey: 'Company',
          valueKey: 'Destination Code',
        },
        contSocCoc: {
          type: 'dropdown',
          label: 'SOC/COC',
          source: 'soccocTypeList',
          itemKey: 'SOCCOC Type',
          valueKey: 'SOCCOC Type',
        },
        remarks: { type: 'text', label: 'Remarks' },
      },
      pendingDocAIFile: null,
      totalPages: 0,
      pageStartObj: null,
      pageEndObj: null,
      showPageSelector: false,
      pendingDocAIPreviewSrc: '',
    };
  },
  computed: {
    isMasterfileFieldsFilled() {
      if (this.currentStep === 0)
        if (this.pageType == 'Import')
          // if Import require "Consignee" only. if Export require "Shipper" only
          return (
            this.selectedHeaderInfoData['selectedConsignee'] !== null &&
            this.selectedHeaderInfoData['selectedBillTo'] !== null &&
            this.selectedHeaderInfoData['selectedTransMode'] !== null &&
            this.selectedHeaderInfoData['selectedCargoType'] !== null
          );
        else
          return (
            this.selectedHeaderInfoData['selectedShipper'] !== null &&
            this.selectedHeaderInfoData['selectedBillTo'] !== null &&
            this.selectedHeaderInfoData['selectedTransMode'] !== null &&
            this.selectedHeaderInfoData['selectedCargoType'] !== null
          );
      else if (this.currentStep === 1)
        return (
          this.selectedShippingInfoData['selectedVesselID'] !== null &&
          this.selectedShippingInfoData['selectedShipCall'] !== null &&
          this.selectedShippingInfoData['selectedPortLoading'] !== null &&
          this.selectedShippingInfoData['selectedPortDischarge'] !== null &&
          this.selectedShippingInfoData['selectedAgentList'] !== null &&
          this.selectedShippingInfoData['voyNo'] !== ''
        );
    },
    selectedContainerCount() {
      return this.containerList.filter((x) => x.selected).length;
    },
    containerBatchFieldOptions() {
      return Object.keys(this.containerBatchFieldConfig).map((key) => ({
        key,
        label: this.containerBatchFieldConfig[key].label,
      }));
    },
    selectedContainerBatchFieldObj: {
      get() {
        return this.containerBatchFieldOptions.find((x) => x.key === this.containerBatchField) || null;
      },
      set(value) {
        this.containerBatchField = value ? value.key : '';
        this.containerBatchValue = '';
      },
    },
    pageOptions() {
      return Array.from({ length: this.totalPages }, (_, i) => ({
        Page: String(i + 1),
      }));
    },

    selectedPageCount() {
      if (!this.pageStartObj || !this.pageEndObj) return 0;

      const start = parseInt(this.pageStartObj.Page);
      const end = parseInt(this.pageEndObj.Page);

      if (!start || !end || start > end) return 0;

      return end - start + 1;
    },
  },
  methods: {
    adjustCalendarPosition(refName) {
      this.$nextTick(() => {
        const calendar = this.$refs[refName].$el.querySelector('.calendar');

        if (calendar) {
          // Ensure the calendar is scrolled into view
          calendar.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      });
    },
    toUpperCaseTrim(value) {
      //normalize value
      return value
        ?.split('(')[0]
        .trim()
        .replace(/[^\w\s]|_/g, '')
        .toUpperCase();
    },
    searchList: function (list, field) {
      //prepare list for fuse matching
      return list
        .map((item) => {
          const preprocessedValue = this.toUpperCaseTrim(item[field]); // field/column to search
          return preprocessedValue ? { location: item, preprocessed: preprocessedValue } : null;
        })
        .filter((item) => item !== null); // remove null items
    },
    bestMatch: function (search, value) {
      if (!value) {
        //console.error("Value is undefined or empty");
        return [];
      }
      const isValid = search.every((item) => item && typeof item === 'object' && item.preprocessed);
      if (!isValid) {
        //console.log(search);
        //console.error("Invalid data detected in search array");
        return [];
      }
      let normalizedValue = this.toUpperCaseTrim(value);
      const options = {
        includeScore: true,
        keys: ['preprocessed'],
      };

      try {
        const fuse = new Fuse(search, options);
        // Use Fuse to search for the best match
        const results = fuse.search(normalizedValue);
        const filteredResults = results.filter((result) => result.score < 0.43); // threshold - 0.0 = must be same , 1 = match anything
        //console.log(normalizedValue + " |Result below")
        //console.log(results)
        //console.log(filteredResults)
        // Check if there are any results and get the best match
        if (filteredResults.length > 0) {
          return filteredResults.map((result) => result.item.location);
        } else {
          return [];
        }
      } catch (error) {
        //console.error("Error during Fuse search:", error);
        return [];
      }
    },
    async docAIFileSelected(file) {
      this.pendingDocAIFile = file;
      this.pendingDocAIPreviewSrc = URL.createObjectURL(file);
      this.totalPages = 0;
      this.pageStart = 1;
      this.pageEnd = 1;
      this.showPageSelector = false;

      this.loading = true;
      this.loadText = 'Reading PDF pages';

      try {
        const pdfBytes = await file.arrayBuffer();
        const pdfDoc = await PDFDocument.load(pdfBytes);

        this.totalPages = typeof pdfDoc.getPageCount === 'function' ? pdfDoc.getPageCount() : pdfDoc.getPages().length;

        this.pageStartObj = this.pageOptions[0] || null;
        this.pageEndObj = this.pageOptions[this.pageOptions.length - 1] || null;
        this.showPageSelector = true;
      } catch (error) {
        console.error(error);
        this.$buefy.toast.open({
          message: 'Unable to read PDF pages.',
          type: 'is-danger',
        });
      } finally {
        this.loading = false;
        this.loadText = '';
      }
    },

    async processSelectedPages() {
      if (!this.pendingDocAIFile) return;

      const startPage = this.pageStartObj ? parseInt(this.pageStartObj.Page) : null;
      const endPage = this.pageEndObj ? parseInt(this.pageEndObj.Page) : null;

      if (!startPage || !endPage || startPage > endPage) {
        this.$showAlert('error', 'Invalid Page Range', 'Please select a valid page range.');
        return;
      }

      if (endPage > this.totalPages) {
        this.$showAlert('error', 'Invalid Page Range', `This document only has ${this.totalPages} pages.`);
        return;
      }

      this.loading = true;
      this.loadText = 'Preparing selected pages';

      try {
        let fileToProcess = this.pendingDocAIFile;

        if (!(startPage === 1 && endPage === this.totalPages)) {
          const pdfBytes = await this.pendingDocAIFile.arrayBuffer();
          const srcDoc = await PDFDocument.load(pdfBytes);
          const newDoc = await PDFDocument.create();

          const pageIndexes = [];
          for (let i = startPage - 1; i <= endPage - 1; i++) {
            pageIndexes.push(i);
          }

          const copiedPages = await newDoc.copyPages(srcDoc, pageIndexes);
          copiedPages.forEach((page) => newDoc.addPage(page));

          const selectedPdfBytes = await newDoc.save();
          fileToProcess = new File([selectedPdfBytes], this.pendingDocAIFile.name, {
            type: 'application/pdf',
          });
        }

        this.showPageSelector = false;
        this.loading = false;
        this.loadText = '';

        this.$refs.docAIUpload.uploadFile(fileToProcess);
      } catch (error) {
        this.loading = false;
        this.loadText = '';
        this.$showAlert(
          'error',
          'Unable To Prepare PDF',
          error.message || 'Error occurred, please try another PDF file or contact dsm support.',
        );
      }
    },
    async onInputChild({ file, data, fileName }) {
      this.$fullscreenLoading('Processing Data');
      this.file = file;
      this.embedSrc = URL.createObjectURL(file);
      this.data = data.data;
      this.fileName = fileName;
      //console.log(data);
      this.$emit('processorVersion', this.data?.processorVersion || 'Unknown'); // emit processor version to JobPage.vue for display
      //this.containerList = this.data.containerDetails; //removed for testing

      //Retrieve master data scanned by Document AI
      this.docAIData['selectedShipperDisplay'] =
        this.data.data[0].keys.find((x) => x.key === 'selectedShipper').Value ?
          this.data.data[0].keys.find((x) => x.key === 'selectedShipper').Value
        : '';
      this.docAIData['selectedShipper'] =
        this.docAIData['selectedShipperDisplay'] ? this.docAIData['selectedShipperDisplay'].split(',')[0] : '';
      this.docAIData['selectedConsigneeDisplay'] =
        this.data.data[0].keys.find((x) => x.key === 'selectedConsignee').Value ?
          this.data.data[0].keys.find((x) => x.key === 'selectedConsignee').Value
        : '';
      this.docAIData['selectedConsignee'] =
        this.docAIData['selectedConsigneeDisplay'] ? this.docAIData['selectedConsigneeDisplay'].split(',')[0] : '';
      this.docAIData['selectedNotifyPartyDisplay'] =
        this.data.data[0].keys.find((x) => x.key === 'selectedNotifyParty').Value ?
          this.data.data[0].keys.find((x) => x.key === 'selectedNotifyParty').Value
        : '';
      this.docAIData['selectedNotifyParty'] =
        this.docAIData['selectedNotifyPartyDisplay'] ? this.docAIData['selectedNotifyPartyDisplay'].split(',')[0] : '';
      this.docAIData['selectedBillToDisplay'] =
        this.data.data[0].keys.find((x) => x.key === 'selectedBillTo').Value ?
          this.data.data[0].keys.find((x) => x.key === 'selectedBillTo').Value
        : '';
      this.docAIData['selectedBillTo'] =
        this.docAIData['selectedBillToDisplay'] ? this.docAIData['selectedBillToDisplay'].split(',')[0] : '';
      this.docAIData['selectedVesselID'] =
        this.data.data[2].keys.find((x) => x.key === 'selectedVesselID').Value ?
          this.data.data[2].keys.find((x) => x.key === 'selectedVesselID').Value
        : '';
      this.docAIData['selectedVesselName'] =
        this.data.data[2].keys.find((x) => x.key === 'selectedVesselName').Value ?
          this.data.data[2].keys.find((x) => x.key === 'selectedVesselName').Value
        : '';
      this.docAIData['selectedPortLoading'] =
        this.data.data[2].keys.find((x) => x.key === 'selectedPortLoading').Value ?
          this.data.data[2].keys.find((x) => x.key === 'selectedPortLoading').Value
        : '';
      this.docAIData['selectedShipCall'] =
        this.data.data[2].keys.find((x) => x.key === 'selectedShipCall').Value ?
          this.data.data[2].keys.find((x) => x.key === 'selectedShipCall').Value
        : '';
      this.docAIData['selectedPortDischarge'] =
        this.data.data[2].keys.find((x) => x.key === 'selectedPortDischarge').Value ?
          this.data.data[2].keys.find((x) => x.key === 'selectedPortDischarge').Value
        : '';
      this.docAIData['selectedShippingLine'] =
        this.data.data[2].keys.find((x) => x.key === 'selectedShippingLine').Value ?
          this.data.data[2].keys.find((x) => x.key === 'selectedShippingLine').Value
        : '';
      //this.docAIData['selectedAgentList'] = this.data.data[2].keys.find(x => x.key === "selectedAgentList").Value ? this.data.data[2].keys.find(x => x.key === "selectedAgentList").Value : '';
      this.docAIData['selectedAgentList'] =
        this.data.data[2].keys.find((x) => x.key === 'selectedAgentList').Value ||
        this.data.data[2].keys.find((x) => x.key === 'selectedShippingLine').Value ||
        ''; // if no value, look for detected shipping line
      this.docAIData['selectedFrtForwarder'] =
        this.data.data[2].keys.find((x) => x.key === 'selectedFrtForwarder').Value ?
          this.data.data[2].keys.find((x) => x.key === 'selectedFrtForwarder').Value
        : '';

      await this.masterDataProcessing();
      this.$fullscreenLoading('Getting defaults');
      this.defaults = await this.$apiCallV2(`/FreightForwarding/GetJobDefaults?jobType=${this.pageType[0]}`);
      //console.log(this.defaults);

      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'COUNTRY' }).then((result) => {
        this.generalInfoData['countryList'] = result;
        this.addressDetails['countryList'] = result;
      }),
        this.$apiCallV2('/Master/GetListMaster', { masterfile: 'POSTCODE' }).then((result) => {
          this.addressDetails['postcodeList'] = result;
        }),
        this.$apiCallV2('/Master/GetListMaster', { masterfile: 'STATE' }).then((result) => {
          this.addressDetails['stateList'] = result;
        }),
        this.$fullscreenLoading('Populating Master Data');
      this.populateMasterData();
      this.$fullscreenLoading('Populating Header Info Data');
      this.populateHeaderInfoData();
      this.$fullscreenLoading('Populating General Info Data');
      this.populateGeneralInfoData();
      this.$fullscreenLoading('Populating Shipping Info Data');
      this.populateShippingInfoData();
      this.$fullscreenLoading('Populating Cargo Info Data');
      this.populateCargoInfoData();

      // for display in "Detected" column
      this.docAIData['shipperAddress'] =
        this.data.data[0].keys.find((x) => x.key === 'shipperAddress').Value ?
          this.data.data[0].keys.find((x) => x.key === 'shipperAddress').Value
        : '';
      this.docAIData['consigneeAddress'] =
        this.data.data[0].keys.find((x) => x.key === 'consigneeAddress').Value ?
          this.data.data[0].keys.find((x) => x.key === 'consigneeAddress').Value
        : '';
      this.docAIData['nPartyAddress'] =
        this.data.data[0].keys.find((x) => x.key === 'nPartyAddress').Value ?
          this.data.data[0].keys.find((x) => x.key === 'nPartyAddress').Value
        : '';

      this.docAIData['voyNo'] =
        this.selectedShippingInfoData['voyNo'] ?
          this.selectedShippingInfoData['voyNo']
        : this.selectedShippingInfoData['mVoyNo'];
      this.docAIData['custRefNo'] = this.selectedHeaderInfoData['custRefNo'];
      this.docAIData['vbkgRefNo'] = this.selectedHeaderInfoData['vbkgRefNo'];
      this.docAIData['houseBL'] = this.selectedGeneralInfoData['houseBL'];
      this.docAIData['fDestETA'] = this.data.data[2].keys.find((x) => x.key === 'fDestETA')?.Value;
      this.docAIData['polETA'] = this.data.data[2].keys.find((x) => x.key === 'polETA')?.Value;
      this.docAIData['polETD'] = this.data.data[2].keys.find((x) => x.key === 'polETD')?.Value;
      this.docAIData['closingTime'] = this.data.data[2].keys.find((x) => x.key === 'closingTime')?.Value;
      this.docAIData['podETA'] = this.data.data[2].keys.find((x) => x.key === 'podETA')?.Value;
      this.docAIData['mVoyNo'] = this.selectedShippingInfoData['mVoyNo'];
      this.docAIData['ETATime'] = this.data.data[2].keys.find((x) => x.key === 'ETATime')?.Value;
      // this.docAIData['gWeight'] = this.selectedCargoInfoData['gWeight'];
      // this.docAIData['volume'] = this.selectedCargoInfoData['volume'];
      // this.docAIData['chargeable'] = this.selectedCargoInfoData['chargeable'];
      this.docAIData['packageCount'] =
        (this.data.data[3].keys.find((x) => x.key === 'packageCount')?.Value || '').split(' ')[0] || null;

      this.docAIData['freeStorage'] = this.data.data[2].keys.find((x) => x.key === 'freeStorage')?.Value;
      this.docAIData['freeDemurrage'] = this.data.data[2].keys.find((x) => x.key === 'freeDemurrage')?.Value;
      this.docAIData['freeDetention'] = this.data.data[2].keys.find((x) => x.key === 'freeDetention')?.Value;

      this.$fullscreenLoading();

      this.checkDocumentType(this.pageType);
    },
    checkDocumentType: async function (pageType) {
      const exportJobDocuments = [
        // Usual documnets used to Open Export Jobs
        'Booking Confirmation',
      ];
      const importJobDocuments = [
        // Usual documnets used to Open Import Jobs
        'Arrival Notice',
        'Bill of Lading',
      ];

      const normalizeDetectedDocument = this.selectedHeaderInfoData.detectedDocument.trim().toLowerCase();
      const normalizeExportDocuments = exportJobDocuments.map((doc) => doc.trim().toLowerCase());
      const normalizeImportDocuments = importJobDocuments.map((doc) => doc.trim().toLowerCase());

      if (pageType === 'Import' && normalizeExportDocuments.includes(normalizeDetectedDocument)) {
        var mismatchAlert = await this.$showAlert(
          'warning',
          'Document Mismatch Detected',
          `This document is typically used for <strong>Export Job</strong>. <br> Are you sure you want to proceed with creating an Import Job?`,
          [{ text: 'Create Import Job' }, { text: 'Open Export Job', color: 'lightgray' }],
        );
        if (mismatchAlert != 'Create Import Job') {
          let route = this.$router.resolve(`/Home/${this.$route.params['user']}/FreightForwarding/Export%20Job`);
          window.open(route.href, '_blank');
        }
      }

      if (pageType === 'Export' && normalizeImportDocuments.includes(normalizeDetectedDocument)) {
        var mismatchAlert = await this.$showAlert(
          'warning',
          'Document Mismatch Detected',
          `This document is typically used for <strong>Import Job</strong>. <br> Are you sure you want to proceed with creating an Export Job?`,
          [{ text: 'Create Export Job' }, { text: 'Open Import Job', color: 'lightgray' }],
        );
        if (mismatchAlert != 'Create Export Job') {
          let route = this.$router.resolve(`/Home/${this.$route.params['user']}/FreightForwarding/Import%20Job`);
          window.open(route.href, '_blank');
        }
      }
    },
    dateChanged: function (field) {
      switch (field) {
        case 'portOfLoadingETA':
          this.step3ErrMessage['portOfLoadingETA'] = '';
          break;
        case 'closingTime':
          this.step3ErrMessage['closingTime'] = '';
          break;
        case 'portOfDischargeETA':
          this.step3ErrMessage['portOfDischargeETA'] = '';
          break;
      }
    },
    validateDates: function (stepIndex = 2) {
      let errors = [];
      this.step3ErrMessage = {};

      const addError = (field, msg) => {
        this.step3ErrMessage[field] = msg;
        errors.push(msg);
      };

      const checkDate = (field, requiredMsg, invalidMsg) => {
        const val = this.selectedShippingInfoData[field];

        if (field === 'portOfLoadingETD' || field === 'ETATime' || field === 'fDestETA') {
          if (val && (val > this.maxDate || val < this.minDate)) {
            addError(field, invalidMsg);
          }
        } else {
          if (!val) {
            if (requiredMsg) addError(field, requiredMsg);
          } else if (val > this.maxDate || val < this.minDate) {
            addError(field, invalidMsg);
          }
        }
      };

      // common checks
      checkDate('portOfDischargeETA', 'POD ETA is required!', 'POD ETA Date Invalid!');
      checkDate('ETATime', null, 'ETA Date Invalid!');
      checkDate('fDestETA', null, 'Final Dest. ETA Date Invalid!');

      // export-only
      if (this.pageType === 'Export') {
        checkDate('portOfLoadingETA', 'POL ETA is required!', 'POL ETA Date Invalid!');
        checkDate('closingTime', 'Closing is required!', 'Closing Date Invalid!');
      }

      // import-only
      if (this.pageType === 'Import') {
        checkDate('portOfLoadingETD', null, 'POL ETD Date Invalid!');
      }

      // IMPORTANT: store into correct step
      this.stepErrors[stepIndex] = errors;

      return errors.length === 0;
    },
    nextStep() {
      this.validateField();
      if (this.currentStep == 4) {
        // console.log(this.selectedGeneralInfoData)
        if (this.selectedGeneralInfoData['remarks'] != '' && this.selectedGeneralInfoData['remarks'] != null) {
          if (this.selectedGeneralInfoData['remarks'].length > 200) {
            this.remarksError = 'Remarks cannot exceed 200 characters';
            return;
          }
        }
      } else if (this.currentStep == 2) {
        this.validateDates();
      }
      this.currentStep += 1;
    },
    prevStep() {
      this.currentStep -= 1;
    },
    validateField: function (stepIndex = this.currentStep) {
      let isValid = true;
      let errors = [];
      // CLEAR previous errors
      //this.stepErrors[stepIndex] = [];

      if (stepIndex === 0) {
        this.step1ErrMessage = {};
        if (this.pageType == 'Import') {
          if (this.selectedHeaderInfoData['selectedConsignee'] == null) {
            this.step1ErrMessage['selectedConsignee'] = 'Consignee is required!';
            errors.push('Consignee is required!');
            isValid = false;
          }
          if (this.selectedHeaderInfoData['selectedTransMode'] == null) {
            this.step1ErrMessage['selectedTransMode'] = 'Transport Mode is required!';
            errors.push('Transport Mode is required!');
            isValid = false;
          }
          if (this.selectedHeaderInfoData['selectedBillTo'] == null) {
            this.step1ErrMessage['selectedBillTo'] = 'Bill To is required!';
            errors.push('Bill To is required!');
            isValid = false;
          }
          if (this.selectedHeaderInfoData['selectedCargoType'] == null) {
            this.step1ErrMessage['selectedCargoType'] = 'Cargo Type is required!';
            errors.push('Cargo Type is required!');
            isValid = false;
          }
        } else if (this.pageType == 'Export') {
          if (this.selectedHeaderInfoData['selectedShipper'] == null) {
            this.step1ErrMessage['selectedShipper'] = 'Shipper is required!';
            errors.push('Shipper is required!');
            isValid = false;
          }
          if (this.selectedHeaderInfoData['selectedBillTo'] == null) {
            this.step1ErrMessage['selectedBillTo'] = 'Bill To is required!';
            errors.push('Bill To is required!');
            isValid = false;
          }
          if (this.selectedHeaderInfoData['selectedTransMode'] == null) {
            this.step1ErrMessage['selectedTransMode'] = 'Transport Mode is required!';
            errors.push('Transport Mode is required!');
            isValid = false;
          }
          if (this.selectedHeaderInfoData['selectedCargoType'] == null) {
            this.step1ErrMessage['selectedCargoType'] = 'Cargo Type is required!';
            errors.push('Cargo Type is required!');
            isValid = false;
          }
        }
      } else if (stepIndex === 1) {
        this.step2ErrMessage = {};
        if (this.selectedShippingInfoData['selectedVesselID'] == null) {
          this.step2ErrMessage['selectedVesselID'] = 'Vessel ID is required!';
          errors.push('Vessel ID is required!');
          isValid = false;
        }
        if (this.selectedShippingInfoData['selectedPortLoading'] == null) {
          this.step2ErrMessage['selectedPortLoading'] = 'Port of Loading is required!';
          errors.push('Port of Loading is required!');
          isValid = false;
        }
        if (this.selectedShippingInfoData['selectedShipCall'] == null) {
          this.step2ErrMessage['selectedShipCall'] = 'Ship Call # is required!';
          errors.push('Ship Call # is required!');
          isValid = false;
        }
        if (this.selectedShippingInfoData['selectedPortDischarge'] == null) {
          this.step2ErrMessage['selectedPortDischarge'] = 'Port of Discharge is required!';
          errors.push('Port of Discharge is required!');
          isValid = false;
        }
        if (this.selectedShippingInfoData['selectedAgentList'] == null) {
          this.step2ErrMessage['selectedAgentList'] = 'Shipping Agent is required!';
          errors.push('Shipping Agent is required!');
          isValid = false;
        }
        if (this.selectedShippingInfoData['voyNo'] == '') {
          this.step2ErrMessage['voyNo'] = 'Voyage # is required!';
          errors.push('Voyage # is required!');
          isValid = false;
        }
      } else if (stepIndex == 5) {
        this.step6ErrMessage = {};
        if (this.selectedCargoInfoData['selectedPMaterial'] == null) {
          this.step6ErrMessage['selectedPMaterial'] = 'Packing Material is required!';
          errors.push('Packing Material is required!');
          isValid = false;
        }
        if (this.selectedCargoInfoData['selectedPackageType'] == null) {
          this.step6ErrMessage['selectedPackageType'] = 'Pkg Type is required!';
          errors.push('Pkg Type is required!');
          isValid = false;
        }
        if (this.selectedCargoInfoData['packageCount'] == '') {
          this.step6ErrMessage['packageCount'] = 'No. of Pkg is required!';
          errors.push('No. of Pkg is required!');
          isValid = false;
        }
      }
      // regenerate errors
      this.stepErrors[stepIndex] = errors;
      //console.log(this.stepErrors);
      return isValid;
    },
    getStepErrors: function (step) {
      let errors = [];

      if (step === 0) {
        errors = Object.values(this.step1ErrMessage || {});
      }

      if (step === 1) {
        errors = Object.values(this.step2ErrMessage || {});
      }

      if (step === 2) {
        errors = Object.values(this.step3ErrMessage || {});
      }

      if (step === 5) {
        errors = Object.values(this.step6ErrMessage || {});
      }

      // remove empty/null
      return errors.filter((x) => x);
    },
    validateSubPackagesContainers: function () {
      this.finalMissingFields = [];
      let errorMessageClone = structuredClone(this.errMessage);
      for (let i = 0; i < this.subPackageList.length; i++) {
        const subPackage = this.subPackageList[i];

        if (!subPackage.pkgQty) {
          this.finalMissingFields.push({ step: 6, field: `Package ${i + 1} - Pkg Qty` });
          errorMessageClone[`pkgQtyRef${i}`] = 'Pkg Qty is required!';
        }

        if (!subPackage.pkgType) {
          this.finalMissingFields.push({ step: 6, field: `Package ${i + 1} - Pkg Type` });
          errorMessageClone[`pkgTypeRef${i}`] = 'Pkg Type is required!';
        }

        if (!subPackage.grossWeight) {
          this.finalMissingFields.push({ step: 6, field: `Package ${i + 1} - Gross Weight` });
          errorMessageClone[`grossWeightRef${i}`] = 'Total Gross (KG) is required!';
        }
      }
      for (let i = 0; i < this.containerList.length; i++) {
        const container = this.containerList[i];

        if (!container.contNo && this.pageType == 'Import') {
          this.finalMissingFields.push({ step: 6, field: `Container ${i + 1} - Container #` });
          errorMessageClone[`contNoRef${i}`] = 'Container # is required!';
        }

        if (!container.contSize) {
          this.finalMissingFields.push({ step: 6, field: `Container ${i + 1} - Size` });
          errorMessageClone[`contSizeRef${i}`] = 'Size is required!';
        }

        if (!container.contType) {
          this.finalMissingFields.push({ step: 6, field: `Container ${i + 1} - Type` });
          errorMessageClone[`contTypeRef${i}`] = 'Type is required!';
        }

        if (!container.handlingType) {
          this.finalMissingFields.push({ step: 6, field: `Container ${i + 1} - Handling Type` });
          errorMessageClone[`handlingTypeRef${i}`] = 'Handling Type is required!';
        }

        if (!container.deliveryType) {
          this.finalMissingFields.push({ step: 6, field: `Container ${i + 1} - Delivery Type` });
          errorMessageClone[`deliveryTypeRef${i}`] = 'Delivery Type is required!';
        }

        if (!container.contSocCoc) {
          this.finalMissingFields.push({ step: 6, field: `Container ${i + 1} - SOC/COC` });
          errorMessageClone[`contSocCocRef${i}`] = 'SOC/COC is required!';
        }
        this.errMessage = errorMessageClone;
      }
    },
    submitCheck: async function () {
      this.validateField();
      this.validateSubPackagesContainers();

      const stepErrorsFlat = Object.values(this.stepErrors || {}).flat();
      const step6Errors = this.finalMissingFields || [];
      if (step6Errors.length > 0) {
        // if there are missing fields in sub package/container, show alert and return

        const formatted = `
          <table border="1" style="margin: 0 auto; border-collapse: collapse;">
            <tr>
              <th>Step</th>
              <th>Field</th>
            </tr>
            ${step6Errors
              .map(
                (f) => `
              <tr>
                <td style="width: 80px; text-align:center;">${f.step}</td>
                <td style="width: 300px;">${f.field}</td>
              </tr>
            `,
              )
              .join('')}
          </table>
          \nPlease complete all required fields above before proceeding.
          `;
        await this.$showAlert('warning', 'Missing details in Sub Package / Container', formatted, [{ text: 'Back' }]);

        return;
      }

      if (stepErrorsFlat.length > 0) {
        // if there are missing fields in main form, show alert with option to proceed or go back to correct

        const rows = [];

        Object.entries(this.stepErrors || {}).forEach(([step, fields]) => {
          (fields || []).forEach((f) => {
            rows.push({
              step: Number(step) + 1,
              field: f.replace(' is required!', ''),
            });
          });
        });

        const uniqueRows = Array.from(new Map(rows.map((r) => [`${r.step}-${r.field}`, r])).values());

        const formatted = `
            <table border="1" style="margin: 0 auto; border-collapse: collapse;">
              <tr>
                <th>Step</th>
                <th>Field</th>
              </tr>

              ${uniqueRows
                .map(
                  (r) => `
                <tr style="cursor: pointer;" onclick="window.goToStep(${r.step - 1})">
                  <td style="width: 80px; text-align:center; padding: 0; cursor: pointer; line-height: 0; color: blue;">
                      <u>${r.step}</u>
                  </td>
                  <td style="width: 300px;">${r.field}</td>
                </tr>
              `,
                )
                .join('')}

            </table>
            \nProceed as <strong>unconfirm</strong> job?
            `;

        const res = await this.$showAlert(
          'warning',
          'The below fields are required but is empty or invalid',
          formatted,
          [{ text: 'Proceed' }, { text: 'Back', color: 'lightgray' }],
        );

        if (res === 'Back') return;
      }

      this.submit();
    },

    submit() {
      //if any Transshipment field have value, tick checkbox after submit
      if (
        this.selectedShippingInfoData['mVoyNo'] != '' ||
        this.selectedShippingInfoData['selectedMVesselID'] != null ||
        this.selectedShippingInfoData['selectedPortDischarge2'] != null ||
        this.selectedShippingInfoData['ETATime'] != null
      ) {
        this.selectedShippingInfoData['isTranshipment'] = true;
      }

      //reassign to match Job module
      // Job.vue only have two keys = polETD and polETA

      //for PORT OF LOADING
      // Import Job = POL ETD ONLY
      // Export Job = POL ETA ONLY

      //for PORT OF DISCHARGE
      // both = POD ETA ONLY
      if (this.pageType == 'Import') {
        this.selectedShippingInfoData['polETD'] =
          this.selectedShippingInfoData['portOfLoadingETD'] ? this.selectedShippingInfoData['portOfLoadingETD'] : null;
      } else {
        this.selectedShippingInfoData['polETD'] =
          this.selectedShippingInfoData['portOfLoadingETA'] ? this.selectedShippingInfoData['portOfLoadingETA'] : null;
      }
      //Port of Discharge field
      this.selectedShippingInfoData['polETA'] =
        this.selectedShippingInfoData['portOfDischargeETA'] ?
          this.selectedShippingInfoData['portOfDischargeETA']
        : null;

      this.selectedHeaderInfoData.shipperAddress = this.shipperAddress;
      this.selectedHeaderInfoData.consigneeAddress = this.consigneeAddress;
      this.selectedHeaderInfoData.notifyPartyAddress = this.notifyPartyAddress;

      this.$emit('submit', {
        selectedHeaderInfoData: this.selectedHeaderInfoData,
        selectedGeneralInfoData: this.selectedGeneralInfoData,
        selectedShippingInfoData: this.selectedShippingInfoData,
        selectedCargoInfoData: this.selectedCargoInfoData,
        subPackageList: this.subPackageList.length > 0 ? this.subPackageList : null,
        containerList: this.containerList.length > 0 ? this.containerList : null,
      });
      let selectedData = {
        shipper:
          this.selectedHeaderInfoData['selectedShipper'] ?
            this.selectedHeaderInfoData['selectedShipper']['Shipper Code'].trim()
          : null,
        consignee:
          this.selectedHeaderInfoData['selectedConsignee'] ?
            this.selectedHeaderInfoData['selectedConsignee']['Consignee Code'].trim()
          : null,
        notifyParty:
          this.selectedHeaderInfoData['selectedNotifyParty'] ?
            this.selectedHeaderInfoData['selectedNotifyParty']['Notify Party Code'].trim()
          : null,
        billTo:
          this.selectedHeaderInfoData['selectedBillTo'] ?
            this.selectedHeaderInfoData['selectedBillTo']['Customer Code'].trim()
          : null,
        vesselID:
          this.selectedShippingInfoData['selectedVesselID'] ?
            this.selectedShippingInfoData['selectedVesselID']['Vessel ID'].trim()
          : null,
        portOfLoading:
          this.selectedShippingInfoData['selectedPortLoading'] ?
            this.selectedShippingInfoData['selectedPortLoading']['Port Code'].trim()
          : null,
        portOfDischarge:
          this.selectedShippingInfoData['selectedPortDischarge'] ?
            this.selectedShippingInfoData['selectedPortDischarge']['Port Code'].trim()
          : null,
        shipcallno:
          this.selectedShippingInfoData['selectedShipCall'] ?
            this.selectedShippingInfoData['selectedShipCall']['Ship Call No'].trim()
          : null,
        shippingLine:
          this.selectedShippingInfoData['selectedShippingLine'] ?
            this.selectedShippingInfoData['selectedShippingLine']['Agent Code'].trim()
          : null,
        agentList:
          this.selectedShippingInfoData['selectedAgentList'] ?
            this.selectedShippingInfoData['selectedAgentList']['Agent Code'].trim()
          : null,
      };
      let scannedData = {
        shipper: this.docAIData['selectedShipper'],
        consignee: this.docAIData['selectedConsignee'],
        notifyParty: this.docAIData['selectedNotifyParty'],
        billTo: this.docAIData['selectedBillTo'],
        vesselID: this.docAIData['selectedVesselID'],
        shipcallno: this.docAIData['selectedShipCall'],
        portOfLoading: this.docAIData['selectedPortLoading'],
        portOfDischarge: this.docAIData['selectedPortDischarge'],
        shippingLine: this.docAIData['selectedShippingLine'],
        agentList: this.docAIData['selectedAgentList'],
      };
      this.$apiCallV2('/UserSelection/SaveUserSelection', {
        coRefname: this.CoRefname,
        selections: JSON.stringify(selectedData),
        scans: JSON.stringify(scannedData),
      });
    },
    async masterDataProcessing() {
      //Calculate soundex and metaphone encoding of master data that requires spell checking
      this.soundexEncoding['selectedShipper'] = this.soundex(this.docAIData['selectedShipper']);
      this.dMetaphoneEncoding['selectedShipper'] =
        this.docAIData['selectedShipper'] ? doubleMetaphone(this.docAIData['selectedShipper']) : ['', ''];
      this.soundexEncoding['selectedConsignee'] = this.soundex(this.docAIData['selectedConsignee']);
      this.dMetaphoneEncoding['selectedConsignee'] =
        this.docAIData['selectedConsignee'] ? doubleMetaphone(this.docAIData['selectedConsignee']) : ['', ''];
      this.soundexEncoding['selectedNotifyParty'] = this.soundex(this.docAIData['selectedNotifyParty']);
      this.dMetaphoneEncoding['selectedNotifyParty'] =
        this.docAIData['selectedNotifyParty'] ? doubleMetaphone(this.docAIData['selectedNotifyParty']) : ['', ''];
      this.soundexEncoding['selectedBillTo'] = this.soundex(this.docAIData['selectedBillTo']);
      this.dMetaphoneEncoding['selectedBillTo'] =
        this.docAIData['selectedBillTo'] ? doubleMetaphone(this.docAIData['selectedBillTo']) : ['', ''];
      this.soundexEncoding['selectedShipCall'] = this.soundex(this.docAIData['selectedShipCall']);
      this.dMetaphoneEncoding['selectedShipCall'] =
        this.docAIData['selectedShipCall'] ? doubleMetaphone(this.docAIData['selectedShipCall']) : ['', ''];
      this.soundexEncoding['selectedPortLoading'] = this.soundex(this.docAIData['selectedPortLoading']);
      this.dMetaphoneEncoding['selectedPortLoading'] =
        this.docAIData['selectedPortLoading'] ? doubleMetaphone(this.docAIData['selectedPortLoading']) : ['', ''];
      this.soundexEncoding['selectedPortDischarge'] = this.soundex(this.docAIData['selectedPortDischarge']);
      this.dMetaphoneEncoding['selectedPortDischarge'] =
        this.docAIData['selectedPortDischarge'] ? doubleMetaphone(this.docAIData['selectedPortDischarge']) : ['', ''];

      //Calculate Levenshtein distance between master data soundex encoding against every soundex encoding of data in the list
      let shipperSoundexLevScore = this.shipperSoundexEncoding.map((a) =>
        this.levenshtein(this.soundexEncoding['selectedShipper'], a),
      );
      let consigneeSoundexLevScore = this.consigneeSoundexEncoding.map((a) =>
        this.levenshtein(this.soundexEncoding['selectedConsignee'], a),
      );
      let notifyPartySoundexLevScore = this.notifyPartySoundexEncoding.map((a) =>
        this.levenshtein(this.soundexEncoding['selectedNotifyParty'], a),
      );
      let billToSoundexLevScore = this.billToSoundexEncoding.map((a) =>
        this.levenshtein(this.soundexEncoding['selectedBillTo'], a),
      );
      let polSoundexLevScore = this.polPortCodeSoundexEncoding.map((a) =>
        this.levenshtein(this.soundexEncoding['selectedPortLoading'], a),
      );
      let scnSoundexLevScore = this.shipCallNoSoundexEncoding.map((a) =>
        this.levenshtein(this.soundexEncoding['selectedShipCall'], a),
      );
      let podSoundexLevScore = this.podPortCodeSoundexEncoding.map((a) =>
        this.levenshtein(this.soundexEncoding['selectedPortDischarge'], a),
      );

      //Reduce the size of the list; only data with levenshtein distance of 0 are kept
      let shipperSoundexZeroDistance = shipperSoundexLevScore.reduce((a, e, i) => {
        e === 0 ? a.push(i) : null;
        return a;
      }, []);
      let consigneeSoundexZeroDistance = consigneeSoundexLevScore.reduce((a, e, i) => {
        e === 0 ? a.push(i) : null;
        return a;
      }, []);
      let notifyPartySoundexZeroDistance = notifyPartySoundexLevScore.reduce((a, e, i) => {
        e === 0 ? a.push(i) : null;
        return a;
      }, []);
      let billToSoundexZeroDistance = billToSoundexLevScore.reduce((a, e, i) => {
        e === 0 ? a.push(i) : null;
        return a;
      }, []);
      let scnSoundexZeroDistance = scnSoundexLevScore.reduce((a, e, i) => {
        e === 0 ? a.push(i) : null;
        return a;
      }, []);
      let polSoundexZeroDistance = polSoundexLevScore.reduce((a, e, i) => {
        e === 0 ? a.push(i) : null;
        return a;
      }, []);
      let podSoundexZeroDistance = podSoundexLevScore.reduce((a, e, i) => {
        e === 0 ? a.push(i) : null;
        return a;
      }, []);

      //Create object based on reduced list to store score
      let shipperScoreTally = shipperSoundexZeroDistance.reduce((a, b) => {
        a[b] = 0;
        return a;
      }, {});
      let consigneeScoreTally = consigneeSoundexZeroDistance.reduce((a, b) => {
        a[b] = 0;
        return a;
      }, {});
      let notifyPartyScoreTally = notifyPartySoundexZeroDistance.reduce((a, b) => {
        a[b] = 0;
        return a;
      }, {});
      let billToScoreTally = billToSoundexZeroDistance.reduce((a, b) => {
        a[b] = 0;
        return a;
      }, {});
      let polScoreTally = polSoundexZeroDistance.reduce((a, b) => {
        a[b] = 0;
        return a;
      }, {});
      let scnScoreTally = scnSoundexZeroDistance.reduce((a, b) => {
        a[b] = 0;
        return a;
      }, {});
      let podScoreTally = podSoundexZeroDistance.reduce((a, b) => {
        a[b] = 0;
        return a;
      }, {});

      //Calculate Levenshtein distance between master data primary metaphone encoding against every primary metaphone encoding of data in the list
      let shipperPrimaryEncodingLevScore = shipperSoundexZeroDistance.map((a) =>
        this.levenshtein(this.shipperDMetaphoneEncoding[a][0], this.dMetaphoneEncoding['selectedShipper'][0]),
      );
      let consigneePrimaryEncodingLevScore = consigneeSoundexZeroDistance.map((a) =>
        this.levenshtein(this.consigneeDMetaphoneEncoding[a][0], this.dMetaphoneEncoding['selectedConsignee'][0]),
      );
      let notifyPartyPrimaryEncodingLevScore = notifyPartySoundexZeroDistance.map((a) =>
        this.levenshtein(this.notifyPartyDMetaphoneEncoding[a][0], this.dMetaphoneEncoding['selectedNotifyParty'][0]),
      );
      let billToPrimaryEncodingLevScore = billToSoundexZeroDistance.map((a) =>
        this.levenshtein(this.billToDMetaphoneEncoding[a][0], this.dMetaphoneEncoding['selectedBillTo'][0]),
      );
      let polPrimaryEncodingLevScore = polSoundexZeroDistance.map((a) =>
        this.levenshtein(this.polPortCodeDMetaphoneEncoding[a][0], this.dMetaphoneEncoding['selectedPortLoading'][0]),
      );
      let podPrimaryEncodingLevScore = podSoundexZeroDistance.map((a) =>
        this.levenshtein(this.podPortCodeDMetaphoneEncoding[a][0], this.dMetaphoneEncoding['selectedPortDischarge'][0]),
      );
      let scnPrimaryEncodingLevScore = scnSoundexZeroDistance.map((a) =>
        this.levenshtein(this.shipCallNoDMetaphoneEncoding[a][0], this.dMetaphoneEncoding['selectedShipCall'][0]),
      );

      //Calculate percentage of score based on calculated Levenshtein distance and the maximum length between scanned data primary metaphone encoding and primary metaphone encoding of data in list
      let shipperPrimaryEncodingFinalScore = shipperPrimaryEncodingLevScore.map((n, i) => {
        let num =
          (
            Math.max(
              this.dMetaphoneEncoding['selectedShipper'][0].length,
              this.shipperDMetaphoneEncoding[i][0].length,
            ) === 0
          ) ?
            '1'
          : Math.max(this.dMetaphoneEncoding['selectedShipper'][0].length, this.shipperDMetaphoneEncoding[i][0].length);
        return (((num - n) / num) * 5).toPrecision(3) === '5.00' ? '6.00' : (((num - n) / num) * 5).toPrecision(3);
      });
      let consigneePrimaryEncodingFinalScore = consigneePrimaryEncodingLevScore.map((n, i) => {
        let num =
          (
            Math.max(
              this.dMetaphoneEncoding['selectedConsignee'][0].length,
              this.consigneeDMetaphoneEncoding[i][0].length,
            ) === 0
          ) ?
            '1'
          : Math.max(
              this.dMetaphoneEncoding['selectedConsignee'][0].length,
              this.consigneeDMetaphoneEncoding[i][0].length,
            );
        return (((num - n) / num) * 5).toPrecision(3) === '5.00' ? '6.00' : (((num - n) / num) * 5).toPrecision(3);
      });
      let notifyPartyPrimaryEncodingFinalScore = notifyPartyPrimaryEncodingLevScore.map((n, i) => {
        let num =
          (
            Math.max(
              this.dMetaphoneEncoding['selectedNotifyParty'][0].length,
              this.notifyPartyDMetaphoneEncoding[i][0].length,
            ) === 0
          ) ?
            '1'
          : Math.max(
              this.dMetaphoneEncoding['selectedNotifyParty'][0].length,
              this.notifyPartyDMetaphoneEncoding[i][0].length,
            );
        return (((num - n) / num) * 5).toPrecision(3) === '5.00' ? '6.00' : (((num - n) / num) * 5).toPrecision(3);
      });
      let billToPrimaryEncodingFinalScore = billToPrimaryEncodingLevScore.map((n, i) => {
        let num =
          (
            Math.max(
              this.dMetaphoneEncoding['selectedBillTo'][0].length,
              this.billToDMetaphoneEncoding[i][0].length,
            ) === 0
          ) ?
            '1'
          : Math.max(this.dMetaphoneEncoding['selectedBillTo'][0].length, this.billToDMetaphoneEncoding[i][0].length);
        return (((num - n) / num) * 5).toPrecision(3) === '5.00' ? '6.00' : (((num - n) / num) * 5).toPrecision(3);
      });
      let polPrimaryEncodingFinalScore = polPrimaryEncodingLevScore.map((n, i) => {
        let num =
          (
            Math.max(
              this.dMetaphoneEncoding['selectedPortDischarge'][0].length,
              this.polPortCodeDMetaphoneEncoding[i][0].length,
            ) === 0
          ) ?
            '1'
          : Math.max(
              this.dMetaphoneEncoding['selectedPortDischarge'][0].length,
              this.polPortCodeDMetaphoneEncoding[i][0].length,
            );
        return (((num - n) / num) * 5).toPrecision(3) === '5.00' ? '6.00' : (((num - n) / num) * 5).toPrecision(3);
      });
      let podPrimaryEncodingFinalScore = podPrimaryEncodingLevScore.map((n, i) => {
        let num =
          (
            Math.max(
              this.dMetaphoneEncoding['selectedPortDischarge'][0].length,
              this.podPortCodeDMetaphoneEncoding[i][0].length,
            ) === 0
          ) ?
            '1'
          : Math.max(
              this.dMetaphoneEncoding['selectedPortDischarge'][0].length,
              this.podPortCodeDMetaphoneEncoding[i][0].length,
            );
        return (((num - n) / num) * 5).toPrecision(3) === '5.00' ? '6.00' : (((num - n) / num) * 5).toPrecision(3);
      });
      let scnPrimaryEncodingFinalScore = scnPrimaryEncodingLevScore.map((n, i) => {
        const masterIndex = scnSoundexZeroDistance[i];

        const num =
          Math.max(
            this.dMetaphoneEncoding['selectedShipCall'][0].length,
            this.shipCallNoDMetaphoneEncoding[masterIndex][0].length,
          ) || 1;

        return (((num - n) / num) * 5).toPrecision(3) === '5.00' ? '6.00' : (((num - n) / num) * 5).toPrecision(3);
      });

      //Calculate Levenshtein distance between master data secondary metaphone encoding against every secondary metaphone encoding of data in the list
      let shipperSecondaryEncodingLevScore = shipperSoundexZeroDistance.map((a) =>
        this.levenshtein(this.shipperDMetaphoneEncoding[a][1], this.dMetaphoneEncoding['selectedShipper'][1]),
      );
      let consigneeSecondaryEncodingLevScore = consigneeSoundexZeroDistance.map((a) =>
        this.levenshtein(this.consigneeDMetaphoneEncoding[a][1], this.dMetaphoneEncoding['selectedConsignee'][1]),
      );
      let notifyPartySecondaryEncodingLevScore = notifyPartySoundexZeroDistance.map((a) =>
        this.levenshtein(this.notifyPartyDMetaphoneEncoding[a][1], this.dMetaphoneEncoding['selectedNotifyParty'][1]),
      );
      let billToSecondaryEncodingLevScore = billToSoundexZeroDistance.map((a) =>
        this.levenshtein(this.billToDMetaphoneEncoding[a][1], this.dMetaphoneEncoding['selectedBillTo'][1]),
      );
      let polSecondaryEncodingLevScore = polSoundexZeroDistance.map((a) =>
        this.levenshtein(this.polPortCodeDMetaphoneEncoding[a][1], this.dMetaphoneEncoding['selectedPortLoading'][1]),
      );
      let podSecondaryEncodingLevScore = podSoundexZeroDistance.map((a) =>
        this.levenshtein(this.podPortCodeDMetaphoneEncoding[a][1], this.dMetaphoneEncoding['selectedPortDischarge'][1]),
      );
      let scnSecondaryEncodingLevScore = scnSoundexZeroDistance.map((a) =>
        this.levenshtein(this.shipCallNoDMetaphoneEncoding[a][1], this.dMetaphoneEncoding['selectedShipCall'][1]),
      );

      //Calculate percentage of score based on calculated Levenshtein distance and the maximum length between scanned data secondary metaphone encoding and secondary metaphone encoding of data in list
      let shipperSecondaryEncodingFinalScore = shipperSecondaryEncodingLevScore.map((n, i) => {
        let num =
          (
            Math.max(
              this.dMetaphoneEncoding['selectedShipper'][1].length,
              this.shipperDMetaphoneEncoding[i][1].length,
            ) === 0
          ) ?
            '1'
          : Math.max(this.dMetaphoneEncoding['selectedShipper'][1].length, this.shipperDMetaphoneEncoding[i][1].length);
        return (((num - n) / num) * 5).toPrecision(3) === '5.00' ? '6.00' : (((num - n) / num) * 5).toPrecision(3);
      });
      let consigneeSecondaryEncodingFinalScore = consigneeSecondaryEncodingLevScore.map((n, i) => {
        let num =
          (
            Math.max(
              this.dMetaphoneEncoding['selectedConsignee'][1].length,
              this.consigneeDMetaphoneEncoding[i][1].length,
            ) === 0
          ) ?
            '1'
          : Math.max(
              this.dMetaphoneEncoding['selectedConsignee'][1].length,
              this.consigneeDMetaphoneEncoding[i][1].length,
            );
        return (((num - n) / num) * 5).toPrecision(3) === '5.00' ? '6.00' : (((num - n) / num) * 5).toPrecision(3);
      });
      let notifyPartySecondaryEncodingFinalScore = notifyPartySecondaryEncodingLevScore.map((n, i) => {
        let num =
          (
            Math.max(
              this.dMetaphoneEncoding['selectedNotifyParty'][1].length,
              this.notifyPartyDMetaphoneEncoding[i][1].length,
            ) === 0
          ) ?
            '1'
          : Math.max(
              this.dMetaphoneEncoding['selectedNotifyParty'][1].length,
              this.notifyPartyDMetaphoneEncoding[i][1].length,
            );
        return (((num - n) / num) * 5).toPrecision(3) === '5.00' ? '6.00' : (((num - n) / num) * 5).toPrecision(3);
      });
      let billToSecondaryEncodingFinalScore = billToSecondaryEncodingLevScore.map((n, i) => {
        let num =
          (
            Math.max(
              this.dMetaphoneEncoding['selectedBillTo'][1].length,
              this.billToDMetaphoneEncoding[i][1].length,
            ) === 0
          ) ?
            '1'
          : Math.max(this.dMetaphoneEncoding['selectedBillTo'][1].length, this.billToDMetaphoneEncoding[i][1].length);
        return (((num - n) / num) * 5).toPrecision(3) === '5.00' ? '6.00' : (((num - n) / num) * 5).toPrecision(3);
      });
      let polSecondaryEncodingFinalScore = polSecondaryEncodingLevScore.map((n, i) => {
        let num =
          (
            Math.max(
              this.dMetaphoneEncoding['selectedPortLoading'][1].length,
              this.polPortCodeDMetaphoneEncoding[i][1].length,
            ) === 0
          ) ?
            '1'
          : Math.max(
              this.dMetaphoneEncoding['selectedPortDischarge'][1].length,
              this.polPortCodeDMetaphoneEncoding[i][1].length,
            );
        return (((num - n) / num) * 5).toPrecision(3) === '5.00' ? '6.00' : (((num - n) / num) * 5).toPrecision(3);
      });
      let podSecondaryEncodingFinalScore = podSecondaryEncodingLevScore.map((n, i) => {
        let num =
          (
            Math.max(
              this.dMetaphoneEncoding['selectedPortDischarge'][1].length,
              this.podPortCodeDMetaphoneEncoding[i][1].length,
            ) === 0
          ) ?
            '1'
          : Math.max(
              this.dMetaphoneEncoding['selectedPortDischarge'][1].length,
              this.podPortCodeDMetaphoneEncoding[i][1].length,
            );
        return (((num - n) / num) * 5).toPrecision(3) === '5.00' ? '6.00' : (((num - n) / num) * 5).toPrecision(3);
      });
      let scnSecondaryEncodingFinalScore = scnSecondaryEncodingLevScore.map((n, i) => {
        const masterIndex = scnSoundexZeroDistance[i];

        const num =
          Math.max(
            this.dMetaphoneEncoding['selectedShipCall'][1].length,
            this.shipCallNoDMetaphoneEncoding[masterIndex][1].length,
          ) || 1;

        return (((num - n) / num) * 5).toPrecision(3) === '5.00' ? '6.00' : (((num - n) / num) * 5).toPrecision(3);
      });

      //Calculate percentage of score based on Levenshtein distance between master data and data in list and the maximum length between scanned data and data in list(not encoding)
      let shipperDefLevScore = shipperSoundexZeroDistance.map((a) => {
        let shipper = this.headerInfoData.shipper[a]['Shipper Name'];
        let score = this.levenshtein(this.docAIData['selectedShipper'], shipper.toUpperCase());
        let num = Math.max(this.docAIData['selectedShipper'].length, shipper.length);
        return (((num - score) / num) * 10).toPrecision(3) === '10.0' ?
            '11.0'
          : (((num - score) / num) * 10).toPrecision(3) || 0;
      });
      let consigneeDefLevScore = consigneeSoundexZeroDistance.map((a) => {
        let consignee = this.headerInfoData.consignee[a]['Consignee Name'];
        let score = this.levenshtein(this.docAIData['selectedConsignee'], consignee.toUpperCase());
        let num = Math.max(this.docAIData['selectedConsignee'].length, consignee.length);
        return (((num - score) / num) * 10).toPrecision(3) === '10.0' ?
            '11.0'
          : (((num - score) / num) * 10).toPrecision(3) || 0;
      });
      let notifyPartyDefLevScore = notifyPartySoundexZeroDistance.map((a) => {
        let notifyParty = this.headerInfoData.notifyParty[a]['Notify Party Name'];
        let score = this.levenshtein(this.docAIData['selectedNotifyParty'], notifyParty.toUpperCase());
        let num = Math.max(this.docAIData['selectedNotifyParty'].length, notifyParty.length);
        return (((num - score) / num) * 10).toPrecision(3) === '10.0' ?
            '11.0'
          : (((num - score) / num) * 10).toPrecision(3) || 0;
      });
      let billToDefLevScore = billToSoundexZeroDistance.map((a) => {
        let billTo = this.headerInfoData.billTo[a]['Customer Name'];
        let score = this.levenshtein(this.docAIData['selectedBillTo'], billTo.toUpperCase());
        let num = Math.max(this.docAIData['selectedBillTo'].length, billTo.length);
        return (((num - score) / num) * 10).toPrecision(3) === '10.0' ?
            '11.0'
          : (((num - score) / num) * 10).toPrecision(3) || 0;
      });
      let polDefLevScore = polSoundexZeroDistance.map((a) => {
        let portLoading = this.shippingInfoData.portOfLoading[a]['Port Location'];
        let score = this.levenshtein(this.docAIData['selectedPortLoading'], portLoading.toUpperCase());
        let num = Math.max(this.docAIData['selectedPortLoading'].length, portLoading.length);
        return (((num - score) / num) * 10).toPrecision(3) === '10.0' ?
            '11.0'
          : (((num - score) / num) * 10).toPrecision(3) || 0;
      });
      let podDefLevScore = podSoundexZeroDistance.map((a) => {
        let portDischarge = this.shippingInfoData.portOfDischarge[a]['Port Location'];
        let score = this.levenshtein(this.docAIData['selectedPortDischarge'], portDischarge.toUpperCase());
        let num = Math.max(this.docAIData['selectedPortDischarge'].length, portDischarge.length);
        return (((num - score) / num) * 10).toPrecision(3) === '10.0' ?
            '11.0'
          : (((num - score) / num) * 10).toPrecision(3) || 0;
      });
      let scnDefLevScore = scnSoundexZeroDistance.map((a) => {
        let scannedShipCall = this.docAIData['selectedShipCall'] || '';
        let shipCallNo = this.shippingInfoData.shipCallNoList[a]['Ship Call No'] || '';
        let score = this.levenshtein(scannedShipCall.toUpperCase(), shipCallNo.toUpperCase());
        let num = Math.max(scannedShipCall.length, shipCallNo.length) || 1;

        return (((num - score) / num) * 10).toPrecision(3) === '10.0' ?
            '11.0'
          : (((num - score) / num) * 10).toPrecision(3) || 0;
      });

      let shipperKeys = Object.keys(shipperScoreTally);
      let consigneeKeys = Object.keys(consigneeScoreTally);
      let notifyPartyKeys = Object.keys(notifyPartyScoreTally);
      let billToKeys = Object.keys(billToScoreTally);
      let polKeys = Object.keys(polScoreTally);
      let scnKeys = Object.keys(scnScoreTally);
      let podKeys = Object.keys(podScoreTally);
      let i = 0;

      // Score Update - Using Lev Score of Primary Metaphone Encoding of Scanned Word against Primary Metaphone Encoding of List
      shipperKeys.forEach((a) => {
        shipperScoreTally[a] = shipperPrimaryEncodingFinalScore[i];
        i++;
      });
      i = 0;
      consigneeKeys.forEach((a) => {
        consigneeScoreTally[a] = consigneePrimaryEncodingFinalScore[i];
        i++;
      });
      i = 0;
      notifyPartyKeys.forEach((a) => {
        notifyPartyScoreTally[a] = notifyPartyPrimaryEncodingFinalScore[i];
        i++;
      });
      i = 0;
      billToKeys.forEach((a) => {
        billToScoreTally[a] = billToPrimaryEncodingFinalScore[i];
        i++;
      });
      i = 0;
      polKeys.forEach((a) => {
        polScoreTally[a] = polPrimaryEncodingFinalScore[i];
        i++;
      });
      i = 0;
      scnKeys.forEach((a) => {
        scnScoreTally[a] = scnPrimaryEncodingFinalScore[i];
        i++;
      });
      i = 0;
      podKeys.forEach((a) => {
        podScoreTally[a] = podPrimaryEncodingFinalScore[i];
        i++;
      });

      // Score Update - Using Lev Score of Secondary Metaphone Encoding of Scanned Word against Secondary Metaphone Encoding of List
      i = 0;
      shipperKeys.forEach((a) => {
        shipperScoreTally[a] = (
          parseFloat(shipperScoreTally[a]) + parseFloat(shipperSecondaryEncodingFinalScore[i])
        ).toPrecision(3);
        i++;
      });
      i = 0;
      consigneeKeys.forEach((a) => {
        consigneeScoreTally[a] = (
          parseFloat(consigneeScoreTally[a]) + parseFloat(consigneeSecondaryEncodingFinalScore[i])
        ).toPrecision(3);
        i++;
      });
      i = 0;
      notifyPartyKeys.forEach((a) => {
        notifyPartyScoreTally[a] = (
          parseFloat(notifyPartyScoreTally[a]) + parseFloat(notifyPartySecondaryEncodingFinalScore[i])
        ).toPrecision(3);
        i++;
      });
      i = 0;
      billToKeys.forEach((a) => {
        billToScoreTally[a] = (
          parseFloat(billToScoreTally[a]) + parseFloat(billToSecondaryEncodingFinalScore[i])
        ).toPrecision(3);
        i++;
      });
      i = 0;
      scnKeys.forEach((a) => {
        scnScoreTally[a] = (parseFloat(scnScoreTally[a]) + parseFloat(scnSecondaryEncodingFinalScore[i])).toPrecision(
          3,
        );
        i++;
      });
      i = 0;
      polKeys.forEach((a) => {
        polScoreTally[a] = (parseFloat(polScoreTally[a]) + parseFloat(polSecondaryEncodingFinalScore[i])).toPrecision(
          3,
        );
        i++;
      });
      i = 0;
      podKeys.forEach((a) => {
        podScoreTally[a] = (parseFloat(podScoreTally[a]) + parseFloat(podSecondaryEncodingFinalScore[i])).toPrecision(
          3,
        );
        i++;
      });

      // Score Update - Using Lev Score of Scanned Word against List
      i = 0;
      shipperKeys.forEach((a) => {
        shipperScoreTally[a] = (parseFloat(shipperScoreTally[a]) + parseFloat(shipperDefLevScore[i])).toPrecision(3);
        i++;
      });
      i = 0;
      consigneeKeys.forEach((a) => {
        consigneeScoreTally[a] = (parseFloat(consigneeScoreTally[a]) + parseFloat(consigneeDefLevScore[i])).toPrecision(
          3,
        );
        i++;
      });
      i = 0;
      notifyPartyKeys.forEach((a) => {
        notifyPartyScoreTally[a] = (
          parseFloat(notifyPartyScoreTally[a]) + parseFloat(notifyPartyDefLevScore[i])
        ).toPrecision(3);
        i++;
      });
      i = 0;
      billToKeys.forEach((a) => {
        billToScoreTally[a] = (parseFloat(billToScoreTally[a]) + parseFloat(billToDefLevScore[i])).toPrecision(3);
        i++;
      });
      i = 0;
      scnKeys.forEach((a) => {
        scnScoreTally[a] = (parseFloat(scnScoreTally[a]) + parseFloat(scnDefLevScore[i])).toPrecision(3);
        i++;
      });
      i = 0;
      polKeys.forEach((a) => {
        polScoreTally[a] = (parseFloat(polScoreTally[a]) + parseFloat(polDefLevScore[i])).toPrecision(3);
        i++;
      });
      i = 0;
      podKeys.forEach((a) => {
        podScoreTally[a] = (parseFloat(podScoreTally[a]) + parseFloat(podDefLevScore[i])).toPrecision(3);
        i++;
      });

      // Score Update - Using Previous User Selection
      const response = await this.$apiCallV2(`/UserSelection/GetHistoricalValue?corefname=${this.CoRefname}`);
      let data = response.data;
      try {
        Object.keys(Object.entries(data['shipper']).find((a) => a[0] === this.docAIData['selectedShipper'])[1])
          .map((a) => this.headerInfoData.shipper.find((b) => b['Shipper Code'] === a)['#'])
          .forEach((c, i) => {
            shipperScoreTally[c - 1] =
              parseFloat(shipperScoreTally[c - 1] || 0) +
              parseFloat(Object.values(data['shipper'][this.docAIData['selectedShipper']])[i].toPrecision(3));
          });
      } catch (err) {}
      try {
        Object.keys(Object.entries(data['consignee']).find((a) => a[0] === this.docAIData['selectedConsignee'])[1])
          .map((a) => this.headerInfoData.consignee.find((b) => b['Consignee Code'] === a)['#'])
          .forEach((c, i) => {
            consigneeScoreTally[c - 1] =
              parseFloat(consigneeScoreTally[c - 1] || 0) +
              parseFloat(Object.values(data['consignee'][this.docAIData['selectedConsignee']])[i].toPrecision(3));
          });
      } catch (err) {}
      try {
        Object.keys(Object.entries(data['notifyParty']).find((a) => a[0] === this.docAIData['selectedNotifyParty'])[1])
          .map((a) => this.headerInfoData.notifyParty.find((b) => b['Notify Party Code'] === a)['#'])
          .forEach((c, i) => {
            notifyPartyScoreTally[c - 1] =
              parseFloat(notifyPartyScoreTally[c - 1] || 0) +
              parseFloat(Object.values(data['notifyParty'][this.docAIData['selectedNotifyParty']])[i].toPrecision(3));
          });
      } catch (err) {}
      try {
        Object.keys(Object.entries(data['billTo']).find((a) => a[0] === this.docAIData['selectedBillTo'])[1])
          .map((a) => this.headerInfoData.billTo.find((b) => b['Customer Code'] === a)['#'])
          .forEach((c, i) => {
            billToScoreTally[c - 1] =
              parseFloat(billToScoreTally[c - 1] || 0) +
              parseFloat(Object.values(data['billTo'][this.docAIData['selectedBillTo']])[i].toPrecision(3));
          });
      } catch (err) {}
      try {
        Object.keys(
          Object.entries(data['portOfLoading']).find((a) => a[0] === this.docAIData['selectedPortLoading'])[1],
        )
          .map((a) => this.shippingInfoData.portOfLoading.find((b) => b['Port Code'] === a)['#'])
          .forEach((c, i) => {
            polScoreTally[c - 1] =
              parseFloat(polScoreTally[c - 1] || 0) +
              parseFloat(Object.values(data['portOfLoading'][this.docAIData['selectedPortLoading']])[i].toPrecision(3));
          });
      } catch (err) {}
      try {
        Object.keys(
          Object.entries(data['portOfDischarge']).find((a) => a[0] === this.docAIData['selectedPortDischarge'])[1],
        )
          .map((a) => this.shippingInfoData.portOfDischarge.find((b) => b['Port Code'] === a)['#'])
          .forEach((c, i) => {
            podScoreTally[c - 1] =
              parseFloat(podScoreTally[c - 1] || 0) +
              parseFloat(
                Object.values(data['portOfDischarge'][this.docAIData['selectedPortDischarge']])[i].toPrecision(3),
              );
          });
      } catch (err) {}

      //Sort object based on descending score and convert to array
      let shipperScoreTallyArray = Object.entries(shipperScoreTally).sort(([, a], [, b]) => b - a);
      let consigneeScoreTallyArray = Object.entries(consigneeScoreTally).sort(([, a], [, b]) => b - a);
      let notifyPartyScoreTallyArray = Object.entries(notifyPartyScoreTally).sort(([, a], [, b]) => b - a);
      let billToScoreTallyArray = Object.entries(billToScoreTally).sort(([, a], [, b]) => b - a);
      let polScoreTallyArray = Object.entries(polScoreTally).sort(([, a], [, b]) => b - a);
      let scnScoreTallyArray = Object.entries(scnScoreTally).sort(([, a], [, b]) => b - a);
      let podScoreTallyArray = Object.entries(podScoreTally).sort(([, a], [, b]) => b - a);

      //Reduce array by filtering data with score less than 10
      shipperScoreTallyArray = shipperScoreTallyArray.filter((a) => parseFloat(a[1]) > '10.0');
      consigneeScoreTallyArray = consigneeScoreTallyArray.filter((a) => parseFloat(a[1]) > '10.0');
      notifyPartyScoreTallyArray = notifyPartyScoreTallyArray.filter((a) => parseFloat(a[1]) > '10.0');
      billToScoreTallyArray = billToScoreTallyArray.filter((a) => parseFloat(a[1]) > '10.0');
      polScoreTallyArray = polScoreTallyArray.filter((a) => parseFloat(a[1]) > '10.0');
      scnScoreTallyArray = scnScoreTallyArray.filter((a) => parseFloat(a[1]) > '10.0');
      podScoreTallyArray = podScoreTallyArray.filter((a) => parseFloat(a[1]) > '10.0');

      //Reduce original list based on filtered array
      let matchingShipper = shipperScoreTallyArray.map((a) => this.headerInfoData.shipper[a[0]]);
      let matchingConsignee = consigneeScoreTallyArray.map((a) => this.headerInfoData.consignee[a[0]]);
      let matchingNotifyParty = notifyPartyScoreTallyArray.map((a) => this.headerInfoData.notifyParty[a[0]]);
      let matchingBillTo = billToScoreTallyArray.map((a) => this.headerInfoData.billTo[a[0]]);
      let matchingPortLoading = polScoreTallyArray.map((a) => this.shippingInfoData.portOfLoading[a[0]]);
      let matchingPortDischarge = podScoreTallyArray.map((a) => this.shippingInfoData.portOfDischarge[a[0]]);
      let matchingShipCallNo = scnScoreTallyArray.map((a) => this.shippingInfoData.shipCallNoList[a[0]]);

      // Getting ready for Fuse matching
      const shipperSearch = this.searchList(this.headerInfoData.shipper, 'Shipper Name');
      const consigneeSearch = this.searchList(this.headerInfoData.consignee, 'Consignee Name');
      const notifyPartySearch = this.searchList(this.headerInfoData.notifyParty, 'Notify Party Name');
      const billToSearch = this.searchList(this.headerInfoData.billTo, 'Customer Name');
      const shipCallNoSearch = this.searchList(this.shippingInfoData.shipCallNoList, 'Ship Call No');
      const portOfLoadingSearch = this.searchList(this.shippingInfoData.portOfLoading, 'Port Location');
      const portOfDischargeSearch = this.searchList(this.shippingInfoData.portOfDischarge, 'Port Location');
      const vesselIDSearch = this.searchList(this.shippingInfoData.vesselIDList, 'Vessel ID');
      const vesselIDSearchName = this.searchList(this.shippingInfoData.vesselIDList, 'Vessel Name');
      const shippingLineSearch = this.searchList(this.shippingInfoData.shippingLineList, 'Agent Name');
      const agentListSearch = this.searchList(this.shippingInfoData.shippingAgentList, 'Agent Name');
      const frtForwarderSearch = this.searchList(this.shippingInfoData.frtForwarderList, 'Agent Name');

      //Fuse matching, if no match, use soundex result
      this.filteredData['shipper'] =
        this.bestMatch(shipperSearch, this.docAIData['selectedShipper']).length > 0 ?
          this.bestMatch(shipperSearch, this.docAIData['selectedShipper'])
        : matchingShipper;

      this.filteredData['consignee'] =
        this.bestMatch(consigneeSearch, this.docAIData['selectedConsignee']).length > 0 ?
          this.bestMatch(consigneeSearch, this.docAIData['selectedConsignee'])
        : matchingConsignee;

      this.filteredData['notifyParty'] =
        this.bestMatch(notifyPartySearch, this.docAIData['selectedNotifyParty']).length > 0 ?
          this.bestMatch(notifyPartySearch, this.docAIData['selectedNotifyParty'])
        : matchingNotifyParty;

      this.filteredData['billTo'] =
        this.bestMatch(billToSearch, this.docAIData['selectedBillTo']).length > 0 ?
          this.bestMatch(billToSearch, this.docAIData['selectedBillTo'])
        : matchingBillTo;

      this.filteredData['shipCallNo'] =
        this.bestMatch(shipCallNoSearch, this.docAIData['selectedShipCall']).length > 0 ?
          this.bestMatch(shipCallNoSearch, this.docAIData['selectedShipCall'])
        : matchingShipCallNo;

      //console.log(this.filteredData['shipCallNo'])
      //port of loading
      // for very common values of port, to ensure matches
      let selectedPortLoading = this.docAIData['selectedPortLoading'];
      const normalizedPortLoading = selectedPortLoading.toLowerCase().trim();
      const commonPorts = ['port klang', 'port kelang'];
      const isValidPort = commonPorts.some((port) => normalizedPortLoading.includes(port));
      if (isValidPort) {
        selectedPortLoading = 'port klang';
      }
      // FIRST attempt
      let result = this.bestMatch(portOfLoadingSearch, selectedPortLoading);
      // if no result clean string and retry
      if (result.length === 0 && /[\/,]/.test(selectedPortLoading)) {
        // take only the part before '/'
        selectedPortLoading = selectedPortLoading.split(/[\/,]/)[0].trim();
        result = this.bestMatch(portOfLoadingSearch, selectedPortLoading);
      }
      // final assignment with fallback to soundex result
      this.filteredData['portOfLoading'] = result.length > 0 ? result : matchingPortLoading;
      //port of discharge
      let selectedPortDischarge = this.docAIData['selectedPortDischarge'];
      const normalizedPortDischarge = selectedPortDischarge.toLowerCase().trim();
      const isValidPort2 = commonPorts.some((port) => normalizedPortDischarge.includes(port));
      if (isValidPort2) {
        selectedPortDischarge = 'port klang';
      }
      // FIRST attempt
      let result2 = this.bestMatch(portOfDischargeSearch, selectedPortDischarge);
      // if no result clean string and retry
      if (result2.length === 0 && /[\/,]/.test(selectedPortDischarge)) {
        // take only the part before '/'
        selectedPortDischarge = selectedPortDischarge.split(/[\/,]/)[0].trim();
        result2 = this.bestMatch(portOfDischargeSearch, selectedPortDischarge);
      }
      // final assignment with fallback to soundex result
      this.filteredData['portOfDischarge'] = result2.length > 0 ? result2 : matchingPortDischarge;

      this.filteredData['vesselID'] =
        this.bestMatch(vesselIDSearch, this.docAIData['selectedVesselID']).length > 0 ?
          this.bestMatch(vesselIDSearch, this.docAIData['selectedVesselID'])
        : this.bestMatch(vesselIDSearchName, this.docAIData['selectedVesselName']); // search for name if id no result
      this.filteredData['shippingLine'] = this.bestMatch(shippingLineSearch, this.docAIData['selectedShippingLine']);
      this.filteredData['agentList'] = this.bestMatch(agentListSearch, this.docAIData['selectedAgentList']);
      this.filteredData['frtForwarder'] = this.bestMatch(frtForwarderSearch, this.docAIData['selectedFrtForwarder']);

      //Calculate number of data that passed the filter
      this.collisionNumber['selectedShipper'] = this.filteredData['shipper']?.length;
      this.collisionNumber['selectedConsignee'] = this.filteredData['consignee']?.length;
      this.collisionNumber['selectedNotifyParty'] = this.filteredData['notifyParty']?.length;
      this.collisionNumber['selectedBillTo'] = this.filteredData['billTo']?.length;
      this.collisionNumber['selectedVesselID'] = this.filteredData['vesselID']?.length;
      this.collisionNumber['selectedPortLoading'] = this.filteredData['portOfLoading']?.length;
      this.collisionNumber['selectedPortDischarge'] = this.filteredData['portOfDischarge']?.length;
      this.collisionNumber['selectedShipCall'] = this.filteredData['shipCallNo']?.length;
      this.collisionNumber['selectedShippingLine'] = this.filteredData['shippingLine']?.length;
      this.collisionNumber['selectedAgentList'] = this.filteredData['agentList']?.length;
      this.collisionNumber['selectedFrtForwarder'] = this.filteredData['frtForwarder']?.length;
    },
    populateMasterData() {
      this.filteredData['shipper'].length > 0 ?
        (this.selectedHeaderInfoData['selectedShipper'] = this.filteredData['shipper'][0])
      : null;
      this.filteredData['consignee'].length > 0 ?
        (this.selectedHeaderInfoData['selectedConsignee'] = this.filteredData['consignee'][0])
      : null;
      this.filteredData['notifyParty'].length > 0 ?
        (this.selectedHeaderInfoData['selectedNotifyParty'] = this.filteredData['notifyParty'][0])
      : null;
      this.filteredData['billTo'].length > 0 ?
        (this.selectedHeaderInfoData['selectedBillTo'] = this.filteredData['billTo'][0])
      : null;
      this.filteredData['vesselID'].length > 0 ?
        (this.selectedShippingInfoData['selectedVesselID'] = this.filteredData['vesselID'][0])
      : null;
      this.filteredData['portOfLoading'].length > 0 ?
        (this.selectedShippingInfoData['selectedPortLoading'] = this.filteredData['portOfLoading'][0])
      : null;
      this.filteredData['portOfDischarge'].length > 0 ?
        (this.selectedShippingInfoData['selectedPortDischarge'] = this.filteredData['portOfDischarge'][0])
      : null;
      this.filteredData['shipCallNo'].length > 0 ?
        (this.selectedShippingInfoData['selectedShipCall'] = this.filteredData['shipCallNo'][0])
      : null;
      //console.log(this.selectedShippingInfoData['selectedShipCall'])
      this.filteredData['shippingLine'].length > 0 ?
        (this.selectedShippingInfoData['selectedShippingLine'] = this.filteredData['shippingLine'][0])
      : null;
      this.filteredData['agentList'].length > 0 ?
        (this.selectedShippingInfoData['selectedAgentList'] = this.filteredData['agentList'][0])
      : null;

      this.filteredData['selectedShipper'][0] = this.selectedHeaderInfoData['selectedShipper'];
      this.filteredData['selectedConsignee'][0] = this.selectedHeaderInfoData['selectedConsignee'];
      this.filteredData['selectedNotifyParty'][0] = this.selectedHeaderInfoData['selectedNotifyParty'];

      this.filteredData['selectedBillTo'][0] = this.selectedHeaderInfoData['selectedBillTo'];
      this.filteredData['selectedVesselID'][0] = this.selectedShippingInfoData['selectedVesselID'];
      this.filteredData['selectedPortLoading'][0] = this.selectedShippingInfoData['selectedPortLoading'];
      this.filteredData['selectedPortDischarge'][0] = this.selectedShippingInfoData['selectedPortDischarge'];
      this.filteredData['selectedShippingLine'][0] = this.selectedShippingInfoData['selectedShippingLine'];
      this.filteredData['selectedAgentList'][0] = this.selectedShippingInfoData['selectedAgentList'];
      this.filteredData['shipCallNo'][0] = this.selectedShippingInfoData['selectedShipCall'];

      this.shipperSelected(this.selectedHeaderInfoData['selectedShipper']);
      this.consigneeSelected(this.selectedHeaderInfoData['selectedConsignee']);
      this.npartySelected(this.selectedHeaderInfoData['selectedNotifyParty']);

      if (
        this.pageType == 'Export' &&
        !this.docAIData['selectedBillTo'] &&
        this.selectedHeaderInfoData['selectedShipper']
      ) {
        this.selectedHeaderInfoData['selectedBillTo'] = this.headerInfoData['billTo'].find(
          (b) => b['Customer Code'] == this.selectedHeaderInfoData['selectedShipper']['Bill To'],
        );
      } // if BillTo is empty and Shipper is not, default to select BillTo based on Shipper data

      if (
        this.pageType == 'Import' &&
        !this.docAIData['selectedBillTo'] &&
        this.selectedHeaderInfoData['selectedConsignee']
      ) {
        this.selectedHeaderInfoData['selectedBillTo'] = this.headerInfoData['billTo'].find(
          (b) => b['Customer Code'] == this.selectedHeaderInfoData['selectedConsignee']['Bill To'],
        );
      } // vise versa for Import, with Consignee
    },
    shipperSelected: function (selected) {
      if (selected) {
        this.shipperAddress = {};
        this.shipperAddress['name'] = selected['Shipper Name'];
        this.shipperAddress['add1'] = selected['Shipper Add1'];
        this.shipperAddress['add2'] = selected['Shipper Add2'];
        this.shipperAddress['add3'] = selected['Shipper Add3'];
        this.shipperAddress['add4'] = selected['Shipper Add4'];
        this.shipperAddress['postcode'] = selected['Shipper PostCode'];
        this.shipperAddress['city'] = selected['Shipper City'];
        this.shipperAddress['state'] = selected['Shipper State'];
        this.shipperAddress['country'] = selected['Shipper Country'];
        this.shipperAddress['attn'] = selected['Shipper Attn'];
        this.shipperAddress['tel'] = selected['Shipper Tel'];
        this.shipperAddress['fax'] = selected['Shipper Fax'];
        this.shipperAddress['gst'] = selected['Shipper GSTNo'];

        this.showShipperAddress = true;

        if (this.pageType == 'Export' && selected != '') {
          // change BillTo based on selected Shipper
          this.selectedHeaderInfoData['selectedBillTo'] = this.headerInfoData['billTo'].find(
            (b) => b['Customer Code'] == selected['Bill To'],
          );
        }
      } else {
        this.shipperAddress = null;
        this.showShipperAddress = false;
      }
    },
    consigneeSelected: function (selected) {
      if (selected) {
        this.consigneeAddress = {};
        this.consigneeAddress['name'] = selected['Consignee Name'];
        this.consigneeAddress['add1'] = selected['Consignee Add1'];
        this.consigneeAddress['add2'] = selected['Consignee Add2'];
        this.consigneeAddress['add3'] = selected['Consignee Add3'];
        this.consigneeAddress['add4'] = selected['Consignee Add4'];
        this.consigneeAddress['postcode'] = selected['Consignee PostCode'];
        this.consigneeAddress['city'] = selected['Consignee City'];
        this.consigneeAddress['state'] = selected['Consignee State'];
        this.consigneeAddress['country'] = selected['Consignee Country'];
        this.consigneeAddress['attn'] = selected['Consignee Attn'];
        this.consigneeAddress['tel'] = selected['Consignee Tel'];
        this.consigneeAddress['fax'] = selected['Consignee Fax'];
        this.consigneeAddress['gst'] = selected['Consignee GSTNo'];
        this.showConsigneeAddress = true;

        if (this.pageType == 'Import' && selected != '') {
          // change BillTo based on selected Consignee
          this.selectedHeaderInfoData['selectedBillTo'] = this.headerInfoData['billTo'].find(
            (b) => b['Customer Code'] == selected['Bill To'],
          );
        }
      } else {
        this.consigneeAddress = null;
        this.showConsigneeAddress = false;
      }
    },
    npartySelected: function (selected) {
      if (selected) {
        this.notifyPartyAddress = {};
        this.notifyPartyAddress['name'] = selected['Notify Party Name'];
        this.notifyPartyAddress['add1'] = selected['Notify Party Add1'];
        this.notifyPartyAddress['add2'] = selected['Notify Party Add2'];
        this.notifyPartyAddress['add3'] = selected['Notify Party Add3'];
        this.notifyPartyAddress['add4'] = selected['Notify Party Add4'];
        this.notifyPartyAddress['postcode'] = selected['Notify Party PostCode'];
        this.notifyPartyAddress['city'] = selected['Notify Party City'];
        this.notifyPartyAddress['state'] = selected['Notify Party State'];
        this.notifyPartyAddress['country'] = selected['Notify Party Country'];
        this.notifyPartyAddress['attn'] = selected['Notify Party Attn'];
        this.notifyPartyAddress['tel'] = selected['Notify Party Tel'];
        this.notifyPartyAddress['fax'] = selected['Notify Party Fax'];
        this.notifyPartyAddress['gst'] = selected['Notify Party GSTNo'];

        this.showNPartyAddress = true;
      } else {
        this.notifyPartyAddress = null;
        this.showNPartyAddress = false;
      }
    },
    // populateX methods below are for filling fields based on scanned key-value pair by Document AI
    populateHeaderInfoData() {
      // Fuse matching- Cargo Type
      const cargoTypeSearch = this.searchList(this.headerInfoData.cargoType, 'Cargo Type');
      //console.log(this.headerInfoData.cargoType)
      this.docAIData['selectedCargoType'] =
        this.data.data[0].keys.find((x) => x.key === 'selectedCargoType').Value ?
          this.data.data[0].keys.find((x) => x.key === 'selectedCargoType').Value
        : '';
      if (this.docAIData['selectedCargoType'].includes('/')) {
        // split values like FCL/FCL and take first value
        let cargoTypeSplit = this.docAIData['selectedCargoType'].split('/')[0];
        this.filteredHeaderInfoData['selectedCargoType'] = this.bestMatch(cargoTypeSearch, cargoTypeSplit);
      } else {
        this.filteredHeaderInfoData['selectedCargoType'] = this.bestMatch(
          cargoTypeSearch,
          this.docAIData['selectedCargoType'],
        );
      }

      if (this.filteredHeaderInfoData['selectedCargoType'].length > 0) {
        this.selectedHeaderInfoData['selectedCargoType'] = this.filteredHeaderInfoData['selectedCargoType'][0];
      } else {
        // if not found in document, select default cargo type
        this.selectedHeaderInfoData['selectedCargoType'] = this.headerInfoData.cargoType.getSelected(
          this.defaults['DefaultCargoType'],
          'Cargo Type',
        );
      }

      this.selectedHeaderInfoData['detectedDocument'] = this.data.data[0].keys.find(
        (x) => x.key === 'detectedDocument',
      ).Value;
      this.selectedHeaderInfoData['selectedTransMode'] = this.headerInfoData['transMode'].find(
        (item) => item['#'] == this.data.data[0].keys.find((x) => x.key === 'selectedTransMode').Value,
      );
      this.selectedHeaderInfoData['selectedSmkFormType'] = this.data.data[0].keys.find(
        (x) => x.key === 'selectedSmkFormType',
      ).Value;
      this.selectedHeaderInfoData['projectNo'] = this.data.data[0].keys.find((x) => x.key === 'projectNo').Value;
      this.selectedHeaderInfoData['custRefNo'] = this.data.data[0].keys.find((x) => x.key === 'custRefNo').Value;

      if (this.pageType == 'Export') {
        this.selectedHeaderInfoData['vbkgRefNo'] = this.data.data[0].keys.find((x) => x.key === 'vbkgRefNo').Value;
      } else {
        this.selectedHeaderInfoData['vbkgRefNo'] = this.data.data[0].keys.find((x) => x.key === 'blNo').Value;
      }
      //this.selectedHeaderInfoData['vbkgRefNo'] = this.data.data[0].keys.find((x) => x.key === 'vbkgRefNo').Value;

      this.selectedHeaderInfoData['billToDetails'] = this.data.data[0].keys.find(
        (x) => x.key === 'billToDetails',
      ).Value;
      this.selectedHeaderInfoData['jobStatus'] = this.data.data[0].keys.find((x) => x.key === 'jobStatus').Value;
    },
    populateGeneralInfoData() {
      // Fuse matching- Freight Type
      const freightTypeSearch = this.searchList(this.generalInfoData.freightTypeList, 'Freight Type Desc');
      this.docAIData['selectedFreightType'] =
        this.data.data[1].keys.find((x) => x.key === 'selectedFreightType').Value ?
          this.data.data[1].keys.find((x) => x.key === 'selectedFreightType').Value
        : '';
      this.filteredGeneralInfoData['selectedFreightType'] = this.bestMatch(
        freightTypeSearch,
        this.docAIData['selectedFreightType'],
      );
      this.filteredGeneralInfoData['selectedFreightType'].length > 0 ?
        (this.selectedGeneralInfoData['selectedFreightType'] = this.filteredGeneralInfoData['selectedFreightType'][0])
      : null;

      // Fuse matching- Payment Country
      const paymentCountrySearch = this.searchList(this.generalInfoData.countryList, 'Country Code');
      const paymentCountrySearchName = this.searchList(this.generalInfoData.countryList, 'Country');
      this.docAIData['selectedCountry'] =
        this.data.data[1].keys.find((x) => x.key === 'selectedCountry').Value ?
          this.data.data[1].keys.find((x) => x.key === 'selectedCountry').Value
        : '';
      const bestMatchCountryCode = this.bestMatch(paymentCountrySearch, this.docAIData['selectedCountry']);
      const bestMatchCountryName = this.bestMatch(paymentCountrySearchName, this.docAIData['selectedCountry']);
      // if country code empty, check country name
      this.filteredGeneralInfoData['selectedCountry'] =
        bestMatchCountryCode.length > 0 ? bestMatchCountryCode : bestMatchCountryName;
      this.filteredGeneralInfoData['selectedCountry'].length > 0 ?
        (this.selectedGeneralInfoData['selectedCountry'] = this.filteredGeneralInfoData['selectedCountry'][0])
      : null;

      // Fuse matching- Currency
      const currencySearch = this.searchList(this.generalInfoData.currencyList, 'Currency Code');
      this.docAIData['selectedCurrency'] =
        this.data.data[1].keys.find((x) => x.key === 'selectedCurrency').Value ?
          this.data.data[1].keys.find((x) => x.key === 'selectedCurrency').Value
        : '';
      this.filteredGeneralInfoData['selectedCurrency'] = this.bestMatch(
        currencySearch,
        this.docAIData['selectedCurrency'],
      );
      this.filteredGeneralInfoData['selectedCurrency'].length > 0 ?
        (this.selectedGeneralInfoData['selectedCurrency'] = this.filteredGeneralInfoData['selectedCurrency'][0])
      : null;

      // Fuse matching- Incoterm
      const incotermSearch = this.searchList(this.generalInfoData.incoTermsList, 'Incoterm');
      const incotermSearchDesc = this.searchList(this.generalInfoData.incoTermsList, 'Incoterm Desc');
      this.docAIData['selectedIncoTerms'] =
        this.data.data[1].keys.find((x) => x.key === 'selectedIncoTerms').Value ?
          this.data.data[1].keys.find((x) => x.key === 'selectedIncoTerms').Value
        : '';
      this.filteredGeneralInfoData['selectedIncoTerms'] =
        this.bestMatch(incotermSearch, this.docAIData['selectedIncoTerms'])?.length > 0 ?
          this.bestMatch(incotermSearch, this.docAIData['selectedIncoTerms'])
        : this.bestMatch(incotermSearchDesc, this.docAIData['selectedIncoTerms']); // fallback to Desc

      this.filteredGeneralInfoData['selectedIncoTerms'].length > 0 ?
        (this.selectedGeneralInfoData['selectedIncoTerms'] = this.filteredGeneralInfoData['selectedIncoTerms'][0])
      : null;

      // Fuse matching- Insurance
      const insuranceSearch = this.searchList(this.generalInfoData.insuranceList, 'Insurance');
      this.docAIData['selectedInsurance'] =
        this.data.data[1].keys.find((x) => x.key === 'selectedInsurance').Value ?
          this.data.data[1].keys.find((x) => x.key === 'selectedInsurance').Value
        : '';
      this.filteredGeneralInfoData['selectedInsurance'] = this.bestMatch(
        insuranceSearch,
        this.docAIData['selectedInsurance'],
      );
      this.filteredGeneralInfoData['selectedInsurance'].length > 0 ?
        (this.selectedGeneralInfoData['selectedInsurance'] = this.filteredGeneralInfoData['selectedInsurance'][0])
      : null;

      this.selectedGeneralInfoData['oriBL'] = this.data.data[1].keys.find((x) => x.key === 'oriBL').Value;
      this.selectedGeneralInfoData['houseBL'] = this.data.data[1].keys.find((x) => x.key === 'houseBL').Value;
      this.selectedGeneralInfoData['selectedBranch'] = this.data.data[1].keys.find(
        (x) => x.key === 'selectedBranch',
      ).Value;
      this.selectedGeneralInfoData['selectedServicePIC'] = this.data.data[1].keys.find(
        (x) => x.key === 'selectedServicePIC',
      ).Value;
      this.selectedGeneralInfoData['selectedServiceTeam'] = this.data.data[1].keys.find(
        (x) => x.key === 'selectedServiceTeam',
      ).Value;
      this.selectedGeneralInfoData['selectedSalesPIC'] = this.data.data[1].keys.find(
        (x) => x.key === 'selectedSalesPIC',
      ).Value;
      this.selectedGeneralInfoData['selectedSalesTeam'] = this.data.data[1].keys.find(
        (x) => x.key === 'selectedSalesTeam',
      ).Value;
      this.selectedGeneralInfoData['remarks'] = this.data.data[1].keys.find((x) => x.key === 'remarks').Value;

      // if detected RM select MYR
      if (this.docAIData['selectedCurrency'] == 'RM') {
        this.selectedGeneralInfoData['selectedCurrency'] = this.generalInfoData.currencyList.find(
          (currency) => currency['Currency Code'] == 'MYR',
        );
      }
      // if country empty and currency is MYR
      if (this.selectedGeneralInfoData['selectedCountry']?.length > 0) {
        if (this.selectedGeneralInfoData['selectedCurrency']?.['Currency Code'] === 'MYR') {
          this.selectedGeneralInfoData['selectedCountry'] = this.generalInfoData.countryList.find(
            (country) => country['Country Code'] === 'MY',
          );
        }
      }
    },
    populateShippingInfoData() {
      // Fuse matching - Final Destination
      const finalDestinationSearch = this.searchList(this.shippingInfoData.finalDestination, 'Port Location');
      this.docAIData['selectedFinalDestination'] =
        this.data.data[2].keys.find((x) => x.key === 'selectedFinalDestination').Value ?
          this.data.data[2].keys.find((x) => x.key === 'selectedFinalDestination').Value
        : '';
      let selectedFinalDestination = this.docAIData['selectedFinalDestination'];
      // normalize + common port
      const normalizedFinalDestination = selectedFinalDestination.toLowerCase().trim();
      const commonPorts = ['port klang', 'port kelang'];
      const isValidFinal = commonPorts.some((port) => normalizedFinalDestination.includes(port));
      if (isValidFinal) {
        selectedFinalDestination = 'port klang';
      }
      // FIRST attempt
      let resultFD = this.bestMatch(finalDestinationSearch, selectedFinalDestination);
      // retry if contains "/" or ","
      if (resultFD.length === 0 && /[\/,]/.test(selectedFinalDestination)) {
        selectedFinalDestination = selectedFinalDestination.split(/[\/,]/)[0].trim();

        resultFD = this.bestMatch(finalDestinationSearch, selectedFinalDestination);
      }
      // final assignment (NO fallback)
      this.filteredShippingInfoData['selectedFinalDestination'] = resultFD;
      if (resultFD.length > 0) {
        this.selectedShippingInfoData['selectedFinalDestination'] = resultFD[0];
      }

      // Fuse matching - Special Treatment
      const STSearch = this.searchList(this.shippingInfoData.specialTreatment, 'ST Desc');
      this.docAIData['selectedSpecialTreatment'] =
        this.data.data[2].keys.find((x) => x.key === 'selectedSpecialTreatment').Value ?
          this.data.data[2].keys.find((x) => x.key === 'selectedSpecialTreatment').Value
        : '';
      this.filteredShippingInfoData['selectedSpecialTreatment'] = this.bestMatch(
        STSearch,
        this.docAIData['selectedSpecialTreatment'],
      );
      this.filteredShippingInfoData['selectedSpecialTreatment'].length > 0 ?
        (this.selectedShippingInfoData['selectedSpecialTreatment'] =
          this.filteredShippingInfoData['selectedSpecialTreatment'][0])
      : null;

      // Fuse matching - Station
      const stationSearch = this.searchList(this.shippingInfoData.station, 'Station Name');
      this.docAIData['selectedStation'] =
        this.data.data[2].keys.find((x) => x.key === 'selectedStation').Value ?
          this.data.data[2].keys.find((x) => x.key === 'selectedStation').Value
        : '';
      this.filteredShippingInfoData['selectedStation'] = this.bestMatch(
        stationSearch,
        this.docAIData['selectedStation'],
      );
      this.filteredShippingInfoData['selectedStation'].length > 0 ?
        (this.selectedShippingInfoData['selectedStation'] = this.filteredShippingInfoData['selectedStation'][0])
      : null;

      // Fuse matching - Port Operator
      const portOperatorSearch = this.searchList(this.shippingInfoData.portOperator, 'Operator Name');
      const portOperatorSearchCode = this.searchList(this.shippingInfoData.portOperator, 'Operator Code');
      this.docAIData['selectedPortOperator'] =
        this.data.data[2].keys.find((x) => x.key === 'selectedPortOperator').Value ?
          this.data.data[2].keys.find((x) => x.key === 'selectedPortOperator').Value
        : '';
      this.filteredShippingInfoData['selectedPortOperator'] =
        this.bestMatch(portOperatorSearch, this.docAIData['selectedPortOperator']) ?
          this.bestMatch(portOperatorSearch, this.docAIData['selectedPortOperator'])
        : this.bestMatch(portOperatorSearchCode, this.docAIData['selectedPortOperator']); // match Code if no match for Name
      this.filteredShippingInfoData['selectedPortOperator'].length > 0 ?
        (this.selectedShippingInfoData['selectedPortOperator'] =
          this.filteredShippingInfoData['selectedPortOperator'][0])
      : null;

      // Fuse matching - Mother Vessel
      const motherVesselSearch = this.searchList(this.shippingInfoData.vesselIDList, 'Vessel Name');
      this.docAIData['selectedMVesselID'] =
        this.data.data[2].keys.find((x) => x.key === 'selectedMVesselID').Value ?
          this.data.data[2].keys.find((x) => x.key === 'selectedMVesselID').Value
        : '';
      this.filteredShippingInfoData['selectedMVesselID'] = this.bestMatch(
        motherVesselSearch,
        this.docAIData['selectedMVesselID'],
      );
      this.filteredShippingInfoData['selectedMVesselID'].length > 0 ?
        (this.selectedShippingInfoData['selectedMVesselID'] = this.filteredShippingInfoData['selectedMVesselID'][0])
      : null;

      // Fuse matching - Mother Port
      const mPortOfDischargeSearch = this.searchList(this.shippingInfoData.portOfDischarge, 'Port Location');
      this.docAIData['selectedPortDischarge2'] =
        this.data.data[2].keys.find((x) => x.key === 'selectedPortDischarge2').Value ?
          this.data.data[2].keys.find((x) => x.key === 'selectedPortDischarge2').Value
        : '';
      this.filteredShippingInfoData['selectedPortDischarge2'] = this.bestMatch(
        mPortOfDischargeSearch,
        this.docAIData['selectedPortDischarge2'],
      );
      this.filteredShippingInfoData['selectedPortDischarge2'].length > 0 ?
        (this.selectedShippingInfoData['selectedPortDischarge2'] =
          this.filteredShippingInfoData['selectedPortDischarge2'][0])
      : null;

      this.selectedShippingInfoData['voyNo'] =
        this.data.data[2].keys.find((x) => x.key === 'voyNo').Value ?
          this.data.data[2].keys.find((x) => x.key === 'voyNo').Value
        : this.data.data[2].keys.find((x) => x.key === 'mVoyNo').Value;
      this.filteredData['frtForwarder'].length > 0 ?
        (this.selectedShippingInfoData['selectedFrtForwarder'] = this.filteredData['frtForwarder'][0])
      : null;
      this.selectedShippingInfoData['warehouse'] = this.data.data[2].keys.find((x) => x.key === 'warehouse').Value;
      this.selectedShippingInfoData['demurageTime'] = this.data.data[2].keys.find(
        (x) => x.key === 'demurageTime',
      ).Value;
      this.selectedShippingInfoData['detention'] = this.data.data[2].keys.find((x) => x.key === 'detention').Value;
      this.selectedShippingInfoData['mVoyNo'] = this.data.data[2].keys.find((x) => x.key === 'mVoyNo').Value;

      this.selectedShippingInfoData['freeStorage'] =
        this.data.data[2].keys.find((x) => x.key === 'freeStorage').Value ?
          this.data.data[2].keys.find((x) => x.key === 'freeStorage').Value
        : '';
      this.selectedShippingInfoData['freeDemurrage'] =
        this.data.data[2].keys.find((x) => x.key === 'freeDemurrage').Value ?
          this.data.data[2].keys.find((x) => x.key === 'freeDemurrage').Value
        : '';
      this.selectedShippingInfoData['freeDetention'] =
        this.data.data[2].keys.find((x) => x.key === 'freeDetention').Value ?
          this.data.data[2].keys.find((x) => x.key === 'freeDetention').Value
        : '';

      const parseDate = (key) => {
        // process different date formats
        try {
          let value = this.data.data[2].keys.find((x) => x.key === key)?.Value;
          if (!value) return null;

          // Remove a trailing "(MYT)" and surrounding spaces - Cosco format
          value = value.replace(/\s*\(MYT\)\s*$/i, '').trim();

          let datePart, timePart;
          let fullYear, month, day;

          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

          // Handle "13-Apr-2026" and "12-Apr-2026 23:59HRS" format - AML format
          const dashMonthRegex = /^(\d{1,2})\s*-\s*([a-zA-Z]{3})\s*-\s*(\d{4})(?:\s+(\d{1,2}):(\d{2})\s*(?:HRS)?)?$/i;

          const dashMonthMatch = value.trim().match(dashMonthRegex);

          if (dashMonthMatch) {
            const day = parseInt(dashMonthMatch[1], 10);
            const monthIndex = monthNames.findIndex((name) => name.toLowerCase() === dashMonthMatch[2].toLowerCase());
            const year = parseInt(dashMonthMatch[3], 10);
            const hours = parseInt(dashMonthMatch[4] ?? '0', 10);
            const minutes = parseInt(dashMonthMatch[5] ?? '0', 10);

            if (monthIndex === -1 || hours > 23 || minutes > 59) {
              return null;
            }

            const date = new Date(year, monthIndex, day, hours, minutes, 0);

            // Prevent invalid dates such as "31-Apr-2026"
            const isValid =
              date.getFullYear() === year &&
              date.getMonth() === monthIndex &&
              date.getDate() === day &&
              date.getHours() === hours &&
              date.getMinutes() === minutes;

            return isValid ? date : null;
          }

          //"17 Mar 2024 17:00" format
          const dateTimeRegex = /^(\d{1,2})\s([a-zA-Z]{3})\s(\d{4})\s(\d{1,2}):(\d{2})$/;
          const dtMatch = value.match(dateTimeRegex);

          if (dtMatch) {
            const day = dtMatch[1].padStart(2, '0');
            const month = (monthNames.indexOf(dtMatch[2]) + 1).toString().padStart(2, '0');
            const fullYear = dtMatch[3];
            const hours = parseInt(dtMatch[4], 10);
            const minutes = parseInt(dtMatch[5], 10);

            const date = new Date(`${fullYear}-${month}-${day}T${hours}:${minutes}:00`);
            return isNaN(date.getTime()) ? null : date;
          }

          // "17 Mar 2024" format
          const dateRegex = /^(\d{1,2})\s([a-zA-Z]{3})\s(\d{4})$/;
          const dateMatch = value.match(dateRegex);

          if (dateMatch) {
            const day = dateMatch[1].padStart(2, '0');
            const month = (monthNames.indexOf(dateMatch[2]) + 1).toString().padStart(2, '0');
            const fullYear = dateMatch[3];

            let hours = 0,
              minutes = 0,
              seconds = 0;

            const date = new Date(
              `${fullYear}-${month}-${day}T${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`,
            );
            return isNaN(date.getTime()) ? null : date;
          }

          // Additional regex to handle "Mar 2024 17" format
          const altDateRegex = /^([a-zA-Z]{3})\s(\d{4})\s(\d{1,2})$/;
          const altDateMatch = value.match(altDateRegex);

          if (altDateMatch) {
            const month = (monthNames.indexOf(altDateMatch[1]) + 1).toString().padStart(2, '0');
            const fullYear = altDateMatch[2];
            const day = altDateMatch[3].padStart(2, '0');

            let hours = 0,
              minutes = 0,
              seconds = 0;

            const date = new Date(
              `${fullYear}-${month}-${day}T${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`,
            );
            return isNaN(date.getTime()) ? null : date;
          }

          // Determine the date separator and split accordingly
          if (value.includes(' ')) {
            const parts = value.split(' ');
            datePart = parts[0];
            timePart = parts.slice(1).join(' ');
          } else if (value.includes('\n')) {
            [datePart, timePart] = value.split('\n');
          } else if (value.includes('-')) {
            datePart = value;
          } else if (value.includes('/')) {
            datePart = value;
          } else {
            datePart = value;
          }

          // Handle the date part
          if (datePart) {
            let dateParts;
            if (datePart.includes('-')) {
              dateParts = datePart.split('-');
            } else if (datePart.includes('/')) {
              dateParts = datePart.split('/');
            } else if (datePart.includes('.')) {
              dateParts = datePart.split('.');
            }

            const [part1, part2, part3] = dateParts;

            if (part1.length === 4) {
              // Assuming YYYY-MM-DD
              fullYear = part1;
              month = part2;
              day = part3;
            } else {
              // Check if the date format is DD/MM/YY or MM/DD/YY
              // if first part is greater than 12, assume (DD/MM/YY). Otherwise, assume month (MM/DD/YY)
              if (parseInt(part1, 10) > 12) {
                // Assuming DD/MM/YY
                day = part1;
                month = part2;

                // if year only 2 digits
                const currentYear = new Date().getFullYear();
                const century = Math.floor(currentYear / 100) * 100;
                fullYear = part3.length === 2 ? century + parseInt(part3, 10) : part3;
              } else {
                // Assuming MM/DD/YY
                month = part1;
                day = part2;

                // if year only 2 digits
                const currentYear = new Date().getFullYear();
                const century = Math.floor(currentYear / 100) * 100;
                fullYear = part3.length === 2 ? century + parseInt(part3, 10) : part3;
              }
            }
          } else {
            return null;
          }

          // handling both 12-hour and 24-hour formats.
          let hours = 0,
            minutes = 0;
          if (timePart) {
            timePart = timePart.replace('@', '').trim();
            const timeMatch = timePart.match(/(\d{1,2}):(\d{2})\s*(A|P|AM|PM)?/i);
            if (timeMatch) {
              hours = parseInt(timeMatch[1], 10);
              minutes = parseInt(timeMatch[2], 10);
              let period = timeMatch[3]?.toUpperCase();

              if (period === 'A') period = 'AM';
              if (period === 'P') period = 'PM';

              // Ensure valid 12-hour AM/PM logic
              if (period) {
                if (period === 'PM' && hours < 12) {
                  hours += 12; // Convert to 24-hour format
                } else if (period === 'AM' && hours === 12) {
                  hours = 0; // Midnight case
                }
              } else if (hours > 12) {
                // If no AM/PM and hours > 12, it's 24-hour format, no conversion needed
              }
            }
          }

          const date = new Date(
            `${fullYear}-${month}-${day}T${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`,
          );
          return isNaN(date.getTime()) ? null : date;
        } catch (error) {
          //console.error('Error parsing date:', error);
          return null;
        }
      };

      this.selectedShippingInfoData['selectedFumigator'] = this.data.data[2].keys.find(
        (x) => x.key === 'selectedFumigator',
      ).Value;
      this.selectedShippingInfoData['selectedLocation'] = this.data.data[2].keys.find(
        (x) => x.key === 'selectedLocation',
      ).Value;
      this.selectedShippingInfoData['fumigateTime'] = this.data.data[2].keys.find(
        (x) => x.key === 'fumigateTime',
      ).Value;
      this.selectedShippingInfoData['ventilationTime'] = this.data.data[2].keys.find(
        (x) => x.key === 'ventilationTime',
      ).Value;
      this.selectedShippingInfoData['phytoMarking'] = this.data.data[2].keys.find(
        (x) => x.key === 'phytoMarking',
      ).Value;
      this.selectedShippingInfoData['goodsDescription'] = this.data.data[2].keys.find(
        (x) => x.key === 'goodsDescription',
      ).Value;
      this.selectedShippingInfoData['shippingMarks'] = this.data.data[2].keys.find(
        (x) => x.key === 'shippingMarks',
      ).Value;

      this.selectedShippingInfoData['ETATime'] = this.selectedShippingInfoData['ETATime'];

      // Doc AI have two keys for vessel - "VesselName" / "MotherVessel"
      // If there is a transshipment, the main vessel / longer travel is "Mother Vessel"
      // if there is no transshipment, the only vessel is a "Mother Vessel".
      // if there is response for key "MotherVessel" but not for "VesselName", assume there is no transshipment

      if (this.docAIData['selectedMVesselID'].length > 0 && this.docAIData['selectedVesselID']?.length == 0) {
        this.selectedShippingInfoData['selectedVesselID'] = this.selectedShippingInfoData['selectedMVesselID'];
        this.selectedShippingInfoData['selectedMVesselID'] = null;
        this.docAIData['selectedVesselName'] = this.docAIData['selectedMVesselID'];
        this.docAIData['selectedMVesselID'] = '';

        // for collisios data
        const vesselIDSearchName = this.searchList(this.shippingInfoData.vesselIDList, 'Vessel Name');
        this.filteredData['vesselID'] =
          this.bestMatch(vesselIDSearchName, this.docAIData['selectedVesselName']).length > 0 ?
            this.bestMatch(vesselIDSearchName, this.docAIData['selectedVesselName'])
          : null;
        this.collisionNumber['selectedVesselID'] = this.filteredData['vesselID']?.length;

        this.selectedShippingInfoData['voyNo'] = this.selectedShippingInfoData['mVoyNo'];
        this.docAIData['voyNo'] = this.selectedShippingInfoData['mVoyNo'];
        this.selectedShippingInfoData['mVoyNo'] = '';
      }

      // if "Vessel Name" and "Mother Vessel" is the same, assume there is no transshipment
      if (
        this.selectedShippingInfoData['selectedMVesselID'] != null &&
        this.selectedShippingInfoData['selectedVesselID'] != null &&
        this.selectedShippingInfoData['selectedMVesselID'] == this.selectedShippingInfoData['selectedVesselID']
      ) {
        this.selectedShippingInfoData['selectedMVesselID'] = null;
        this.docAIData['selectedMVesselID'] = '';
        this.selectedShippingInfoData['mVoyNo'] = '';
      }

      this.selectedShippingInfoData['portOfLoadingETD'] = parseDate('polETD') ? parseDate('polETD') : null;
      this.selectedShippingInfoData['portOfLoadingETA'] = parseDate('polETA') ? parseDate('polETA') : null;
      this.selectedShippingInfoData['portOfDischargeETA'] = parseDate('podETA') ? parseDate('podETA') : null;
      this.selectedShippingInfoData['ETATime'] = parseDate('ETATime') ? parseDate('ETATime') : null;
      this.selectedShippingInfoData['fDestETA'] = parseDate('fDestETA') ? parseDate('fDestETA') : null;
      this.selectedShippingInfoData['closingTime'] = parseDate('closingTime') ? parseDate('closingTime') : null;
    },
    populateCargoInfoData() {
      // Fuse matching - Type of Package
      const packageTypeSearch = this.searchList(this.cargoInfoData.pkgTypeList, 'Description');
      this.docAIData['selectedPackageType'] =
        this.data.data[3].keys.find((x) => x.key === 'selectedPackageType').Value ?
          this.data.data[3].keys.find((x) => x.key === 'selectedPackageType').Value
        : '';
      this.filteredCargoInfoData['selectedPackageType'] = this.bestMatch(
        packageTypeSearch,
        this.docAIData['selectedPackageType'],
      );
      //this.filteredCargoInfoData['selectedPackageType'].length > 0 ? this.selectedCargoInfoData['selectedPackageType'] = this.filteredCargoInfoData['selectedPackageType'][0] : null;
      if (this.filteredCargoInfoData['selectedPackageType'].length > 0) {
        this.selectedCargoInfoData['selectedPackageType'] = this.filteredCargoInfoData['selectedPackageType'][0];
      } else {
        // if not found in document, select default Package type
        this.selectedCargoInfoData['selectedPackageType'] = this.cargoInfoData.pkgTypeList.getSelected(
          this.defaults['DefaultPackageType'],
          'Package Code',
        );
      }

      // Fuse matching - Packing Material
      const packingMaterialSearch = this.searchList(this.cargoInfoData.pMaterialList, 'Material Description');
      this.docAIData['selectedPMaterial'] =
        this.data.data[3].keys.find((x) => x.key === 'selectedPMaterial').Value ?
          this.data.data[3].keys.find((x) => x.key === 'selectedPMaterial').Value
        : '';
      this.filteredCargoInfoData['selectedPMaterial'] = this.bestMatch(
        packingMaterialSearch,
        this.data.data[3].keys.find((x) => x.key === 'selectedPMaterial').Value,
      );
      //this.filteredCargoInfoData['selectedPMaterial'].length > 0 ? this.selectedCargoInfoData['selectedPMaterial'] = this.filteredCargoInfoData['selectedPMaterial'][0] : null;
      if (this.filteredCargoInfoData['selectedPMaterial'].length > 0) {
        this.selectedCargoInfoData['selectedPMaterial'] = this.filteredCargoInfoData['selectedPMaterial'][0];
      } else {
        // if not found in document, select default Package Material
        this.selectedCargoInfoData['selectedPMaterial'] = this.cargoInfoData.pMaterialList.getSelected(
          this.defaults['DefaultPackageMaterial'],
          'Packing Material Code',
        );
      }

      // Fuse matching - Commodity Code
      const commodityCodeSearch = this.searchList(this.cargoInfoData.commodityList, 'Description');
      this.docAIData['selectedCommodity'] =
        this.data.data[3].keys.find((x) => x.key === 'selectedCommodity').Value ?
          this.data.data[3].keys.find((x) => x.key === 'selectedCommodity').Value
        : '';
      this.filteredCargoInfoData['selectedCommodity'] = this.bestMatch(
        commodityCodeSearch,
        this.data.data[3].keys.find((x) => x.key === 'selectedCommodity').Value,
      );
      this.filteredCargoInfoData['selectedCommodity'].length > 0 ?
        (this.selectedCargoInfoData['selectedCommodity'] = this.filteredCargoInfoData['selectedCommodity'][0])
      : null;

      let gWeightValue = this.data.data[3].keys.find((x) => x.key === 'gWeight').Value?.replace(/[^0-9.]/g, '');
      let volumeValue = this.data.data[3].keys.find((x) => x.key === 'volume').Value?.replace(/[^0-9.]/g, '');
      let chargeableValue = this.data.data[3].keys.find((x) => x.key === 'chargeable').Value?.replace(/[^0-9.]/g, '');

      this.docAIData['gWeight'] = gWeightValue;
      this.docAIData['volume'] = volumeValue;
      this.docAIData['chargeable'] = chargeableValue;

      this.selectedCargoInfoData['gWeight'] =
        gWeightValue && !isNaN(gWeightValue) ? this.formatWeightValue(gWeightValue) : '';

      this.selectedCargoInfoData['volume'] =
        volumeValue && !isNaN(volumeValue) ? this.formatWeightValue(volumeValue) : '';

      this.selectedCargoInfoData['chargeable'] =
        chargeableValue && !isNaN(chargeableValue) ? this.formatWeightValue(chargeableValue) : '';

      this.selectedCargoInfoData['packageCount'] =
        this.convertWordToNumber(
          (this.data.data[3].keys.find((x) => x.key === 'packageCount')?.Value || '').split(' ')[0],
        ) || '';
      if (this.selectedCargoInfoData['packageCount'] == '') {
        this.selectedCargoInfoData['packageCount'] = this.defaults['DefaultPackageCount'] || '';
      }

      if (this.subPackageList.length > 0) {
        this.subPackageList.forEach((subPackage, index) => {
          this.initializeDimensions(index);
        });
      }

      this.populateSubPkgData();
      this.populateContainerData();
    },
    populateSubPkgData() {
      //console.log(this.data.data[3].keys)
      //populate Container section
      this.subPackageList = [];
      this.filteredPkgType = [];
      this.filteredItemCode = [];

      // Iterate through each item in the data array
      this.data.data[3].keys.forEach((item) => {
        const index = item.IndexSP;

        if (index === -1) {
          return; // Skip the current iteration
        }

        if (!this.subPackageList[index]) {
          this.subPackageList.push({
            id: `subpackage-${Date.now()}-${index}`,
            itemCode: '',
            packingListNumber: '',
            invoiceNumber: '',
            containerNumber: '',
            dimensions: {
              pkgLength: '0',
              pkgWidth: '0',
              pkgHeight: '0',
            },
            dimensionsFull: '',
            pkgRemarks: '',
            pkgQty: '',
            pkgType: '',
            grossWeight: '',
            measurement: '',
            grossPerPkg: '',
            netWeight: '',
            netPerPkg: '',
            totalVol: '',
            volPerPkg: '',
          });
        }
        switch (item.key) {
          case 'pkgQty':
            this.subPackageList[index].pkgQty = item.Value;
            break;
          case 'pkgType':
            const pkgtTypeSearch = this.searchList(this.cargoInfoData.pkgTypeList, 'Package Code');
            const pkgtTypeSearchName = this.searchList(this.cargoInfoData.pkgTypeList, 'Description');
            const bestMatchPkgType = this.bestMatch(pkgtTypeSearch, item.Value);
            const bestMatchPkgTypeName = this.bestMatch(pkgtTypeSearchName, item.Value);
            this.filteredPkgType[index] = bestMatchPkgTypeName.length > 0 ? bestMatchPkgTypeName : bestMatchPkgType;
            this.filteredPkgType[index].length > 0 ?
              (this.subPackageList[index].pkgType = this.filteredPkgType[index][0]['Package Code'])
            : null;
            break;
          case 'pkgLength':
            this.subPackageList[index].dimensions.pkgLength = this.formatWeightValue(
              item.Value.replace(/[^0-9.]/g, ''),
            );
            break;
          case 'pkgWidth':
            this.subPackageList[index].dimensions.pkgWidth = this.formatWeightValue(item.Value.replace(/[^0-9.]/g, ''));
            break;
          case 'pkgHeight':
            this.subPackageList[index].dimensions.pkgHeight = this.formatWeightValue(
              item.Value.replace(/[^0-9.]/g, ''),
            );
            break;
          case 'grossWeight':
            this.subPackageList[index].grossWeight = this.formatWeightValue(item.Value.replace(/[^0-9.]/g, ''));
            break;
          case 'netWeight':
            this.subPackageList[index].netWeight = this.formatWeightValue(item.Value.replace(/[^0-9.]/g, ''));
            break;
          case 'itemCode':
            const itemCodeSearch = this.searchList(this.cargoInfoData.itemCode, 'Product Code');
            const itemCodeSearchName = this.searchList(this.cargoInfoData.itemCode, 'Description');
            const bestMatchItemCode = this.bestMatch(itemCodeSearch, item.Value);
            const bestMatchItemCodeName = this.bestMatch(itemCodeSearchName, item.Value);
            this.filteredItemCode[index] = bestMatchItemCodeName.length > 0 ? bestMatchItemCodeName : bestMatchItemCode;
            this.filteredItemCode[index].length > 0 ?
              (this.subPackageList[index].itemCode = this.filteredItemCode[index][0]['Product Code'])
            : null;
            break;
          case 'packingListNumber':
            this.subPackageList[index].packingListNumber = item.Value;
            break;
          case 'invoiceNumber':
            this.subPackageList[index].invoiceNumber = item.Value;
            break;
          case 'containerNumber':
            this.subPackageList[index].containerNumber = item.Value;
            break;
          case 'pkgRemarks':
            this.subPackageList[index].pkgRemarks = item.Value;
            break;
        }
        if (this.subPackageList && this.subPackageList.length > index) {
          this.initializeDimensions(index);
          this.calculateAllInput(index);
        }
      });
    },
    populateContainerData() {
      // console.log(this.data.data[3].keys)
      //populate Container section
      this.containerList = [];
      this.filteredContSize = [];
      this.filteredContType = [];
      this.filteredHandlingType = [];
      this.filteredDeliveryType = [];
      this.filteredSendTo = [];
      this.filteredStatus = [];
      this.filteredContSocCoc = [];

      // Iterate through each item in the data array
      this.data.data[3].keys.forEach((item) => {
        const index = item.Index;
        if (index === -1) {
          return; // Skip the current iteration
        }

        if (!this.containerList[index]) {
          this.containerList.push({
            id: `container-${Date.now()}-${index}`,
            contNo: '',
            sealNo: '',
            contSize: '',
            contType: '',
            handlingType: '',
            deliveryType: '',
            sendTo: '',
            status: '',
            remarks: '',
            contWeight: '',
            contSocCoc: '',
            selected: false,
          });
        }
        switch (item.key) {
          case 'contNo':
            this.containerList[index].contNo = item.Value;
            break;
          case 'sealNo':
            this.containerList[index].sealNo = item.Value;
            break;
          case 'contSize':
            // Extract numeric value for size (e.g., "40" from "40HC")
            const contSizeValue = item.Value.match(/\d+/)?.[0];

            const contSizeSearch = this.searchList(this.cargoInfoData.contSizeList, 'Size');
            this.filteredContSize[index] = this.bestMatch(contSizeSearch, contSizeValue);
            this.filteredContSize[index].length > 0 ?
              (this.containerList[index].contSize = this.filteredContSize[index][0]['Size'])
            : null;
            break;
          case 'contType':
            this.containerList[index].contType = this.resolveContainerType(item.Value, index);
            break;
          case 'handlingType': //list
            //console.log(this.cargoInfoData.contHandlingTypeList)
            const conthandlingTypeSearch = this.searchList(this.cargoInfoData.contHandlingTypeList, 'Handling Type');
            const conthandlingTypeSearchName = this.searchList(this.cargoInfoData.contHandlingTypeList, 'Description');
            const bestMatchHandlingType = this.bestMatch(conthandlingTypeSearch, item.Value);
            const bestMatchHandlingTypeName = this.bestMatch(conthandlingTypeSearchName, item.Value);
            this.filteredHandlingType[index] =
              bestMatchHandlingType.length > 0 ? bestMatchHandlingType : bestMatchHandlingTypeName;
            this.filteredHandlingType[index].length > 0 ?
              (this.containerList[index].handlingType = this.filteredHandlingType[index][0]['Handling Type'])
            : null;
            break;
          case 'deliveryType': //list
            //console.log(this.cargoInfoData.contDeliveryTypeList)
            const contDeliveryTypeSearch = this.searchList(this.cargoInfoData.contDeliveryTypeList, 'Delivery Type');
            const contDeliveryTypeSearchName = this.searchList(this.cargoInfoData.contDeliveryTypeList, 'Description');
            const bestMatchDeliveryType = this.bestMatch(contDeliveryTypeSearch, item.Value);
            const bestMatchDeliveryTypeName = this.bestMatch(contDeliveryTypeSearchName, item.Value);
            this.filteredDeliveryType[index] =
              bestMatchDeliveryType.length > 0 ? bestMatchDeliveryType : bestMatchDeliveryTypeName;
            this.filteredDeliveryType[index].length > 0 ?
              (this.containerList[index].deliveryType = this.filteredDeliveryType[index][0]['Delivery Type'])
            : null;
            break;
          case 'sendTo': //list
            // console.log(this.cargoInfoData.contSendToList)
            const contSendToSearch = this.searchList(this.cargoInfoData.contSendToList, 'Destination Code');
            const contSendToSearchName = this.searchList(this.cargoInfoData.contSendToList, 'Company');
            const bestMatchSendTo = this.bestMatch(contSendToSearch, item.Value);
            const bestMatchSendToName = this.bestMatch(contSendToSearchName, item.Value);
            this.filteredSendTo[index] = bestMatchSendTo.length > 0 ? bestMatchSendTo : bestMatchSendToName;
            this.filteredSendTo[index].length > 0 ?
              (this.containerList[index].sendTo = this.filteredSendTo[index][0]['Destination Code'])
            : null;
            break;
          case 'status': //list
            this.containerList[index].status = this.resolveContainerMasterValue(
              this.cargoInfoData.contStatusList,
              'Cont Status',
              item.Value,
              index,
              this.filteredStatus,
            );
            break;
          case 'remarks':
            this.containerList[index].remarks = item.Value;
            break;
          case 'contWeight':
            this.containerList[index].contWeight = this.formatWeightValue(item.Value.replace(/[^0-9.]/g, ''));
            break;
          case 'contSocCoc': //list
            // console.log(this.cargoInfoData.soccocTypeList)
            const contSocCocSearch = this.searchList(this.cargoInfoData.soccocTypeList, 'SOCCOC Type');
            const contSocCocSearchName = this.searchList(this.cargoInfoData.soccocTypeList, 'Description');
            const bestMatchContSocCoc = this.bestMatch(contSocCocSearch, item.Value);
            const bestMatchContSocCocName = this.bestMatch(contSocCocSearchName, item.Value);
            this.filteredContSocCoc[index] =
              bestMatchContSocCoc.length > 0 ? bestMatchContSocCoc : bestMatchContSocCocName;
            this.filteredContSocCoc[index].length > 0 ?
              (this.containerList[index].contSocCoc = this.filteredContSocCoc[index][0]['SOCCOC Type'])
            : null;
            break;
        }
      });

      const detectedStatuses = this.data.data[3].keys.filter((item) => item.key === 'status' && item.Value); // fill missing status for every row.
      const defaultStatus = detectedStatuses.length === 1 ? detectedStatuses[0].Value : '';

      if (defaultStatus) {
        this.containerList.forEach((container, index) => {
          if (!container.status) {
            container.status = this.resolveContainerMasterValue(
              this.cargoInfoData.contStatusList,
              'Cont Status',
              defaultStatus,
              index,
              this.filteredStatus,
            );
          }
        });
      }

      // console.log(this.containerList)
    },
    formatWeightValue: function (value) {
      // to format value to weight format with decimals and remove the separator to avoid issues
      if (value) {
        if (typeof value === 'number') {
          value = value.toString();
        }
        value = value.replaceAll(',', '');
        return this.$weightFormat(value).replaceAll(',', '');
      } else return this.$weightFormat('0');
    },
    convertWordToNumber: function (value) {
      // handle package count in word
      const smallNumbers = {
        ZERO: 0,
        ONE: 1,
        TWO: 2,
        THREE: 3,
        FOUR: 4,
        FIVE: 5,
        SIX: 6,
        SEVEN: 7,
        EIGHT: 8,
        NINE: 9,
        TEN: 10,
        ELEVEN: 11,
        TWELVE: 12,
        THIRTEEN: 13,
        FOURTEEN: 14,
        FIFTEEN: 15,
        SIXTEEN: 16,
        SEVENTEEN: 17,
        EIGHTEEN: 18,
        NINETEEN: 19,
      };

      const tens = {
        TWENTY: 20,
        THIRTY: 30,
        FORTY: 40,
        FIFTY: 50,
        SIXTY: 60,
        SEVENTY: 70,
        EIGHTY: 80,
        NINETY: 90,
      };

      const scales = {
        HUNDRED: 100,
        THOUSAND: 1000,
      };

      function parseWords(words) {
        let current = 0;
        let total = 0;

        words.forEach((word) => {
          if (smallNumbers[word] !== undefined) {
            current += smallNumbers[word];
          } else if (tens[word] !== undefined) {
            current += tens[word];
          } else if (scales[word] !== undefined) {
            current *= scales[word];
            total += current;
            current = 0;
          }
        });

        return (total + current).toString();
      }

      // Check if the value is already a number
      if (!isNaN(value)) {
        return value.toString(); // Convert number to string
      }

      // Process word-based numbers
      const words = value.toUpperCase().replace(/-/g, ' ').split(' ');
      const result = parseWords(words);

      return result || null;
    },
    resetFile() {
      this.file = '';
      this.embedSrc = '';

      this.pendingDocAIFile = null;
      this.pendingDocAIPreviewSrc = '';
      this.totalPages = 0;
      this.pageStartObj = null;
      this.pageEndObj = null;
      this.showPageSelector = false;
    },
    listDMetaphone(list) {
      let dMetaphoneEncoding = [];
      for (let i = 0; i < list.length; i++) {
        let name = list[i];
        let code = doubleMetaphone(name);
        dMetaphoneEncoding.push(code);
      }
      return dMetaphoneEncoding;
    },
    listSoundex(list) {
      let soundexEncoding = [];
      for (let i = 0; i < list.length; i++) {
        let name = list[i];
        let code = this.soundex(name);
        soundexEncoding.push(code);
      }
      return soundexEncoding;
    },
    soundex(name) {
      let s = [];
      let si = 1;
      let c;
      let mappings = '01230120022455012623010202';
      try {
        s[0] = name[0].toUpperCase();
        for (let j = 0; j < name.length; j++) {
          c = name[j].toUpperCase().charCodeAt(0) - 65;
          if (c >= 0 && c <= 25) {
            if (mappings[c] != '0') {
              if (mappings[c] != s[si - 1]) {
                s[si] = mappings[c];
                si++;
              }
              if (si > 3) {
                break;
              }
            }
          }
        }
        if (si <= 3) {
          while (si <= 3) {
            s[si] = 0;
            si++;
          }
        }
        s = s.join('');
      } catch (err) {
        s = '';
      }
      return s;
    },
    levenshtein(s, t) {
      if (s === t) {
        return 0;
      }
      var n = s.length,
        m = t.length;
      if (n === 0 || m === 0) {
        return n + m;
      }
      var x = 0,
        y,
        a,
        b,
        c,
        d,
        g,
        h,
        k;
      var p = new Array(n);
      for (y = 0; y < n; ) {
        p[y] = ++y;
      }

      for (; x + 3 < m; x += 4) {
        var e1 = t.charCodeAt(x);
        var e2 = t.charCodeAt(x + 1);
        var e3 = t.charCodeAt(x + 2);
        var e4 = t.charCodeAt(x + 3);
        c = x;
        b = x + 1;
        d = x + 2;
        g = x + 3;
        h = x + 4;
        for (y = 0; y < n; y++) {
          k = s.charCodeAt(y);
          a = p[y];
          if (a < c || b < c) {
            c = a > b ? b + 1 : a + 1;
          } else {
            if (e1 !== k) {
              c++;
            }
          }

          if (c < b || d < b) {
            b = c > d ? d + 1 : c + 1;
          } else {
            if (e2 !== k) {
              b++;
            }
          }

          if (b < d || g < d) {
            d = b > g ? g + 1 : b + 1;
          } else {
            if (e3 !== k) {
              d++;
            }
          }

          if (d < g || h < g) {
            g = d > h ? h + 1 : d + 1;
          } else {
            if (e4 !== k) {
              g++;
            }
          }
          p[y] = h = g;
          g = d;
          d = b;
          b = c;
          c = a;
        }
      }

      for (; x < m; ) {
        var e = t.charCodeAt(x);
        c = x;
        d = ++x;
        for (y = 0; y < n; y++) {
          a = p[y];
          if (a < c || d < c) {
            d = a > d ? d + 1 : a + 1;
          } else {
            if (e !== s.charCodeAt(y)) {
              d = c + 1;
            } else {
              d = c;
            }
          }
          p[y] = d;
          c = a;
        }
        h = d;
      }

      return h;
    },
    onTableDivInputReceived(e, arr, id, item, details) {
      if (!e) {
        this[arr][id][item] = null;
        return;
      }
      this[arr][id][item] = e[details];
    },

    // #region Select button of table box clicked
    shipperClicked: function (a, row) {
      this.selectedHeaderInfoData['selectedShipper'] = row;
      this.matchesList['selectedShipper'] = false;
      this.step1ErrMessage['selectedShipper'] = '';
      this.shipperSelected(row);
    },
    consigneeClicked: function (a, row) {
      this.selectedHeaderInfoData['selectedConsignee'] = row;
      this.matchesList['selectedConsignee'] = false;
      this.step1ErrMessage['selectedConsignee'] = '';
      this.consigneeSelected(row);
    },
    notifyPartyClicked: function (a, row) {
      this.selectedHeaderInfoData['selectedNotifyParty'] = row;
      this.matchesList['selectedNotifyParty'] = false;
      this.npartySelected(row);
    },
    billToClicked: function (a, row) {
      this.selectedHeaderInfoData['selectedBillTo'] = row;
      this.matchesList['selectedBillTo'] = false;
      this.step1ErrMessage['selectedBillTo'] = '';
    },
    vesselIDClicked: function (a, row) {
      this.selectedShippingInfoData['selectedVesselID'] = row;
      this.matchesList['selectedVesselID'] = false;
      this.step1ErrMessage['selectedVesselID'] = '';
    },
    shipCallClicked: async function (a, row) {
      this.selectedShippingInfoData['selectedShipCall'] = row;
      this.matchesList['selectedShipCall'] = false;
      this.step2ErrMessage['selectedShipCall'] = '';

      await this.shipCallNoSelected(row);
    },
    portOfLoadingClicked: function (a, row) {
      this.selectedShippingInfoData['selectedPortLoading'] = row;
      this.matchesList['selectedPortLoading'] = false;
      this.step1ErrMessage['selectedPortLoading'] = '';
    },
    portDischargeClicked: function (a, row) {
      this.selectedShippingInfoData['selectedPortDischarge'] = row;
      this.matchesList['selectedPortDischarge'] = false;
      this.step1ErrMessage['selectedPortDischarge'] = '';
    },
    shippingLineClicked: function (a, row) {
      this.selectedShippingInfoData['selectedShippingLine'] = row;
      this.matchesList['selectedShippingLine'] = false;
    },
    agentListClicked: function (a, row) {
      this.selectedShippingInfoData['selectedAgentList'] = row;
      this.matchesList['selectedAgentList'] = false;
      this.step1ErrMessage['selectedAgentList'] = '';
    },
    frtForwarderClicked: function (a, row) {
      this.selectedShippingInfoData['selectedFrtForwarder'] = row;
      this.matchesList['selectedFrtForwarder'] = false;
    },
    // #endregion

    openTableBox: function (e, item, from) {
      let rect = e.target.getBoundingClientRect();
      let isOpen = item[from];

      this.$nextTick(() => {
        this.$nextTick(() => {
          let top = rect.top + rect.height;
          let right = window.innerWidth - rect.right;

          item['tableStyling'] = {
            top: `${top}px`,
            right: `${right}px`,
          };
          item[from] = !isOpen;

          if (item[from]) {
            let winHeight = window.innerHeight / 2;

            if (top + winHeight > window.innerHeight - 10) {
              let diff = top + winHeight + 10 - window.innerHeight;
              item['tableStyling'] = {
                top: `${top - diff}px`,
                right: `${right + rect.width}px`,
              };
            }
          }
        });
      });
      this.matchesList[from] = true; // open the table box
    },
    addSubPackage: function () {
      this.subPackageList.push({
        id: Date.now(),
        itemCode: '',
        packingListNumber: '',
        invoiceNumber: '',
        containerNumber: '',
        dimensions: {
          pkgLength: this.$weightFormat('0'),
          pkgWidth: this.$weightFormat('0'),
          pkgHeight: this.$weightFormat('0'),
        },
        dimensionsFull: '',
        pkgRemarks: '',
        pkgQty: '',
        pkgType: '',
        grossWeight: this.$weightFormat('0'),
        measurement: '',
        grossPerPkg: this.$weightFormat('0'),
        netWeight: this.$weightFormat('0'),
        netPerPkg: this.$weightFormat('0'),
        totalVol: this.$weightFormat('0'),
        volPerPkg: this.$weightFormat('0'),
      });
      // console.log(this.subPackageList)
    },
    deleteSubPackage: async function (index) {
      var deleteSubPkg = await this.$showAlert(
        'warning',
        'Sub Package Delete',
        //`Are you sure you want to delete Package ${index + 1} ?`,
        `Are you sure you want to delete this package?`,
        [{ text: 'Delete' }, { text: 'Cancel' }],
      );

      if (deleteSubPkg === 'Delete') {
        this.subPackageList.splice(index, 1); // Remove the sub-package at the given index
      }
    },
    deleteSubPackageAll: async function () {
      var deleteSubPkg = await this.$showAlert(
        'warning',
        'Sub Package Delete',
        `Are you sure you want to delete all packages ?`,
        [{ text: 'Delete' }, { text: 'Cancel' }],
      );

      if (deleteSubPkg === 'Delete') {
        this.subPackageList.splice(0, this.subPackageList.length);
      }
    },
    addContainer: function () {
      this.containerList.push({
        id: Date.now(),
        contNo: '',
        sealNo: '',
        contSize: '',
        contType: '',
        handlingType: '',
        deliveryType: '',
        sendTo: '',
        status: '',
        remarks: '',
        contWeight: '',
        contSocCoc: '',
        selected: false,
      });
    },
    deleteContainer: async function (index) {
      var deleteCont = await this.$showAlert(
        'warning',
        'Container Delete',
        // `Are you sure you want to delete Container ${index + 1} ?`,
        `Are you sure you want to delete this container?`,
        [{ text: 'Delete' }, { text: 'Cancel' }],
      );

      if (deleteCont === 'Delete') {
        this.containerList.splice(index, 1); // Remove the container at the given index
      }
    },
    deleteContainerAll: async function () {
      var deleteCont = await this.$showAlert(
        'warning',
        'Container Delete',
        `Are you sure you want to delete all containers ?`,
        [{ text: 'Delete' }, { text: 'Cancel' }],
      );

      if (deleteCont === 'Delete') {
        this.containerList.splice(0, this.containerList.length);
      }
    },
    calculateAllInput: function (index) {
      let subPackageListClone = structuredClone(this.subPackageList); // using structured clone because object too deep, Vue cannot detect changes
      const dimensions = this.subPackageList[index]['dimensions'];
      const { pkgLength, pkgWidth, pkgHeight } = dimensions;

      if (pkgLength && pkgWidth && pkgHeight) {
        subPackageListClone[index].dimensionsFull = `${pkgLength} X ${pkgWidth} X ${pkgHeight}`; // put dimensions together

        // calculate Total Vol per Pakage
        const vol = parseFloat(pkgLength) * parseFloat(pkgWidth) * parseFloat(pkgHeight);
        subPackageListClone[index].volPerPkg = this.formatWeightValue(vol);

        // calculate Total Vol
        if (this.subPackageList[index]['pkgQty']) {
          subPackageListClone[index].totalVol = this.formatWeightValue(
            vol * parseInt(this.subPackageList[index]['pkgQty']),
          );
        } else {
          subPackageListClone[index].totalVol = this.$weightFormat('0');
        }

        subPackageListClone[index]['dimensions']['pkgHeight'] = this.formatWeightValue(
          this.subPackageList[index]['dimensions']['pkgHeight'],
        );
        subPackageListClone[index]['dimensions']['pkgLength'] = this.formatWeightValue(
          this.subPackageList[index]['dimensions']['pkgLength'],
        );
        subPackageListClone[index]['dimensions']['pkgWidth'] = this.formatWeightValue(
          this.subPackageList[index]['dimensions']['pkgWidth'],
        );

        if (
          subPackageListClone[index].volPerPkg.toString().split('.')[0].length > 7 ||
          subPackageListClone[index].totalVol.toString().split('.')[0].length > 7
        ) {
          this.$showAlert(
            'warning',
            'Volume (M³) cannot exceed 7 digits',
            'The calculated volume exceeds the limit of 7 digits. Please enter valid dimensions.',
            [{ text: 'OK' }],
          );
          // Reset dimensions if volume exceeds the limit
          subPackageListClone[index].dimensionsFull = '';
          subPackageListClone[index].dimensions.pkgHeight = this.$weightFormat('0');
          subPackageListClone[index].dimensions.pkgLength = this.$weightFormat('0');
          subPackageListClone[index].dimensions.pkgWidth = this.$weightFormat('0');
          subPackageListClone[index].volPerPkg = this.$weightFormat('0');
          subPackageListClone[index].totalVol = this.$weightFormat('0');
          this.subPackageList = subPackageListClone;
        }
      } else {
        subPackageListClone[index].dimensionsFull = ''; // Clear if any dimension is missing
        subPackageListClone[index].volPerPkg = this.$weightFormat('0');
        subPackageListClone[index].totalVol = this.$weightFormat('0');
      }

      // calculate Gross per Pakage
      if (this.subPackageList[index]['grossWeight'] && this.subPackageList[index]['pkgQty']) {
        subPackageListClone[index].grossPerPkg = this.formatWeightValue(
          parseFloat(this.subPackageList[index]['grossWeight']) / parseInt(this.subPackageList[index]['pkgQty']),
        );
      } else {
        subPackageListClone[index].grossPerPkg = this.$weightFormat('0');
      }

      // calculate Net per Pakage
      if (this.subPackageList[index]['netWeight'] && this.subPackageList[index]['pkgQty']) {
        subPackageListClone[index].netPerPkg = this.formatWeightValue(
          parseFloat(this.subPackageList[index]['netWeight']) / parseInt(this.subPackageList[index]['pkgQty']),
        );
      } else {
        subPackageListClone[index].netPerPkg = this.$weightFormat('0');
      }

      if (this.subPackageList[index]['grossWeight']) {
        subPackageListClone[index].grossWeight = this.formatWeightValue(`${this.subPackageList[index]['grossWeight']}`);
      }
      if (this.subPackageList[index]['netWeight']) {
        subPackageListClone[index].netWeight = this.formatWeightValue(this.subPackageList[index]['netWeight']);
      }

      this.subPackageList = subPackageListClone;
    },

    initializeDimensions: function (index) {
      const dimensionsFull = this.subPackageList[index]['dimensionsFull'];

      if (dimensionsFull) {
        const [pkgLength, pkgWidth, pkgHeight] = dimensionsFull.split(' X ').map(Number);
        this.$set(this.subPackageList[index]['dimensions'], 'pkgLength', String(pkgLength) || '0');
        this.$set(this.subPackageList[index]['dimensions'], 'pkgWidth', String(pkgWidth) || '0');
        this.$set(this.subPackageList[index]['dimensions'], 'pkgHeight', String(pkgHeight) || '0');
      }
    },
    editDetailsPopup: function (type) {
      //console.log(type);
      //console.log(this.shipperAddress);
      this.edittingDetails = true;
      this.detailsEditObject = {};

      let postcode = '';
      let state = '';
      let country = '';

      switch (type) {
        case 'shipper':
          this.detailsEditTitle = 'Shipper';
          this.detailsEditObject = {
            name: this.shipperAddress['name'],
            addressLine1: this.shipperAddress['add1'],
            addressLine2: this.shipperAddress['add2'],
            addressLine3: this.shipperAddress['add3'],
            addressLine4: this.shipperAddress['add4'],
            postcodeSearch: '',
            postcodeKey: 'PostCode',
            city: this.shipperAddress['city'],
            stateSearch: '',
            stateKey: 'State',
            countrySearch: '',
            countryKey: 'Country',
            attn: this.shipperAddress['attn'],
            tel: this.shipperAddress['tel'],
            fax: this.shipperAddress['fax'],
            gst: this.shipperAddress['gst'],
            postcode: this.shipperAddress['postcode'], //added for v2
            state: this.shipperAddress['state'], //added for v2
            country: this.shipperAddress['country'], //added for v2
          };

          postcode = this.shipperAddress['postcode'];
          state = this.shipperAddress['state'];
          country = this.shipperAddress['country'];
          break;
        case 'consignee':
          this.detailsEditTitle = 'Consignee';
          this.detailsEditObject = {
            name: this.consigneeAddress['name'],
            addressLine1: this.consigneeAddress['add1'],
            addressLine2: this.consigneeAddress['add2'],
            addressLine3: this.consigneeAddress['add3'],
            addressLine4: this.consigneeAddress['add4'],
            postcodeSearch: '',
            postcodeKey: 'PostCode',
            city: this.consigneeAddress['city'],
            stateSearch: '',
            stateKey: 'State',
            countrySearch: '',
            countryKey: 'Country',
            attn: this.consigneeAddress['attn'],
            tel: this.consigneeAddress['tel'],
            fax: this.consigneeAddress['fax'],
            gst: this.consigneeAddress['gst'],
            postcode: this.consigneeAddress['postcode'], //added for v2
            state: this.consigneeAddress['state'], //added for v2
            country: this.consigneeAddress['country'], //added for v2
          };

          postcode = this.consigneeAddress['postcode'];
          state = this.consigneeAddress['state'];
          country = this.consigneeAddress['country'];
          break;
        case 'nparty':
          this.detailsEditTitle = 'Notify Party';
          this.detailsEditObject = {
            name: this.notifyPartyAddress['name'],
            addressLine1: this.notifyPartyAddress['add1'],
            addressLine2: this.notifyPartyAddress['add2'],
            addressLine3: this.notifyPartyAddress['add3'],
            addressLine4: this.notifyPartyAddress['add4'],
            postcodeSearch: '',
            postcodeKey: 'PostCode',
            city: this.notifyPartyAddress['city'],
            stateSearch: '',
            stateKey: 'State',
            countrySearch: '',
            countryKey: 'Country',
            attn: this.notifyPartyAddress['attn'],
            tel: this.notifyPartyAddress['tel'],
            fax: this.notifyPartyAddress['fax'],
            gst: this.notifyPartyAddress['gst'],
            postcode: this.notifyPartyAddress['postcode'], //added for v2
            state: this.notifyPartyAddress['state'], //added for v2
            country: this.notifyPartyAddress['country'], //added for v2
          };

          postcode = this.notifyPartyAddress['postcode'];
          state = this.notifyPartyAddress['state'];
          country = this.notifyPartyAddress['country'];
          break;
      }

      this.detailsEditObject['postcodeItems'] = this.addressDetails['postcodeList'];
      this.detailsEditObject['stateItems'] = this.addressDetails['stateList'];
      this.detailsEditObject['countryItems'] = this.addressDetails['countryList'];

      this.$nextTick(() => {
        // this.detailsEditObject['postcodeSearch'] = postcode;
        // this.detailsEditObject['stateSearch'] = state;
        // this.detailsEditObject['countrySearch'] = country;
      });
    },
    showHideAddress: function (type) {
      switch (type) {
        case 'shipper':
          this.showShipperAddress = !this.showShipperAddress;
          break;
        case 'consignee':
          this.showConsigneeAddress = !this.showConsigneeAddress;
          break;
        case 'nparty':
          this.showNPartyAddress = !this.showNPartyAddress;
          break;
      }
    },
    detailsEditSave: function (type) {
      if (type == 'save') {
        switch (this.detailsEditTitle) {
          case 'Shipper':
            this.shipperAddress['name'] = this.detailsEditObject['name'];
            this.shipperAddress['add1'] = this.detailsEditObject['addressLine1'];
            this.shipperAddress['add2'] = this.detailsEditObject['addressLine2'];
            this.shipperAddress['add3'] = this.detailsEditObject['addressLine3'];
            this.shipperAddress['add4'] = this.detailsEditObject['addressLine4'];
            this.shipperAddress['postcode'] = this.detailsEditObject['postcode'];
            this.shipperAddress['city'] = this.detailsEditObject['city'];
            this.shipperAddress['state'] = this.detailsEditObject['state'];
            this.shipperAddress['country'] = this.detailsEditObject['country'];
            this.shipperAddress['attn'] = this.detailsEditObject['attn'];
            this.shipperAddress['tel'] = this.detailsEditObject['tel'];
            this.shipperAddress['fax'] = this.detailsEditObject['fax'];
            this.shipperAddress['gst'] = this.detailsEditObject['gst'];
            this.showShipperAddress = true;
            break;
          case 'Consignee':
            this.consigneeAddress['name'] = this.detailsEditObject['name'];
            this.consigneeAddress['add1'] = this.detailsEditObject['addressLine1'];
            this.consigneeAddress['add2'] = this.detailsEditObject['addressLine2'];
            this.consigneeAddress['add3'] = this.detailsEditObject['addressLine3'];
            this.consigneeAddress['add4'] = this.detailsEditObject['addressLine4'];
            this.consigneeAddress['postcode'] = this.detailsEditObject['postcode'];
            this.consigneeAddress['city'] = this.detailsEditObject['city'];
            this.consigneeAddress['state'] = this.detailsEditObject['state'];
            this.consigneeAddress['country'] = this.detailsEditObject['country'];
            this.consigneeAddress['attn'] = this.detailsEditObject['attn'];
            this.consigneeAddress['tel'] = this.detailsEditObject['tel'];
            this.consigneeAddress['fax'] = this.detailsEditObject['fax'];
            this.consigneeAddress['gst'] = this.detailsEditObject['gst'];
            this.showConsigneeAddress = true;
            break;
          case 'Notify Party':
            this.notifyPartyAddress['name'] = this.detailsEditObject['name'];
            this.notifyPartyAddress['add1'] = this.detailsEditObject['addressLine1'];
            this.notifyPartyAddress['add2'] = this.detailsEditObject['addressLine2'];
            this.notifyPartyAddress['add3'] = this.detailsEditObject['addressLine3'];
            this.notifyPartyAddress['add4'] = this.detailsEditObject['addressLine4'];
            this.notifyPartyAddress['postcode'] = this.detailsEditObject['postcode'];
            this.notifyPartyAddress['city'] = this.detailsEditObject['city'];
            this.notifyPartyAddress['state'] = this.detailsEditObject['state'];
            this.notifyPartyAddress['country'] = this.detailsEditObject['country'];
            this.notifyPartyAddress['attn'] = this.detailsEditObject['attn'];
            this.notifyPartyAddress['tel'] = this.detailsEditObject['tel'];
            this.notifyPartyAddress['fax'] = this.detailsEditObject['fax'];
            this.notifyPartyAddress['gst'] = this.detailsEditObject['gst'];
            this.showNPartyAddress = true;
            break;
        }
      }

      this.edittingDetails = false;
      this.detailsEditObject = null;
    },
    applyDocAIAddress: function (docAIAddress, targetAddress, showFlagKey) {
      if (!docAIAddress || !targetAddress) return;

      const text = docAIAddress.replace(/\s+/g, ' ').trim();

      const parts = [];
      let i = 0;

      while (i < text.length && parts.length < 4) {
        parts.push(text.substring(i, i + 40));
        i += 40;
      }

      targetAddress.add1 = parts[0] || '';
      targetAddress.add2 = parts[1] || '';
      targetAddress.add3 = parts[2] || '';
      targetAddress.add4 = parts[3] || '';

      //  force re-render for v-if blocks
      this[showFlagKey] = false;
      this.$nextTick(() => {
        this[showFlagKey] = true;
      });
    },
    goToStep: function (index) {
      for (let i = 0; i <= index; i++) {
        this.validateField(i);
        //console.log('validating step ', i);

        if (i >= 2) {
          this.validateDates();
        }
      }

      this.currentStep = index;
    },
    setUnit(field, value) {
      this.selectedShippingInfoData[field] = value;
    },
    shipCallNoSelected: async function (e, p) {
      if (!e) return;

      const resetChangedFields = () => {
        Object.keys(this.shipCallChangedFields).forEach((field) => {
          this.shipCallChangedFields[field] = false;
        });
      };
      const markChanged = (field, changed) => {
        if (changed) {
          this.shipCallChangedFields[field] = true;
        }
      };

      const getShipCallDate = (value) => {
        if (!value) return null;
        if (value instanceof Date) return new Date(value);

        return this.parseShipCallDate(value);
      };

      const dateValue = (value) => {
        if (!value) return null;

        const date = new Date(value);
        return isNaN(date.getTime()) ? null : date.getTime();
      };

      const textValue = (value) => {
        return value == null ? '' : String(value).trim();
      };

      //resetChangedFields();

      const vessel =
        this.shippingInfoData['vesselIDList'].find((v) => {
          return v['Vessel ID']?.trim() === e['Vessel ID']?.trim();
        }) ||
        (e['Vessel ID'] ?
          {
            'Vessel ID': e['Vessel ID'],
            'Vessel Name': e['Vessel Name'] || '',
            'ID - Name': `${e['Vessel ID']} - ${e['Vessel Name'] || ''}`,
          }
        : null);

      const oldVesselID = this.selectedShippingInfoData['selectedVesselID']?.['Vessel ID'];

      if (vessel) {
        markChanged('selectedVesselID', textValue(oldVesselID) !== textValue(vessel['Vessel ID']));

        this.selectedShippingInfoData['selectedVesselID'] = vessel;
        this.step2ErrMessage['selectedVesselID'] = '';
      }

      markChanged('voyNo', textValue(this.selectedShippingInfoData['voyNo']) !== textValue(e['Voy No']));

      this.selectedShippingInfoData['voyNo'] = e['Voy No'] || '';

      this.step2ErrMessage['selectedShipCall'] = '';
      this.step2ErrMessage['voyNo'] = '';

      const shipCallETA = getShipCallDate(e['ETA']);

      if (this.pageType === 'Import') {
        markChanged('portOfDischargeETA', dateValue(this.selectedShippingInfoData['portOfDischargeETA']) !== dateValue(shipCallETA));

        this.selectedShippingInfoData['portOfDischargeETA'] = shipCallETA;
        this.step3ErrMessage['portOfDischargeETA'] = '';
        return;
      }

      markChanged('portOfLoadingETA', dateValue(this.selectedShippingInfoData['portOfLoadingETA']) !== dateValue(shipCallETA));

      this.selectedShippingInfoData['portOfLoadingETA'] = shipCallETA;
      this.step3ErrMessage['portOfLoadingETA'] = '';

      if (!shipCallETA || !this.selectedHeaderInfoData['selectedCargoType']) {
        markChanged('closingTime', dateValue(this.selectedShippingInfoData['closingTime']) !== null);

        this.selectedShippingInfoData['closingTime'] = null;
        return;
      }

      const cargoType = this.selectedHeaderInfoData['selectedCargoType']['Cargo Type'] || '';
      const settingsKey = cargoType[0] === 'F' ? 'CLOSINGDATE_FCL_HRS' : 'CLOSINGDATE_LCL_HRS';

      try {
        this.$fullscreenLoading('Calculating Closing Time');

        const settings = await this.$apiCallV2(
          `/FreightForwarding/GetSettingsSystem?settingsItem=${encodeURIComponent(settingsKey)}`,
        );

        const closingHours = Number(settings?.[0]?.['SettingsValue']);

        if (!Number.isFinite(closingHours)) {
          markChanged('closingTime', dateValue(this.selectedShippingInfoData['closingTime']) !== null);

          this.selectedShippingInfoData['closingTime'] = null;
          return;
        }

        const closingDate = new Date(shipCallETA);
        closingDate.setHours(closingDate.getHours() - closingHours);

        markChanged('closingTime', dateValue(this.selectedShippingInfoData['closingTime']) !== dateValue(closingDate));

        this.selectedShippingInfoData['closingTime'] = closingDate;
        this.step3ErrMessage['closingTime'] = '';
      } catch (error) {
        console.error('Failed to calculate closing time:', error);
        this.selectedShippingInfoData['closingTime'] = null;
      } finally {
        this.$fullscreenLoading();
      }
    },
    // shipCallNoSelected: async function (e, p) {
    //   //SCN selected -> Vessel ID, Voyage No, Import POD ETA / Export POL ETD, Export Closing Time
    //   if (!e) return;

    //   const vessel = this.shippingInfoData['vesselIDList'].find((v) => {
    //     return v['Vessel ID']?.trim() === e['Vessel ID']?.trim();
    //   });

    //   if (vessel) {
    //     this.selectedShippingInfoData['selectedVesselID'] = vessel;
    //     this.step1ErrMessage['selectedVesselID'] = '';
    //   }

    //   this.selectedShippingInfoData['voyNo'] = e['Voy No'] || '';
    //   this.step1ErrMessage['selectedShipCall'] = '';
    //   this.step1ErrMessage['voyNo'] = '';

    //   if (this.pageType == 'Import') {
    //       this.selectedShippingInfoData['portOfDischargeETA'] = e['ETA'] ? this.parseShipCallDate(e['ETA']) : null;
    //   } else {
    //     console.log(e['ETA'])
    //     console.log(e['ETD'])

    //     this.selectedShippingInfoData['portOfLoadingETA'] = e['ETD'] ? this.parseShipCallDate(e['ETD']) : null;

    //     if (
    //       this.selectedHeaderInfoData['selectedTransMode'] &&
    //       this.selectedHeaderInfoData['selectedCargoType'] &&
    //       this.selectedShippingInfoData['portOfLoadingETA']
    //     ) {
    //       console.log('test')
    //       let settingsKey = 'CLOSINGDATE_';
    //       settingsKey += this.selectedHeaderInfoData['selectedCargoType']['Cargo Type'][0] == 'F' ? 'FCL_' : 'LCL_';
    //       settingsKey += this.selectedHeaderInfoData['selectedTransMode']['Description'] == 'by Air' ? 'AIR' : 'HRS';

    //       this.$fullscreenLoading('Calculating Closing Time');
    //       const hours = await this.$apiCallV2(`/FreightForwarding/GetSettingsSystem?settingsItem=${settingsKey}`);
    //       console.log(hours)
    //       this.$fullscreenLoading();

    //       const closingDate = new Date(this.selectedShippingInfoData['portOfLoadingETA']);
    //       closingDate.setHours(closingDate.getHours() - Number(hours[0]['SettingsValue']));
    //       this.selectedShippingInfoData['closingTime'] = closingDate;
    //       console.log(closingDate)

    //     }
    //   }
    // },
    parseShipCallDate: function (value) {
      if (!value) return null;

      const text = String(value).trim();

      // Handles: 2026-06-25 2200
      const match = text.match(/^(\d{4}-\d{2}-\d{2})\s+(\d{2})(\d{2})$/);

      if (match) {
        return new Date(`${match[1]}T${match[2]}:${match[3]}:00`);
      }

      // Handles already-normal formats like: 2026-06-25 22:00
      const normalized = text.replace(' ', 'T');
      const date = new Date(normalized);

      return isNaN(date.getTime()) ? null : date;
    },
    resolveContainerMasterValue: function (list, key, value, index, filteredList) {
      //logic for container status matching
      const normalize = (text) =>
        String(text || '')
          .replace(/[^a-zA-Z0-9/]/g, '')
          .toUpperCase();
      const exact = list.filter((x) => normalize(x[key]) === normalize(value));

      filteredList[index] = exact.length > 0 ? exact : this.bestMatch(this.searchList(list, key), value);
      return filteredList[index]?.[0]?.[key] || '';
    },
    resolveContainerType: function (value, index) {
      //logic for container type matching
      const raw = String(value || '').toUpperCase();
      const code = raw.replace(/^(20|40|45|48|53)/, '').replace(/[^A-Z0-9]/g, '');

      const candidates = [];

      if (code.includes('96') || code.includes('HC') || code.includes('HQ')) {
        candidates.push('HC', 'HQ', 'HIGH CUBE');
      }

      candidates.push(code, raw);

      for (const candidate of candidates) {
        const exact = this.cargoInfoData.contTypeList.filter((x) => {
          return String(x['Cont Type']).trim().toUpperCase() === candidate;
        });

        if (exact.length > 0) {
          this.filteredContType[index] = exact;
          return exact[0]['Cont Type'];
        }

        const matches = this.bestMatch(this.searchList(this.cargoInfoData.contTypeList, 'Description'), candidate);
        if (matches.length > 0) {
          this.filteredContType[index] = matches;
          return matches[0]['Cont Type'];
        }
      }

      this.filteredContType[index] = [];
      return '';
    },
    toggleContainerSelectAll() {
      this.containerList.forEach((container) => {
        this.$set(container, 'selected', this.containerSelectAll);
      });
    },

    syncContainerSelectAll() {
      this.containerSelectAll =
        this.containerList.length > 0 && this.containerList.every((container) => container.selected);
    },

    clearSelectedContainers() {
      this.containerSelectAll = false;
      this.containerList.forEach((container) => {
        this.$set(container, 'selected', false);
      });
    },

    getContainerBatchSelectedObject() {
      if (!this.containerBatchField || !this.containerBatchValue) return null;

      const config = this.containerBatchFieldConfig[this.containerBatchField];
      const list = this.cargoInfoData[config.source] || [];

      if (typeof this.containerBatchValue === 'object') return this.containerBatchValue;

      return list.find((x) => x[config.valueKey] === this.containerBatchValue) || null;
    },

    getContainerBatchValue() {
      if (!this.containerBatchField) return '';

      const config = this.containerBatchFieldConfig[this.containerBatchField];

      if (config.type === 'dropdown' || config.type === 'inputQuery') {
        return this.containerBatchValue ? this.containerBatchValue[config.valueKey] || '' : '';
      }

      if (this.containerBatchField === 'contWeight') {
        return this.formatWeightValue(String(this.containerBatchValue || '').replace(/[^0-9.]/g, ''));
      }

      return this.containerBatchValue || '';
    },

    applyContainerBatchEdit() {
      if (!this.containerBatchField || this.selectedContainerCount === 0) return;

      const value = this.getContainerBatchValue();

      this.containerList.forEach((container) => {
        if (!container.selected) return;
        this.$set(container, this.containerBatchField, value);
      });
    },

    deleteSelectedContainers: async function () {
      const deleteCont = await this.$showAlert(
        'warning',
        'Container Delete',
        `Are you sure you want to delete ${this.selectedContainerCount} selected containers?`,
        [{ text: 'Delete' }, { text: 'Cancel' }],
      );

      if (deleteCont !== 'Delete') return;

      this.containerList = this.containerList.filter((container) => !container.selected);
      this.containerSelectAll = false;
    },
  },
  async mounted() {
    let pageRoute = this.$route.path.split('/');
    this.pageType = pageRoute[pageRoute.length - 1].split(' ')[0]; // check page type (Import/Export)

    this.CoRefname = await this.$apiCallV2('/Login/GetDetails').then((result) => {
      return result['CoRefname'];
    });

    this.shipperSoundexEncoding = this.listSoundex(this.headerInfoData.shipper.map((a) => a['Shipper Name']));
    this.consigneeSoundexEncoding = this.listSoundex(this.headerInfoData.consignee.map((a) => a['Consignee Name']));
    this.notifyPartySoundexEncoding = this.listSoundex(
      this.headerInfoData.notifyParty.map((a) => a['Notify Party Name']),
    );
    this.billToSoundexEncoding = this.listSoundex(this.headerInfoData.billTo.map((a) => a['Customer Name']));
    this.polPortCodeSoundexEncoding = this.listSoundex(
      this.shippingInfoData.portOfLoading.map((a) => a['Port Location']),
    );
    this.podPortCodeSoundexEncoding = this.listSoundex(
      this.shippingInfoData.portOfDischarge.map((a) => a['Port Location']),
    );
    this.shipperDMetaphoneEncoding = this.listDMetaphone(this.headerInfoData.shipper.map((a) => a['Shipper Name']));
    this.consigneeDMetaphoneEncoding = this.listDMetaphone(
      this.headerInfoData.consignee.map((a) => a['Consignee Name']),
    );
    this.notifyPartyDMetaphoneEncoding = this.listDMetaphone(
      this.headerInfoData.notifyParty.map((a) => a['Notify Party Name']),
    );
    this.billToDMetaphoneEncoding = this.listDMetaphone(this.headerInfoData.billTo.map((a) => a['Customer Name']));
    this.polPortCodeDMetaphoneEncoding = this.listDMetaphone(
      this.shippingInfoData.portOfLoading.map((a) => a['Port Location']),
    );
    this.podPortCodeDMetaphoneEncoding = this.listDMetaphone(
      this.shippingInfoData.portOfDischarge.map((a) => a['Port Location']),
    );
    this.shipCallNoSoundexEncoding = this.listSoundex(
      this.shippingInfoData.shipCallNoList.map((a) => a['Ship Call No']),
    );

    this.shipCallNoDMetaphoneEncoding = this.listDMetaphone(
      this.shippingInfoData.shipCallNoList.map((a) => a['Ship Call No']),
    );

    window.goToStep = (step) => {
      this.currentStep = step;
      // this.$nextTick(() => {
      //   document.querySelector('.stepDisplay')?.scrollIntoView({ behavior: 'smooth' });
      // });
      this.validateField();
      if (step == 2) {
        this.validateDates();
      }
      const allButtons = document.querySelectorAll('button');
      allButtons.forEach((btn) => {
        if (btn.innerText.trim() === 'Back') btn.click();
      });
    };
  },
};
</script>

<style scoped>
.jobFormMain {
  width: 100%;
  height: 100%;
  font-size: 12px;
  display: flex;
  flex-direction: column;
}

.formDisplay {
  width: 100%;
}

.row {
  display: flex;
  width: 100%;
}

.noteToUser {
  font-size: 15px;
  margin-left: 10px;
}

.pdfDisplayColumn {
  width: 49%;
  padding: 7px 5px 5px 5px;
  box-sizing: border-box;
}

.formDisplayColumn {
  width: 51%;
  padding: 7px 10px 10px 10px;
  box-sizing: border-box;
}

.pdf-display-condition {
  height: 100%;
  width: 100%;
  padding: 0;
  margin: 0 0 0 10px;
  overflow: hidden;
}

.pdf-display {
  height: 100%;
  width: 100%;
  margin: 0;
  border: 0px;
  /* zoom: scale(0.49);
    -webkit-transform: scale(1);
    -moz-transform: scale(1);
    -ms-transform: scale(1);
    transform: scale(1);
    -o-transform: scale(1);
    -o-transform-origin: 0 0;
    -moz-transform-origin: 0 0;
    -webkit-transform-origin: 0 0;
    transform-origin: 0 0;*/
  transform: none;
}

.stepDisplay {
  height: 72vh;
  overflow: auto;
}

.table-wrapper > div:not(.button-group) {
  display: table;
  width: 100%;
  border-spacing: 5px;
}

.form-step-0-group-1 {
  margin-bottom: 20px;
}

.form-step-2-group-1 {
  height: 1px;
}

.form-content-row {
  display: table-row;
  width: 100%;
  clear: both;
  margin: 2px 0px;
  background: whitesmoke;
}

.form-content-row-address {
  display: table-row;
  clear: both;
  margin: 2px 0px;
  background: whitesmoke;
}

.form-content-row > div,
.form-content-row > p {
  display: inline-block;
  margin: 0;
  padding: 3px;
  height: auto;
  min-height: 0;
  box-sizing: border-box;
}

.form-content-key-columns {
  float: left;
  display: table-column;
  width: 18%;
  padding: 5px 15px;
}

.form-masterfile-detected-columns {
  float: left;
  display: table-column;
  width: 22%;
  height: 60px;
}

.form-masterfile-submitted-columns {
  float: left;
  display: table-column;
  width: 42%;
  height: 60px;
}

.form-masterfile-long-submitted-columns {
  float: left;
  display: table-column;
  width: 60%;
  height: 60px;
}

.form-masterfile-only-submitted-columns {
  float: left;
  display: table-column;
  width: 60%;
  height: 60px;
}

.form-collision-number-columns {
  float: left;
  display: table-column;
  width: 18%;
  height: 60px;
}

.form-masterfile-detected-columns > div > p,
.form-masterfile-submitted-columns > div > p,
.form-collision-number-columns > div > p {
  font-size: 10.5px;
}

.form-collision-number-columns > div > p {
  font-size: 10.5px;
  padding: 5px;
}

.matchFound {
  background: lawngreen;
  display: inline-block;
  padding: 5px;
}

.matchNotFound {
  background: #b3c4ff;
  display: inline-block;
  padding: 5px;
}

.form-content-columns {
  float: left;
  display: table-column;
  width: 70%;
  padding: 0px 20px 0px 5px;
}

.errorMessage {
  color: red;
  font-size: 11px;
}

.checkoutTable {
  display: table;
  width: 100%;
}

.tableHeaderColumn {
  font-size: 14px;
  background: #44475c;
  color: white;
  width: 100%;
  padding: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-sizing: border-box;
}

.tableBody {
  border: 1px solid #ccc;
  padding: 10px;
}

.tableBody .tableItems {
  padding: 5px;
}

.tableBody:nth-child(2n) {
  background: #d4d8f9;
}

.tableBody2 {
  border: 1px solid #ccc;
  padding: 10px;
}

.tableBody2 .tableItems {
  padding: 5px;
}

.tableBody2:nth-child(2n) {
  background: #d4d8f9;
}

.contTableCol0 {
  float: left;
  width: 4%;
  display: table-column;
}

.divisionLabel {
  font-size: 1.17em;
  margin: 0em 0em 1em 0em;
  font-weight: bold;
  border-bottom: solid 1px;
}

.divisionLabelSmall {
  display: inline-block;
  font-size: 1.17em;
  margin: 0.5em 7em 0em 0em;
  font-weight: bold;

  width: 200px;
}

label {
  margin-bottom: 0;
}

.compulsory {
  font-weight: normal;
  color: #ff0000;
}

.error {
  font-weight: lighter;
  color: #ff0000;
  margin: 0px 0px 5px 235px;
  padding: 0;
}

input {
  width: 60%;
}

.button-group {
  position: fixed;
  right: 65px;
  bottom: 21px;
  padding: 10px;
}

.prevButton {
  background: white;
  border: 3.5px solid darkred;
  color: darkred;
  text-transform: uppercase;
  width: 160px;
  border-radius: 20px;
  box-shadow: inset 0 0 0 2px white;
  margin-right: 20px;
}

.jfCompNextButton,
.submitButton {
  background: white;
  border: 3.5px solid darkcyan;
  color: darkcyan;
  text-transform: uppercase;
  width: 160px;
  border-radius: 20px;
  box-shadow: inset 0 0 0 2px white;
}

.jfCompNextButton:disabled,
.jfCompNextButton[disabled] {
  border: 3.5px solid gray;
  color: gray;
  opacity: 60%;
}

.status-container {
  border: 1px solid rgb(200, 200, 200);
  padding: 3px;
  margin-right: 5px;
  margin-left: 3px;
}

.outstanding-prog {
  display: flex;
  border: 1px solid rgb(200, 200, 200);
  position: relative;
  width: calc(100% - 2px);
  margin-left: 3px;
  padding: 3px;
}

.outstanding-prog .progress-ind {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: 50%;
  background-color: rgba(51, 231, 255, 0.5);
}

.tablebox-container {
  padding: 5px;
  border-radius: 5px;
  background-color: white;
}

.collision-table {
  position: fixed;
  z-index: 151;
  width: 50vw;
  height: 50vh;
  padding: 5px;
  background-color: white;
  border: 1px solid gray;
}

.collision-table-background {
  z-index: 149;
  width: 100vw;
  height: 100vh;
  top: 0;
  left: 0;
  position: fixed;
}

.center-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
}

.button-size {
  padding: 1px 5px;
  font-size: 10px;
  height: 70%;
  width: 80%;
  border: 1px solid #ccc;
  border-radius: 4px;
}

.img-size {
  width: 16px;
  height: 16px;
}

.add-package-btn {
  margin-bottom: 10px;
  padding: 8px 16px;
  background-color: #4caf50;
  color: white;
  border: none;
  cursor: pointer;
  width: 100%;
  text-align: center;
}

.deleteall-package-btn {
  margin-bottom: 10px;
  padding: 8px 16px;
  background-color: #f44336;
  color: white;
  border: none;
  cursor: pointer;
  width: 30%;
  text-align: center;
}

.delete-package-btn {
  margin-left: 10px;
  padding: 4px 8px;
  background-color: #f44336;
  color: white;
  border: none;
  cursor: pointer;
}

.add-package-btn:hover,
.delete-package-btn:hover {
  opacity: 0.8;
}

.delete-button-container {
  display: flex;
}

.add-button-container {
  padding: 5px;
  display: flex;
  justify-content: center;
  /* justify-content: flex-end;  */
  margin-bottom: 10px;
  gap: 5px;
}

.content-details {
  width: 100%;
  height: 100%;
  padding: 5px;
  overflow: visible;
  border-top-right-radius: 5px;
  border-top-left-radius: 5px;
}

.dimension-input {
  display: flex;
  align-items: center;
}

.dimension-input > div {
  margin: 0 5px; /* Adjusts spacing between inputs */
}

.custom-background {
  background: linear-gradient(90deg, #535cda 0%, #6673f2 52%);
  color: #ffffff;
  border-radius: 3px 3px 0 0;
  box-shadow: inset 0 -1px 0 rgba(255, 255, 255, 0.25);
}

.custom-background > div {
  min-height: 30px;
  height: 30px;
  padding-top: 2px;
  padding-bottom: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  box-sizing: border-box;
  font-weight: 700;
  white-space: nowrap;
}

.custom-background .form-collision-number-columns {
  padding-right: 10px;
}

.comparison-header-cell > span,
.comparison-header-cell > * {
  flex: 0 0 auto;
}
.no-ai-value {
  color: #8a6d1d;
  background: #fff7dd;
  border-radius: 4px;
  padding: 4px 6px;
  font-style: italic;
}
.cancel-button {
  position: absolute;
  bottom: 10px;
  right: 10px;
  padding: 5px 10px;
  background-color: #f44336;
  color: white;
  border: none;
  cursor: pointer;
}

.cancel-button:hover {
  background-color: #d32f2f;
}

.address {
  height: 100%;
  border-radius: 3px;
  border: 1px solid rgba(128, 128, 128, 0.5);
  min-height: 60px;
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  margin-bottom: 5px;
}

.edit-area {
  position: absolute;
  top: 3px;
  right: 3px;
  display: flex;
  align-items: center;
}

.button {
  padding: 1px 10px;
}

.img {
  transition: 0.3s;
  cursor: pointer;
}

.img-hide {
  transform: scaleY(1);
}

.img-show {
  transform: scaleY(-1);
}

.address-block {
  overflow-y: auto;
  overflow-x: hidden;
  padding: 5px;
  padding-right: 58px;
  flex: 1;
  min-height: 0;
}

.address-collapse {
  max-height: 28px;
  min-height: 28px;
  justify-content: center;
  padding-left: 5px;
}

.edit-address-popup {
  position: absolute;
  left: 0;
  top: 0;
  background-color: rgba(128, 128, 128, 0.5);
  height: 100%;
  width: 100%;
  z-index: 999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.edit-content {
  width: 90%;
  max-width: 500px;
  background-color: white;
  padding: 10px;
}
.title {
  font-weight: bold;
  font-size: 13px;
  margin-bottom: 5px;
}

.button-section {
  display: flex;
  justify-content: flex-end;
}
.button {
  margin-left: 5px;
}

.step-fade-enter-active,
.step-fade-leave-active {
  transition: opacity 0.2s ease;
}

.step-fade-enter-from {
  opacity: 0;
}

.step-fade-enter-to {
  opacity: 1;
}

.step-fade-leave-from {
  opacity: 1;
}

.step-fade-leave-to {
  opacity: 0;
}

.pdf-guidance {
  background: #f5f7ff;
  border-left: 4px solid #535cda;
  padding: 6px 10px;
  font-size: 12px;
  margin-bottom: 6px;
}

.unit-toggle {
  display: flex;
  flex-shrink: 0;
  border: 1px solid #b3c4ff;
  border-radius: 4px;
  overflow: hidden;
}

.toggle-btn {
  padding: 0 8px;
  height: 28px;
  font-size: 11px;
  border: none;
  background-color: #f0f0f0;
  color: #555;
  cursor: pointer;
  transition:
    background-color 0.15s,
    color 0.15s;
}

.toggle-btn.first-child {
  border-right: 1px solid rgba(0, 0, 0, 0.15);
}

.toggle-btn.active {
  background-color: #6e6eff;
  color: #fff;
  font-weight: 600;
}

.toggle-btn:hover:not(:disabled):not(.active) {
  background-color: #b3c4ff;
  color: #fff;
}

.free-input-group {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
}

.free-input-group > .unit-toggle {
  display: flex;
  flex-shrink: 0;
  border: 1px solid #b3c4ff;
  border-radius: 4px;
  overflow: hidden;
}

.free-input-group > .unit-toggle.disabled {
  opacity: 0.5;
  pointer-events: none;
}

.free-input-group > .unit-toggle .toggle-btn {
  padding: 0 8px;
  height: 28px;
  font-size: 11px;
  border: none;
  background-color: #f0f0f0;
  color: #555;
  cursor: pointer;
  transition:
    background-color 0.15s,
    color 0.15s;
}

.free-input-group > .unit-toggle .toggle-btn:first-child {
  border-right: 1px solid rgba(0, 0, 0, 0.15);
}

.free-input-group > .unit-toggle .toggle-btn.active {
  background-color: #6e6eff;
  color: #fff;
  font-weight: 600;
}

.free-input-group > .unit-toggle .toggle-btn:hover:not(:disabled):not(.active) {
  background-color: #b3c4ff;
  color: #fff;
}

.custom-background ::v-deep .tooltip-icon,
.custom-background ::v-deep img,
.custom-background ::v-deep svg {
  color: #ffffff52;
  fill: #ffffff52;
  stroke: black;
}

.divisionLabelSmall.port-label-cell {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  width: auto;
  margin-right: 1rem;
}

.port-code-badge {
  padding: 1px 6px;
  border-radius: 3px;
  background: #e8edff;
  color: #535cda;
  font-size: 10px;
  font-weight: bold;
  line-height: 1.4;
  white-space: nowrap;
}

.ship-call-changed-indicator {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 18px;
  margin-left: 5px;
  border: 1px solid #d59b00;
  border-radius: 50%;
  background: #fff4c2;
  color: #7a5200;
  font-size: 9px;
  font-weight: 700;
  line-height: 1;
  cursor: default;
  vertical-align: middle;
}

.container-batch-panel {
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  padding: 8px;
  margin-bottom: 8px;
  border: 1px solid #d7dbe8;
  background: #f7f9fc;
}

.container-select-all,
.container-row-checkbox {
  cursor: pointer;
}

.container-row-checkbox {
  margin-right: 6px;
}

.big-checkbox {
  width: 18px;
  height: 18px;
  vertical-align: middle;
}

.page-selector-wrapper {
  width: 650px;
  margin-top: 12px;
  padding: 10px;
  border: 1px solid #d7dbe8;
  background: #f7f9fc;
  box-sizing: border-box;
}

.page-selector {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 8px;
}

.page-select {
  height: 28px;
  min-width: 70px;
}

.page-count {
  color: #555;
  font-size: 13px;
}

.page-review-controls {
  width: 360px;
  padding: 16px;
  border: 1px solid #d8d8d8;
  background: #fff;
}

.page-range-row {
  margin-bottom: 12px;
}

.page-range-row label {
  display: block;
  margin-bottom: 6px;
  font-weight: 600;
}

.page-range-row select {
  width: 100%;
  height: 36px;
}

.page-selector-summary {
  margin: 12px 0;
  font-weight: 600;
}

.process-page-button,
.cancel-page-button {
  width: 100%;
  margin-top: 8px;
}

.page-review-layout {
  display: inline;
  align-items: flex-start;
}

.page-review-pdf .pdf-display {
  width: 100%;
  height: 75vh;
  border: 1px solid #d8d8d8;
}
</style>
